import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { routeToTab, tabToRoute, sectionForTab, documentTitleForTab } from "@/lib/adminSections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { 
  LayoutDashboard, 
  FileText, 
  LogOut, 
  MoreHorizontal, 
  Eye, 
  Trash2, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2,
  RefreshCw,
  Search,
  Users,
  Plus,
  Calendar as CalendarIcon,
  MapPin,
  Briefcase,
  Ban,
  Download,
  UserPlus,
  Image,
  Repeat,
  Printer,
  Star,
  Upload,
  GripVertical,
  X,
  Edit3,
  Camera,
  Settings,
  Lock,
  User,
  Share2,
  ExternalLink,
  Menu,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Activity,
  MessageSquareText,
  MessageSquare,
  Sparkles,
  Wand2,
  MessageCircle,
  Calculator,
  Copy,
  PoundSterling,
  Target,
  UserCheck,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  ArrowUpDown,
  Wallet,
  Crown,
  Hammer,
  Factory,
  Mail,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Package
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import PipelineTab from "@/components/admin/PipelineTab";
import { MetricInfo } from "@/components/admin/MetricInfo";
import AnalyticsTab from "@/components/admin/AnalyticsTab";
import AIInsightsPanel from "@/components/admin/AIInsightsPanel";
import { GilbertsTab } from "@/components/admin/GilbertsTab";
import { QuotesInvoicesTab } from "@/components/admin/QuotesInvoicesTab";
import { CustomersTab } from "@/components/admin/CustomersTab";
import { FeedbackTab } from "@/components/admin/FeedbackTab";
import { SettingsTab } from "@/components/admin/SettingsTab";
import { InvoiceProfitabilityWidget } from "@/components/admin/InvoiceProfitabilityWidget";
import { SiteEditorTab } from "@/components/admin/SiteEditorTab";

// Import centralized constants
import { 
  JOB_STATUSES, 
  PIPELINE_STAGES,
  QUOTE_STATUSES,
  INVOICE_STATUSES,
  SERVICE_CATEGORIES,
  SOURCE_LABELS,
  JOB_TAGS,
  PRIORITY_LEVELS,
  formatDate as formatDateStandard,
  getStatusInfo,
  normalizeJobStatus,
  getServiceLabel,
  getNextAction,
  getJobStatusOptions,
  getServiceOptions,
  getSourceOptions,
  PRICING,
  calculateJobPrice,
  UPCOMING_JOB_STATUSES
} from "@/lib/constants";
import { PortfolioTab } from "@/components/admin/PortfolioTab";
import { AIAssistantTab } from "@/components/admin/AIAssistantTab";
import SiteModeTab from "@/components/admin/SiteModeTab";
import { JobsTab } from "@/components/admin/JobsTab";
import { GrowthOpportunitiesPanel } from "@/components/admin/GrowthOpportunitiesPanel";
import { MaterialsTab } from "@/components/admin/MaterialsTab";
import { JobMaterialsPanel } from "@/components/admin/JobMaterialsPanel";
import { ProjectCommandCenter } from "@/components/admin/ProjectCommandCenter";
import { CustomerTimeline } from "@/components/admin/CustomerTimeline";
import { WhatsAppButton } from "@/components/admin/WhatsAppButton";
import { ErrorBoundary } from "@/components/admin/ErrorBoundary";
import NoindexHead from "@/components/NoindexHead";
import { APP_BUILD_VERSION } from "@/buildVersion";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/25pknzhh_ee436b9cf26fbe3a949749993a8a0799c33dc4d497d0bf4b65f923b400bf1fc0.jpeg";
const API = `${window.location.origin}/api`;

const statusConfig = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800 border-yellow-200", icon: Clock },
  "in-progress": { label: "In Progress", color: "bg-blue-100 text-blue-800 border-blue-200", icon: Loader2 },
  completed: { label: "Completed", color: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800 border-red-200", icon: XCircle }
};

const jobStatusConfig = {
  planned: { label: "Planned", color: "bg-blue-100 text-blue-800 border-blue-200" },
  "in-progress": { label: "In Progress", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  completed: { label: "Completed", color: "bg-green-100 text-green-800 border-green-200" }
};

const blockTypeConfig = {
  personal: { label: "Personal", color: "bg-gray-200 text-gray-800 border-gray-300", hex: "#9CA3AF" },
  holiday: { label: "Holiday", color: "bg-purple-200 text-purple-800 border-purple-300", hex: "#A78BFA" },
  admin: { label: "Admin", color: "bg-orange-200 text-orange-800 border-orange-300", hex: "#FB923C" },
  enquiry: { label: "Enquiry", color: "bg-amber-200 text-amber-800 border-amber-300", hex: "#FCD34D" },
  job: { label: "Job", color: "bg-emerald-200 text-emerald-800 border-emerald-300", hex: "#34D399" },
  other: { label: "Other", color: "bg-slate-200 text-slate-800 border-slate-300", hex: "#94A3B8" }
};

// Default team member colors
const TEAM_COLORS = [
  "#3B82F6",  // Blue
  "#10B981",  // Green  
  "#F59E0B",  // Amber
  "#EF4444",  // Red
  "#8B5CF6",  // Purple
  "#EC4899",  // Pink
  "#06B6D4",  // Cyan
  "#F97316",  // Orange
];

// Days of week for recurring contracts
const DAYS_OF_WEEK = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" }
];

const serviceLabels = {
  carpentry: "Carpentry",
  "general-repairs": "General Repairs",
  "furniture-builds": "Furniture Builds",
  "cnc-factory": "CNC/Factory Work"
};

const TIME_SLOTS = [
  "08:00", "09:00", "10:00", "11:00", "12:00", 
  "13:00", "14:00", "15:00", "16:00", "17:00", 
  "18:00", "19:00", "20:00"
];

// Helper to get full image URL - handles both local and Drive URLs
const getImageUrl = (url) => {
  if (!url) return '';
  // If it's already a full URL (Drive, external)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Convert Google Drive URLs to use our proxy (avoids CORS issues)
    // Pattern 1: https://drive.google.com/file/d/FILE_ID/view
    const driveViewMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)\/view/);
    if (driveViewMatch) {
      return `${window.location.origin}/api/drive-proxy/${driveViewMatch[1]}`;
    }
    // Pattern 2: https://drive.google.com/uc?export=view&id=FILE_ID
    const driveUcMatch = url.match(/drive\.google\.com\/uc\?export=view&id=([^&]+)/);
    if (driveUcMatch) {
      return `${window.location.origin}/api/drive-proxy/${driveUcMatch[1]}`;
    }
    // Pattern 3: https://drive.google.com/uc?id=FILE_ID&export=view
    const driveUcMatch2 = url.match(/drive\.google\.com\/uc\?id=([^&]+)/);
    if (driveUcMatch2) {
      return `${window.location.origin}/api/drive-proxy/${driveUcMatch2[1]}`;
    }
    // Pattern 4: https://lh3.googleusercontent.com/d/FILE_ID=s1600
    const lh3Match = url.match(/lh3\.googleusercontent\.com\/d\/([^=]+)/);
    if (lh3Match) {
      return `${window.location.origin}/api/drive-proxy/${lh3Match[1]}`;
    }
    return url;
  }
  // Otherwise, it's a local URL, prepend backend URL
  return `${window.location.origin}${url}`;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  // Phase 4: the URL is the single source of truth for the active section.
  // Unknown or over-deep /admin/* paths render the in-layout not-found view.
  const routeParams = useParams();
  const activeTab = (!routeParams["*"] && routeToTab(routeParams.section)) || "__not-found__";
  const setActiveTab = useCallback((tab) => {
    const route = tabToRoute(tab);
    if (route) navigate(`/admin/${route}`);
  }, [navigate]);
  const [stats, setStats] = useState({ total_enquiries: 0, pending: 0, in_progress: 0, completed: 0, cancelled: 0, total_customers: 0, upcoming_jobs: 0 });
  const [enquiries, setEnquiries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isCustomerDetailOpen, setIsCustomerDetailOpen] = useState(false);
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [isAddJobOpen, setIsAddJobOpen] = useState(false);
  const [isAddEnquiryOpen, setIsAddEnquiryOpen] = useState(false);
  const [isEditEnquiryOpen, setIsEditEnquiryOpen] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [newEnquiry, setNewEnquiry] = useState({ name: "", email: "", phone: "", service: "general-repairs", message: "", preferred_date: "", source: "whatsapp" });
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [notes, setNotes] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [newJob, setNewJob] = useState({ description: "", date: "", time_slot: "", duration: 2, status: "planned", is_recurring: false, recurring_interval: "", photos: [], featured_in_portfolio: false, assigned_admins: [], is_all_day: false });
  const [calendarDate, setCalendarDate] = useState(new Date());
  // Gilberts state (kept for calendar integration)
  const [gilbertsShifts, setGilbertsShifts] = useState([]);
  const [gilbertsRates, setGilbertsRates] = useState({ cnc: { label: "CNC", rate: 27.50 }, maintenance: { label: "Maintenance", rate: 20.00 } });
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarViewMode, setCalendarViewMode] = useState('day'); // 'day' or 'week'
  const [blockedDays, setBlockedDays] = useState([]);
  const [isBlockDayOpen, setIsBlockDayOpen] = useState(false);
  const [newBlockedDay, setNewBlockedDay] = useState({ 
    date: "", 
    reason: "", 
    block_type: "enquiry", 
    time_slot: "", 
    duration: 1, 
    assigned_admins: [],
    // Multi-day scheduling
    scheduledDays: [],  // Array of dates
    teamSize: 1,        // 1 or 2 people
    dayRate: PRICING.DAY_RATES[1],
    multiDayDiscount: null,  // Custom discount or auto-calculated
    selectionMode: "single", // "single", "range", or "multi"
    rangeStart: null,
    rangeEnd: null,
    linkedJobId: null,  // Link to enquiry/job if scheduling from Jobs panel
  });
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedJobIndex, setSelectedJobIndex] = useState(null);
  const [isJobPhotosOpen, setIsJobPhotosOpen] = useState(false);
  const [newPhotoUrl, setNewPhotoUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [draggedPhoto, setDraggedPhoto] = useState(null);
  const [editingCaption, setEditingCaption] = useState(null);
  const [isAddAdminOpen, setIsAddAdminOpen] = useState(false);
  const [newAdminData, setNewAdminData] = useState({ email: "", password: "", name: "" });
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [isShareCalendarOpen, setIsShareCalendarOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedBlockedDay, setSelectedBlockedDay] = useState(null);
  const [isBlockedDayDetailOpen, setIsBlockedDayDetailOpen] = useState(false);
  const fileInputRef = useRef(null);
  // Portfolio/Lightbox management
  const [portfolioPhotos, setPortfolioPhotos] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [draggedPortfolioPhoto, setDraggedPortfolioPhoto] = useState(null);
  
  // Drag and drop for calendar scheduling
  const [draggedJob, setDraggedJob] = useState(null);
  const [dropTargetDate, setDropTargetDate] = useState(null);
  const [selectedPortfolioPhoto, setSelectedPortfolioPhoto] = useState(null);
  const [isAddPortfolioPhotoOpen, setIsAddPortfolioPhotoOpen] = useState(false);
  const [newPortfolioPhoto, setNewPortfolioPhoto] = useState({ url: '', caption: '', photo_type: 'general', category: 'carpentry' });
  const [isUploadingPortfolio, setIsUploadingPortfolio] = useState(false);
  const [portfolioCategoryFilter, setPortfolioCategoryFilter] = useState('all');
  const portfolioFileInputRef = useRef(null);
  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);
  const [invoiceSortBy, setInvoiceSortBy] = useState('date'); // 'date', 'reference', 'customer'
  const [invoiceSortOrder, setInvoiceSortOrder] = useState('desc'); // 'asc', 'desc'
  const [isCreateInvoiceOpen, setIsCreateInvoiceOpen] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    customer_name: '',
    customer_address: '',
    description: '',
    line_items: [{ description: '', quantity: 1, unit_cost: 0 }],
    payment_days: 7,
    date: '',
    reference: ''
  });
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  // Edit Invoice
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [isEditInvoiceOpen, setIsEditInvoiceOpen] = useState(false);
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);
  const [regeneratingInvoiceId, setRegeneratingInvoiceId] = useState(null);
  // Quotes
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [isCreateQuoteOpen, setIsCreateQuoteOpen] = useState(false);
  const [newQuote, setNewQuote] = useState({
    customer_name: '',
    customer_address: '',
    customer_email: '',
    description: '',
    line_items: [{ description: '', quantity: 1, unit_cost: 0 }],
    valid_days: 30,
    date: '',
    reference: '',
    attachments: []
  });
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);
  const [quoteFiles, setQuoteFiles] = useState([]);
  const [isUploadingQuoteFiles, setIsUploadingQuoteFiles] = useState(false);
  // Edit Quote
  const [editingQuote, setEditingQuote] = useState(null);
  const [isEditQuoteOpen, setIsEditQuoteOpen] = useState(false);
  const [isSavingQuote, setIsSavingQuote] = useState(false);
  // Edit Feedback
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [isEditFeedbackOpen, setIsEditFeedbackOpen] = useState(false);
  const [isCreateReviewOpen, setIsCreateReviewOpen] = useState(false);
  // Request Review Dialog
  const [isRequestReviewOpen, setIsRequestReviewOpen] = useState(false);
  const [requestReviewJob, setRequestReviewJob] = useState(null);
  // Add to Portfolio Dialog  
  const [isAddToPortfolioOpen, setIsAddToPortfolioOpen] = useState(false);
  const [addToPortfolioJob, setAddToPortfolioJob] = useState(null);
  const [selectedPhotosForPortfolio, setSelectedPhotosForPortfolio] = useState([]);
  const [portfolioCategory, setPortfolioCategory] = useState('carpentry');
  
  // AI Assistance State
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiResultType, setAiResultType] = useState(null); // 'quote', 'description', 'reply', 'review_request'
  // Admin management
  const [allAdmins, setAllAdmins] = useState([]);
  const [adminProfile, setAdminProfile] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: "", new: "", confirm: "" });
  const [profileData, setProfileData] = useState({ name: "", email: "", phone: "", address: "", color: "" });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  // Edit Booking
  const [editingBooking, setEditingBooking] = useState(null);
  const [isEditBookingOpen, setIsEditBookingOpen] = useState(false);
  // Recurring Contracts
  const [recurringContracts, setRecurringContracts] = useState([]);
  const [isAddContractOpen, setIsAddContractOpen] = useState(false);
  const [newContract, setNewContract] = useState({ 
    name: "", 
    customer_id: "", 
    customer_name: "",
    customer_address: "",
    days_of_week: [], 
    start_time: "10:00", 
    end_time: "20:00", 
    assigned_admins: [], 
    rolling_weeks: 4, 
    block_type: "job",
    service_type: "general-repairs"
  });
  const [editingContract, setEditingContract] = useState(null);
  const [contractConflicts, setContractConflicts] = useState([]);
  const [showConflicts, setShowConflicts] = useState(false);
  // Google Integration
  const [googleStatus, setGoogleStatus] = useState({ connected: false, email: null });
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveFolderInfo, setDriveFolderInfo] = useState({ main_folder_id: null, folders: [] });

  const token = localStorage.getItem("admin_session");
  // Auth is carried by a secure httpOnly cookie, sent automatically on every
  // request via axios.defaults.withCredentials. `token` here is just a truthy
  // session flag (never the real JWT) used for client-side gating.
  const authHeaders = useMemo(() => ({}), []);
  const getAuth = () => ({});

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs only on session/navigate change
  useEffect(() => {
    if (!token) {
      // Phase 5 safe-return: remember the attempted admin section deep-link so
      // login can send the admin back here. Only store a real section route
      // (never /admin), so it can't be clobbered by a racing writer.
      try {
        if (/^\/admin\/[a-z0-9-]+/.test(window.location.pathname) && activeTab !== "__not-found__") {
          sessionStorage.setItem('admin_return_to', window.location.pathname + window.location.search);
        }
      } catch (e) { /* storage unavailable — default return applies */ }
      navigate("/admin");
      return;
    }
    
    // Stagger requests to avoid overwhelming the connection
    const loadDashboard = async () => {
      await fetchData();
      // Secondary fetches after dashboard data loads
      fetchAdminProfile();
      fetchAllAdmins();
      fetchGoogleStatus();
      fetchInvoices();
      fetchQuotes();
      fetchGilbertsShifts();
    };
    loadDashboard();
    
    // Check for Google connection callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === 'true') {
      toast.success("Google Calendar connected!");
      window.history.replaceState({}, '', window.location.pathname);
      fetchGoogleStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  // Version-drift detector: compare this bundle's APP_BUILD_VERSION against the
  // server's /api/app/version. If they diverge, the user is running a stale PWA
  // cache — aggressively unregister service workers, clear caches, and reload.
  const [versionDriftInfo, setVersionDriftInfo] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const checkVersion = async () => {
      try {
        const res = await fetch(`${API}/app/version`, { cache: 'no-store' });
        if (!res.ok) return;
        const { version: serverVersion } = await res.json();
        if (cancelled || !serverVersion) return;
        if (serverVersion === APP_BUILD_VERSION) {
          // Current build — ensure any stale banner is cleared
          setVersionDriftInfo(null);
          return;
        }
        console.warn(`[SHS] Version drift: client=${APP_BUILD_VERSION} server=${serverVersion} — forcing refresh`);
        setVersionDriftInfo({ client: APP_BUILD_VERSION, server: serverVersion });
        toast.info("New build detected — refreshing to pick up fixes…", { duration: 3500 });
        // Unregister ALL service workers
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(r => r.unregister()));
        }
        // Wipe ALL caches
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map(k => caches.delete(k)));
        }
        // Hard-reload bypassing HTTP cache
        setTimeout(() => window.location.reload(), 1500);
      } catch (e) { /* offline / endpoint missing — ignore */ }
    };
    checkVersion();
    // Re-check on focus so long-lived sessions catch fixes too
    window.addEventListener('focus', checkVersion);
    return () => { cancelled = true; window.removeEventListener('focus', checkVersion); };
  }, []);

  // ============ Push Notification Deep Linking ============
  // Reads ?openJob=<id> / ?openInvoice=<id> on mount (and after enquiries/invoices load)
  // and opens the matching modal. Also listens for postMessages from the service worker
  // when the tab is already open (notification tap → DEEP_LINK message).
  const [pendingDeepLink, setPendingDeepLink] = useState(null); // { job_id?, invoice_id? }

  useEffect(() => {
    // Parse URL once on mount
    const params = new URLSearchParams(window.location.search);
    const openJob = params.get('openJob');
    const openInvoice = params.get('openInvoice');
    if (openJob || openInvoice) {
      setPendingDeepLink({ job_id: openJob || null, invoice_id: openInvoice || null });
      // Clean URL so a refresh doesn't re-trigger
      const clean = window.location.pathname;
      window.history.replaceState({}, '', clean);
    }

    // Listen for SW postMessage deep-link payloads
    const onSwMessage = (event) => {
      const d = event.data;
      if (d && d.type === 'DEEP_LINK') {
        setPendingDeepLink({ job_id: d.job_id || null, invoice_id: d.invoice_id || null });
        // Also extract from URL if provided
        if (d.url && typeof d.url === 'string' && d.url.includes('?')) {
          try {
            const u = new URL(d.url, window.location.origin);
            const j = u.searchParams.get('openJob');
            const i = u.searchParams.get('openInvoice');
            if (j || i) setPendingDeepLink({ job_id: j || null, invoice_id: i || null });
          } catch (_) {}
        }
      }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSwMessage);
    }
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onSwMessage);
      }
    };
  }, []);

  // Manual emergency cache-clear button handler
  const handleForceReload = async () => {
    if (!window.confirm("Clear cached app shell and reload? You will need to log in again.")) return;
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
    } finally {
      window.location.reload();
    }
  };

  // Subscribe to push notifications after login
  useEffect(() => {
    if (!token) return;
    
    const subscribeToPush = async () => {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;
        
        const registration = await navigator.serviceWorker.ready;
        
        // Get VAPID public key
        const vapidRes = await axios.get(`${API}/push/vapid-key`, authHeaders);
        const vapidKey = vapidRes.data.public_key;
        if (!vapidKey) return;
        
        // Convert VAPID key to Uint8Array
        const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
        const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = atob(base64);
        const applicationServerKey = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; i++) applicationServerKey[i] = rawData.charCodeAt(i);
        
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey
          });
        }
        
        // Send subscription to backend
        await axios.post(`${API}/push/subscribe`, {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
          }
        }, authHeaders);
      } catch (err) {
      }
    };
    
    subscribeToPush();
  }, [token, authHeaders]);

  const fetchGoogleStatus = async () => {
    try {
      const token = localStorage.getItem('admin_session');
      const h = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/google/status`, h);
      setGoogleStatus(res.data);
      try {
        const driveRes = await axios.get(`${API}/admin/drive/status`, h);
        setDriveConnected(driveRes.data.connected === true);
        if (driveRes.data.connected) {
          const foldersRes = await axios.get(`${API}/admin/drive/folders`, h);
          setDriveFolderInfo(foldersRes.data);
        }
      } catch (e) {
        setDriveConnected(false);
      }
    } catch (error) {
      console.error("Failed to fetch Google status");
    }
  };

  // Helper function to upload file - tries Drive first, falls back to local storage
  const uploadFile = async (file, folderType = "portfolio", customerName = null, jobId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folderType);

    try {
      const token = localStorage.getItem('admin_session');
      const res = await axios.post(`${API}/files/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      return { url: res.data.url, isDrive: false };
    } catch (error) {
      console.error("Upload failed:", error);
      throw error;
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const res = await axios.get(`${API}/auth/google/login`, authHeaders);
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error("Failed to start Google connection");
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm("Disconnect Google Calendar?")) return;
    try {
      await axios.post(`${API}/admin/google/disconnect`, {}, authHeaders);
      toast.success("Google Calendar disconnected");
      setGoogleStatus({ connected: false, email: null });
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const handleSyncAllToGoogle = async () => {
    try {
      const res = await axios.post(`${API}/admin/google/sync-all`, {}, authHeaders);
      toast.success(`Synced ${res.data.synced} bookings to Google Calendar`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync");
    }
  };

  // Fetch invoices
  const fetchInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const token = localStorage.getItem('admin_session');
      const res = await axios.get(`${API}/admin/invoices`, { headers: { Authorization: `Bearer ${token}` } });
      setInvoices(res.data.invoices || []);
      setInvoicesLoaded(true);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setInvoicesLoading(false);
    }
  };

  // Sort invoices
  const sortedInvoices = [...invoices].sort((a, b) => {
    let comparison = 0;
    if (invoiceSortBy === 'date') {
      const dateA = a.created_at || a.date || '';
      const dateB = b.created_at || b.date || '';
      comparison = dateA.localeCompare(dateB);
    } else if (invoiceSortBy === 'reference') {
      comparison = (a.reference || '').localeCompare(b.reference || '');
    } else if (invoiceSortBy === 'customer') {
      comparison = (a.customer_name || '').localeCompare(b.customer_name || '');
    } else if (invoiceSortBy === 'total') {
      comparison = (a.total || 0) - (b.total || 0);
    }
    return invoiceSortOrder === 'asc' ? comparison : -comparison;
  });

  const toggleInvoiceSort = (field) => {
    if (invoiceSortBy === field) {
      setInvoiceSortOrder(invoiceSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setInvoiceSortBy(field);
      setInvoiceSortOrder('desc');
    }
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    if (!newInvoice.customer_name || !newInvoice.customer_address) {
      toast.error("Customer name and address are required");
      return;
    }
    if (newInvoice.line_items.length === 0 || !newInvoice.line_items[0].description) {
      toast.error("At least one line item is required");
      return;
    }
    
    setIsCreatingInvoice(true);
    try {
      // Only send non-empty optional fields
      const invoicePayload = {
        ...newInvoice,
        date: newInvoice.date || null,
        reference: newInvoice.reference || null
      };
      const response = await axios.post(`${API}/admin/invoices`, invoicePayload, authHeaders);
      toast.success(`Invoice ${response.data.invoice.reference} created!`);
      
      // Open PDF in new tab
      window.open(`${window.location.origin}${response.data.pdf_url}`, '_blank');
      
      setIsCreateInvoiceOpen(false);
      setNewInvoice({
        customer_name: '',
        customer_address: '',
        description: '',
        line_items: [{ description: '', quantity: 1, unit_cost: 0 }],
        payment_days: 7,
        date: '',
        reference: ''
      });
      fetchInvoices();
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast.error("Failed to create invoice");
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  // Add line item to invoice
  const addInvoiceLineItem = () => {
    setNewInvoice(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit_cost: 0 }]
    }));
  };

  // Remove line item from invoice
  const removeInvoiceLineItem = (index) => {
    setNewInvoice(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  // Update line item
  const updateInvoiceLineItem = (index, field, value) => {
    setNewInvoice(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: field === 'description' ? value : parseFloat(value) || 0 } : item
      )
    }));
  };

  // Delete invoice
  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    try {
      await axios.delete(`${API}/admin/invoices/${invoiceId}`, authHeaders);
      toast.success("Invoice deleted");
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  // Edit invoice
  const openEditInvoice = (invoice) => {
    setEditingInvoice({
      ...invoice,
      date: invoice.date ? '' : '', // Will use existing date if not changed
      line_items: invoice.line_items || [{ description: '', quantity: 1, unit_cost: 0 }]
    });
    setIsEditInvoiceOpen(true);
  };

  const saveEditedInvoice = async () => {
    if (!editingInvoice) return;
    setIsSavingInvoice(true);
    try {
      await axios.put(`${API}/admin/invoices/${editingInvoice.id}`, {
        customer_name: editingInvoice.customer_name,
        customer_address: editingInvoice.customer_address,
        description: editingInvoice.description,
        line_items: editingInvoice.line_items,
        payment_days: editingInvoice.payment_days,
        date: editingInvoice.date || null
      }, authHeaders);
      toast.success("Invoice updated and PDF regenerated!");
      setIsEditInvoiceOpen(false);
      setEditingInvoice(null);
      fetchInvoices();
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast.error("Failed to update invoice");
    } finally {
      setIsSavingInvoice(false);
    }
  };

  // Regenerate PDF
  const regenerateInvoicePdf = async (invoiceId) => {
    setRegeneratingInvoiceId(invoiceId);
    try {
      const response = await axios.post(`${API}/admin/invoices/${invoiceId}/regenerate-pdf`, {}, authHeaders);
      toast.success("PDF regenerated successfully!");
      fetchInvoices();
    } catch (error) {
      console.error("Error regenerating PDF:", error);
      toast.error("Failed to regenerate PDF");
    } finally {
      setRegeneratingInvoiceId(null);
    }
  };

  // Add/remove line items for editing invoice
  const addEditInvoiceLineItem = () => {
    setEditingInvoice(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit_cost: 0 }]
    }));
  };

  const removeEditInvoiceLineItem = (index) => {
    setEditingInvoice(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const updateEditInvoiceLineItem = (index, field, value) => {
    setEditingInvoice(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: field === 'description' ? value : parseFloat(value) || 0 } : item
      )
    }));
  };

  // Pre-fill invoice from customer
  const createInvoiceFromCustomer = (customer) => {
    setNewInvoice({
      customer_name: customer.name,
      customer_address: customer.address || '',
      description: '',
      line_items: [{ description: '', quantity: 1, unit_cost: 0 }],
      payment_days: 7,
      date: '',
      reference: ''
    });
    setIsCreateInvoiceOpen(true);
  };

  // ============== QUOTE FUNCTIONS ==============
  const fetchQuotes = async () => {
    setQuotesLoading(true);
    try {
      const token = localStorage.getItem('admin_session');
      const res = await axios.get(`${API}/admin/quotes`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes(res.data.quotes || []);
    } catch (error) {
      console.error("Error fetching quotes:", error);
    } finally {
      setQuotesLoading(false);
    }
  };

  const handleCreateQuote = async () => {
    if (!newQuote.customer_name || !newQuote.customer_address) {
      toast.error("Customer name and address are required");
      return;
    }
    if (newQuote.line_items.length === 0 || !newQuote.line_items[0].description) {
      toast.error("At least one line item is required");
      return;
    }
    
    setIsCreatingQuote(true);
    try {
      const quotePayload = {
        ...newQuote,
        date: newQuote.date || null,
        reference: newQuote.reference || null,
        attachments: quoteFiles.map(f => f.url)
      };
      const response = await axios.post(`${API}/admin/quotes`, quotePayload, authHeaders);
      toast.success(`Quote ${response.data.quote.reference} created!`);
      window.open(`${window.location.origin}${response.data.pdf_url}`, '_blank');
      
      setIsCreateQuoteOpen(false);
      setNewQuote({
        customer_name: '', customer_address: '', customer_email: '', description: '',
        line_items: [{ description: '', quantity: 1, unit_cost: 0 }],
        valid_days: 30, date: '', reference: '', attachments: []
      });
      setQuoteFiles([]);
      fetchQuotes();
    } catch (error) {
      console.error("Error creating quote:", error);
      toast.error("Failed to create quote");
    } finally {
      setIsCreatingQuote(false);
    }
  };

  const addQuoteLineItem = () => {
    setNewQuote(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit_cost: 0 }]
    }));
  };

  const removeQuoteLineItem = (index) => {
    setNewQuote(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const updateQuoteLineItem = (index, field, value) => {
    setNewQuote(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: field === 'description' ? value : parseFloat(value) || 0 } : item
      )
    }));
  };

  const handleQuoteFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setIsUploadingQuoteFiles(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/upload-public`, formData);
        setQuoteFiles(prev => [...prev, { name: file.name, url: response.data.url }]);
        toast.success(`Uploaded: ${file.name}`);
      } catch (error) {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }
    setIsUploadingQuoteFiles(false);
    e.target.value = '';
  };

  const removeQuoteFile = (index) => {
    setQuoteFiles(prev => prev.filter((_, i) => i !== index));
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm("Are you sure you want to delete this quote?")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}`, authHeaders);
      toast.success("Quote deleted");
      fetchQuotes();
    } catch (error) {
      toast.error("Failed to delete quote");
    }
  };

  const convertQuoteToInvoice = async (quoteId) => {
    if (!window.confirm("Convert this quote to an invoice?")) return;
    try {
      const response = await axios.post(`${API}/admin/quotes/${quoteId}/convert-to-invoice`, {}, authHeaders);
      toast.success("Quote converted to invoice!");
      window.open(`${window.location.origin}${response.data.invoice.pdf_url}`, '_blank');
      fetchQuotes();
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to convert quote");
    }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${quoteId}/status?status=${status}`, {}, authHeaders);
      toast.success(`Quote marked as ${status}`);
      fetchQuotes();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Edit quote functions
  const openEditQuote = (quote) => {
    setEditingQuote({
      ...quote,
      date: quote.date ? '' : '',
      line_items: quote.line_items || [{ description: '', quantity: 1, unit_cost: 0 }]
    });
    setIsEditQuoteOpen(true);
  };

  const saveEditedQuote = async () => {
    if (!editingQuote) return;
    setIsSavingQuote(true);
    try {
      await axios.put(`${API}/admin/quotes/${editingQuote.id}`, {
        customer_name: editingQuote.customer_name,
        customer_address: editingQuote.customer_address,
        customer_email: editingQuote.customer_email,
        description: editingQuote.description,
        line_items: editingQuote.line_items,
        valid_days: editingQuote.valid_days,
        date: editingQuote.date || null
      }, authHeaders);
      toast.success("Quote updated and PDF regenerated!");
      setIsEditQuoteOpen(false);
      setEditingQuote(null);
      fetchQuotes();
    } catch (error) {
      console.error("Error updating quote:", error);
      toast.error("Failed to update quote");
    } finally {
      setIsSavingQuote(false);
    }
  };

  const addEditQuoteLineItem = () => {
    setEditingQuote(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit_cost: 0 }]
    }));
  };

  const removeEditQuoteLineItem = (index) => {
    setEditingQuote(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const updateEditQuoteLineItem = (index, field, value) => {
    setEditingQuote(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: field === 'description' ? value : parseFloat(value) || 0 } : item
      )
    }));
  };

  const [metrics, setMetrics] = useState(null);

  const fetchData = async () => {
    setIsLoading(true);
    const token = localStorage.getItem('admin_session');
    if (!token) { setIsLoading(false); return; }
    const h = { headers: { Authorization: `Bearer ${token}` } };
    
    try {
      const statsRes = await axios.get(`${API}/admin/dashboard`, h);
      setStats(statsRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('admin_session');
        navigate("/admin");
        return;
      }
    }

    // Canonical metrics (single source of truth shared with Analytics & AI)
    axios.get(`${API}/admin/metrics/summary`, h)
      .then((r) => setMetrics(r.data?.metrics || null))
      .catch(() => {});
    
    try {
      const enquiriesRes = await axios.get(`${API}/admin/enquiries`, h);
      setEnquiries(enquiriesRes.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('admin_session'); navigate("/admin"); return; }
    }
    
    try {
      const customersRes = await axios.get(`${API}/admin/customers`, h);
      setCustomers(customersRes.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('admin_session'); navigate("/admin"); return; }
    }
    
    try {
      const blockedRes = await axios.get(`${API}/admin/blocked-days`, h);
      setBlockedDays(blockedRes.data);
    } catch (e) { /* non-critical */ }
    
    try {
      const contractsRes = await axios.get(`${API}/admin/recurring-contracts`, h);
      setRecurringContracts(contractsRes.data || []);
    } catch (e) { /* non-critical */ }
    
    setIsLoading(false);
  };

  // Fetch Gilberts shifts
  const fetchGilbertsShifts = async () => {
    try {
      const token = localStorage.getItem('admin_session');
      const h = { headers: { Authorization: `Bearer ${token}` } };
      const shiftsRes = await axios.get(`${API}/admin/gilberts/shifts`, h);
      setGilbertsShifts(shiftsRes.data);
      const ratesRes = await axios.get(`${API}/admin/gilberts/rates`, h);
      if (ratesRes.data?.rates) setGilbertsRates(ratesRes.data.rates);
    } catch (error) {
      console.error("Error fetching Gilberts data:", error);
    }
  };

  // Fetch all featured portfolio photos with customer/job context + standalone photos
  const fetchPortfolioPhotos = async () => {
    setPortfolioLoading(true);
    try {
      const token = localStorage.getItem('admin_session');
      const headers = { headers: { Authorization: `Bearer ${token}` } };
      // Get all customers to build portfolio data
      const customersRes = await axios.get(`${API}/admin/customers`, headers);
      const allCustomers = customersRes.data;
      
      // Extract all featured photos with their context
      const photos = [];
      allCustomers.forEach(customer => {
        (customer.jobs || []).forEach((job, jobIndex) => {
          if (job.featured_in_portfolio && job.photos?.length > 0) {
            job.photos.forEach((photo, photoIndex) => {
              const photoData = typeof photo === 'string' 
                ? { id: `${customer.id}-${jobIndex}-${photoIndex}`, url: photo, caption: '', photo_type: 'general' }
                : photo;
              photos.push({
                ...photoData,
                customerId: customer.id,
                customerName: customer.name,
                jobIndex,
                jobDescription: job.description,
                jobDate: job.date,
                photoIndex,
                globalOrder: photos.length,
                standalone: false
              });
            });
          }
        });
      });
      
      // Also get standalone portfolio photos
      try {
        const standaloneRes = await axios.get(`${API}/admin/portfolio-photos`, headers);
        standaloneRes.data.forEach((photo) => {
          photos.push({
            ...photo,
            customerName: '',
            jobDescription: 'Portfolio Photo',
            standalone: true
          });
        });
      } catch (e) {
        // Standalone photos endpoint might not exist yet
      }
      
      setPortfolioPhotos(photos);
    } catch (error) {
      toast.error("Failed to load portfolio photos");
    } finally {
      setPortfolioLoading(false);
    }
  };

  // Phase 4: /admin/lightbox deep links must load photos (previously only the
  // sidebar click fetched them). Single fetch per section entry — the sidebar
  // Link no longer fetches, so navigation cannot double-fetch.
  useEffect(() => {
    if (token && activeTab === "portfolio") fetchPortfolioPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token]);

  // Remove photo from portfolio (unfeatured or delete standalone)
  const handleRemoveFromPortfolio = async (photo) => {
    try {
      if (photo.standalone) {
        // Delete standalone photo
        await axios.delete(`${API}/admin/portfolio-photos/${photo.id}`, authHeaders);
        toast.success("Photo removed from portfolio");
      } else {
        // Get customer data and unfeatured the job
        const customerRes = await axios.get(`${API}/admin/customers/${photo.customerId}`, authHeaders);
        const customer = customerRes.data;
        const job = customer.jobs[photo.jobIndex];
        
        // Update job to not be featured
        await axios.patch(
          `${API}/admin/customers/${photo.customerId}/jobs/${photo.jobIndex}`,
          { ...job, featured_in_portfolio: false },
          authHeaders
        );
        toast.success("Removed from portfolio");
      }
      
      fetchPortfolioPhotos();
      fetchData();
    } catch (error) {
      toast.error("Failed to remove from portfolio");
    }
  };

  // Add standalone photo to portfolio
  const handleAddStandalonePhoto = async () => {
    if (!newPortfolioPhoto.url) {
      toast.error("Please provide a photo URL");
      return;
    }
    try {
      await axios.post(`${API}/admin/portfolio-photos`, newPortfolioPhoto, authHeaders);
      toast.success("Photo added to portfolio");
      setNewPortfolioPhoto({ url: '', caption: '', photo_type: 'general', category: 'carpentry' });
      setIsAddPortfolioPhotoOpen(false);
      fetchPortfolioPhotos();
    } catch (error) {
      toast.error("Failed to add photo");
    }
  };

  // Upload file and add to portfolio
  const handlePortfolioFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setIsUploadingPortfolio(true);
    
    for (const file of files) {
      try {
        // Check file size (warn if > 10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.warning(`${file.name} is large (${(file.size / 1024 / 1024).toFixed(1)}MB), upload may take a while...`);
        }
        
        // Use the helper function that tries Drive first
        // Portfolio photos go to "Lightbox Photos" folder
        const uploadResult = await uploadFile(file, "lightbox");
        
        // Add to portfolio
        await axios.post(`${API}/admin/portfolio-photos`, {
          url: uploadResult.url,
          caption: '',
          photo_type: 'general'
        }, authHeaders);
        
        const storageType = uploadResult.isDrive ? "Google Drive" : "server";
        toast.success(`Added to ${storageType}: ${file.name}`);
      } catch (error) {
        console.error("Upload error:", error);
        const errorMsg = error.response?.data?.detail || error.message || "Network error";
        toast.error(`Failed: ${file.name} - ${errorMsg}`);
      }
    }
    
    setIsUploadingPortfolio(false);
    fetchPortfolioPhotos();
    if (portfolioFileInputRef.current) portfolioFileInputRef.current.value = '';
  };

  // Update photo in portfolio (caption, type)
  const handleUpdatePortfolioPhoto = async (photo, updates) => {
    try {
      const token = localStorage.getItem('admin_session');
      const headers = { headers: { Authorization: `Bearer ${token}` } };
      if (photo.isStandalone || photo.standalone) {
        await axios.patch(`${API}/admin/portfolio-photos/${photo.id}`, updates, headers);
        toast.success("Photo updated");
      } else {
        const customerRes = await axios.get(`${API}/admin/customers/${photo.customerId}`, headers);
        const customer = customerRes.data;
        const job = customer.jobs[photo.jobIndex];
        
        const updatedPhotos = job.photos.map((p, i) => {
          if (i === photo.photoIndex) {
            return typeof p === 'string' 
              ? { id: photo.id, url: p, ...updates }
              : { ...p, ...updates };
          }
          return p;
        });
        
        await axios.patch(
          `${API}/admin/customers/${photo.customerId}/jobs/${photo.jobIndex}`,
          { ...job, photos: updatedPhotos },
          headers
        );
        toast.success("Photo updated");
      }
      // Update local state immediately
      setSelectedPortfolioPhoto(prev => prev ? { ...prev, ...updates } : null);
    } catch (error) {
      toast.error("Failed to update photo");
    }
  };

  // Admin profile functions
  const fetchAdminProfile = async () => {
    try {
      const token = localStorage.getItem('admin_session');
      const h = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/profile`, h);
      setAdminProfile(res.data);
      setProfileData({ 
        name: res.data.name || "", 
        email: res.data.email || "",
        phone: res.data.phone || "",
        address: res.data.address || "",
        color: res.data.color || TEAM_COLORS[0]
      });
    } catch (error) {
      console.error("Failed to fetch admin profile");
    }
  };

  const fetchAllAdmins = async () => {
    try {
      const token = localStorage.getItem('admin_session');
      const res = await axios.get(`${API}/admin/all-admins`, { headers: { Authorization: `Bearer ${token}` } });
      setAllAdmins(res.data);
    } catch (error) {
      console.error("Failed to fetch admins");
    }
  };

  const handleUpdateProfile = async () => {
    setIsSavingProfile(true);
    try {
      await axios.patch(`${API}/admin/profile`, profileData, getAuth());
      toast.success("Profile updated");
      fetchAdminProfile();
      fetchAllAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Edit booking functions
  const openEditBooking = (booking) => {
    setEditingBooking({
      ...booking,
      assigned_admins: booking.assigned_admins || []
    });
    setIsEditBookingOpen(true);
  };

  const handleUpdateBooking = async () => {
    if (!editingBooking) return;
    try {
      await axios.patch(`${API}/admin/blocked-days/${editingBooking.id}`, {
        date: editingBooking.date,
        reason: editingBooking.reason,
        block_type: editingBooking.block_type,
        time_slot: editingBooking.time_slot,
        duration: editingBooking.duration,
        assigned_admins: editingBooking.assigned_admins
      }, authHeaders);
      toast.success("Booking updated");
      setIsEditBookingOpen(false);
      setEditingBooking(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update booking");
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new !== passwordData.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (passwordData.new.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwordData.current,
        new_password: passwordData.new
      }, authHeaders);
      toast.success("Password changed successfully");
      setPasswordData({ current: "", new: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm("Are you sure you want to delete this team member?")) return;
    
    try {
      await axios.delete(`${API}/admin/admins/${adminId}`, authHeaders);
      toast.success("Team member deleted");
      fetchAllAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete team member");
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminData.email || !newAdminData.password) {
      toast.error("Email and password are required");
      return;
    }
    if (newAdminData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      await axios.post(`${API}/admin/admins`, newAdminData, authHeaders);
      toast.success("Team member added");
      setNewAdminData({ email: "", password: "", name: "" });
      setIsAddAdminOpen(false);
      fetchAllAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add team member");
    }
  };

  const handleUpdateAdmin = async () => {
    if (!editingAdmin) return;
    try {
      await axios.patch(`${API}/admin/admins/${editingAdmin.id}`, {
        name: editingAdmin.name,
        email: editingAdmin.email,
        phone: editingAdmin.phone,
        address: editingAdmin.address,
        color: editingAdmin.color
      }, authHeaders);
      toast.success("Team member updated");
      setEditingAdmin(null);
      fetchAllAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update team member");
    }
  };

  // ============== RECURRING CONTRACTS ==============
  
  const handleGenerateContractSlots = async (contractId) => {
    try {
      const res = await axios.post(`${API}/admin/recurring-contracts/${contractId}/generate`, {}, authHeaders);
      const { generated_count, conflict_count, conflicts } = res.data;
      
      if (conflict_count > 0) {
        setContractConflicts(conflicts);
        setShowConflicts(true);
        toast.warning(`Generated ${generated_count} slots. ${conflict_count} conflicts found!`);
      } else {
        toast.success(`Generated ${generated_count} slots successfully`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate slots");
    }
  };

  const handleDeleteContract = async (contractId) => {
    if (!window.confirm("Delete this recurring contract? Existing bookings will remain.")) return;
    try {
      await axios.delete(`${API}/admin/recurring-contracts/${contractId}`, authHeaders);
      toast.success("Contract deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete contract");
    }
  };

  const handleToggleContractActive = async (contract) => {
    try {
      await axios.patch(`${API}/admin/recurring-contracts/${contract.id}`, { active: !contract.active }, authHeaders);
      toast.success(contract.active ? "Contract paused" : "Contract activated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update contract");
    }
  };

  const openEditContract = (contract) => {
    setEditingContract(contract);
    setNewContract({
      name: contract.name,
      customer_id: contract.customer_id || "",
      customer_name: contract.customer_name || "",
      customer_address: contract.customer_address || "",
      days_of_week: contract.days_of_week || [],
      start_time: contract.start_time || "10:00",
      end_time: contract.end_time || "20:00",
      assigned_admins: contract.assigned_admins || [],
      rolling_weeks: contract.rolling_weeks || 4,
      block_type: contract.block_type || "job",
      service_type: contract.service_type || "general-repairs"
    });
    setIsAddContractOpen(true);
  };

  const handleSaveContract = async () => {
    if (!newContract.name || newContract.days_of_week.length === 0) {
      toast.error("Contract name and at least one day are required");
      return;
    }
    try {
      if (editingContract) {
        // Update existing contract
        await axios.patch(`${API}/admin/recurring-contracts/${editingContract.id}`, newContract, authHeaders);
        toast.success("Contract updated");
      } else {
        // Create new contract
        await axios.post(`${API}/admin/recurring-contracts`, newContract, authHeaders);
        toast.success("Contract created");
      }
      setNewContract({ 
        name: "", customer_id: "", customer_name: "", customer_address: "",
        days_of_week: [], start_time: "10:00", end_time: "20:00", 
        assigned_admins: [], rolling_weeks: 4, block_type: "job", service_type: "general-repairs"
      });
      setEditingContract(null);
      setIsAddContractOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save contract");
    }
  };

  const closeContractDialog = () => {
    setIsAddContractOpen(false);
    setEditingContract(null);
    setNewContract({ 
      name: "", customer_id: "", customer_name: "", customer_address: "",
      days_of_week: [], start_time: "10:00", end_time: "20:00", 
      assigned_admins: [], rolling_weeks: 4, block_type: "job", service_type: "general-repairs"
    });
  };

  const handleCheckAllConflicts = async () => {
    try {
      const res = await axios.get(`${API}/admin/recurring-contracts/check-conflicts`, authHeaders);
      setContractConflicts(res.data.conflicts);
      setShowConflicts(true);
      if (res.data.total_conflicts === 0) {
        toast.success("No conflicts found!");
      } else {
        toast.warning(`Found ${res.data.total_conflicts} conflicts`);
      }
    } catch (error) {
      toast.error("Failed to check conflicts");
    }
  };

  const updateAdminColor = async (adminId, color) => {
    try {
      await axios.patch(`${API}/admin/admins/${adminId}/color`, { color }, authHeaders);
      fetchAllAdmins();
      toast.success("Color updated");
    } catch (error) {
      toast.error("Failed to update color");
    }
  };

  // Get admin color helper
  const getAdminColor = (adminId) => {
    const admin = allAdmins.find(a => a.id === adminId);
    return admin?.color || TEAM_COLORS[0];
  };

  // Toggle admin selection for multi-select
  const toggleAdminSelection = (admin, currentAdmins, setter) => {
    const exists = currentAdmins.find(a => a.id === admin.id);
    if (exists) {
      setter(currentAdmins.filter(a => a.id !== admin.id));
    } else {
      setter([...currentAdmins, { id: admin.id, name: admin.name || admin.email.split('@')[0] }]);
    }
  };

  const handleLogout = async () => {
    // Clear the httpOnly session cookie server-side, then wipe local flags.
    try { await axios.post(`${API}/admin/logout`); } catch (e) { /* best-effort */ }
    localStorage.removeItem("admin_session");
    localStorage.removeItem("admin_token");
    localStorage.removeItem("offline_cred_hash");
    localStorage.removeItem("offline_admin_email");
    navigate("/admin");
    toast.success("Logged out successfully");
  };

  const handleStatusChange = async (enquiryId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${enquiryId}`, { status: newStatus, next_action_dismissed: false }, authHeaders);
      toast.success("Status updated");
      fetchData();
      if (selectedEnquiry?.id === enquiryId) {
        setSelectedEnquiry(prev => ({ ...prev, status: newStatus, next_action_dismissed: false }));
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedEnquiry) return;
    setIsSavingNotes(true);
    try {
      await axios.patch(`${API}/admin/enquiries/${selectedEnquiry.id}`, { notes }, authHeaders);
      toast.success("Notes saved");
      setSelectedEnquiry(prev => ({ ...prev, notes }));
      fetchData();
    } catch (error) {
      toast.error("Failed to save notes");
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleDelete = async (enquiryId) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${enquiryId}`, authHeaders);
      toast.success("Enquiry deleted");
      fetchData();
      setIsDetailOpen(false);
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleAddEnquiry = () => {
    if (!newEnquiry.name || !newEnquiry.phone || !newEnquiry.message) {
      toast.error("Name, phone and message are required");
      return;
    }
    
    // Validate email format if provided
    const emailToSend = newEnquiry.email && newEnquiry.email !== 'N/A' && newEnquiry.email.includes('@') 
      ? newEnquiry.email 
      : '';
    
    const payload = {
      ...newEnquiry,
      email: emailToSend || 'no-email@placeholder.local' // Use placeholder if no valid email
    };
    
    axios.post(`${API}/admin/enquiries`, payload, authHeaders)
      .then(() => {
        toast.success("Job added successfully!");
        setIsAddEnquiryOpen(false);
        setNewEnquiry({ name: "", email: "", phone: "", service: "general-repairs", message: "", preferred_date: "", source: "whatsapp", customer_id: "" });
        fetchData();
      })
      .catch((error) => {
        console.error("Failed to add job:", error);
        const errorMsg = error.response?.data?.detail;
        if (typeof errorMsg === 'object') {
          toast.error("Validation error - please check all fields");
        } else {
          toast.error(errorMsg || "Failed to add job");
        }
      });
  };

  const openEditEnquiry = (enquiry) => {
    setEditingEnquiry({
      id: enquiry.id,
      name: enquiry.name || '',
      email: enquiry.email || '',
      phone: enquiry.phone || '',
      address: enquiry.address || '',
      service: enquiry.service || 'general-repairs',
      message: enquiry.message || '',
      notes: enquiry.notes || '',
      preferred_date: enquiry.preferred_date || '',
      date_completed: enquiry.date_completed || '',
      source: enquiry.source || 'website',
      customer_id: enquiry.customer_id || ''
    });
    setIsEditEnquiryOpen(true);
    setIsDetailOpen(false);
  };

  const handleSaveEnquiry = async () => {
    if (!editingEnquiry) return;
    try {
      await axios.patch(`${API}/admin/enquiries/${editingEnquiry.id}`, {
        name: editingEnquiry.name,
        email: editingEnquiry.email,
        phone: editingEnquiry.phone,
        address: editingEnquiry.address,
        service: editingEnquiry.service,
        message: editingEnquiry.message,
        notes: editingEnquiry.notes,
        preferred_date: editingEnquiry.preferred_date,
        date_completed: editingEnquiry.date_completed || null,
        customer_id: editingEnquiry.customer_id || null
      }, authHeaders);
      toast.success("Job updated successfully");
      setIsEditEnquiryOpen(false);
      setEditingEnquiry(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update job");
    }
  };

  // Quick field update for master record fields (priority, tags, assigned_admin, estimated_value)
  const handleQuickFieldUpdate = (enquiryId, field, value) => {
    axios.patch(`${API}/admin/enquiries/${enquiryId}`, { [field]: value }, authHeaders)
      .then(() => {
        // Update local state immediately for responsive UI
        setSelectedEnquiry(prev => prev ? { ...prev, [field]: value } : prev);
        // Refresh data in background
        fetchData();
      })
      .catch((error) => {
        console.error(`Failed to update ${field}:`, error);
        toast.error(`Failed to update ${field}`);
      });
  };

  // Handle quick actions from dropdown menu
  const handleQuickAction = async (enquiry, action) => {
    switch (action) {
      case 'generate_quote':
        // First convert to customer if not already, then navigate to quotes
        if (!enquiry.customer_id) {
          try {
            const response = await axios.post(`${API}/admin/enquiries/${enquiry.id}/convert`, {}, authHeaders);
            toast.success("Customer created! Redirecting to create quote...");
            fetchData();
            setActiveTab("quotes-invoices");
            // Store enquiry info for quote creation with job ID for linking
            sessionStorage.setItem('createQuoteFor', JSON.stringify({
              customerId: response.data.customer_id,
              customerName: enquiry.name,
              customerEmail: enquiry.email,
              service: enquiry.service,
              description: enquiry.message,
              jobId: enquiry.id
            }));
          } catch (error) {
            toast.error("Failed to create customer for quote");
          }
        } else {
          setActiveTab("quotes-invoices");
          sessionStorage.setItem('createQuoteFor', JSON.stringify({
            customerId: enquiry.customer_id,
            customerName: enquiry.name,
            customerEmail: enquiry.email,
            service: enquiry.service,
            description: enquiry.message,
            jobId: enquiry.id
          }));
        }
        break;
        
      case 'create_invoice':
        if (!enquiry.customer_id) {
          try {
            const response = await axios.post(`${API}/admin/enquiries/${enquiry.id}/convert`, {}, authHeaders);
            toast.success("Customer created! Redirecting to create invoice...");
            fetchData();
            setActiveTab("quotes-invoices");
            sessionStorage.setItem('createInvoiceFor', JSON.stringify({
              customerId: response.data.customer_id,
              customerName: enquiry.name,
              customerEmail: enquiry.email,
              service: enquiry.service,
              description: enquiry.message,
              jobId: enquiry.id
            }));
          } catch (error) {
            toast.error("Failed to create customer for invoice");
          }
        } else {
          setActiveTab("quotes-invoices");
          sessionStorage.setItem('createInvoiceFor', JSON.stringify({
            customerId: enquiry.customer_id,
            customerName: enquiry.name,
            customerEmail: enquiry.email,
            service: enquiry.service,
            description: enquiry.message,
            jobId: enquiry.id
          }));
        }
        break;
        
      case 'request_review':
        // Open request review dialog
        setRequestReviewJob(enquiry);
        setIsRequestReviewOpen(true);
        break;
        
      case 'add_to_portfolio':
        // Open add to portfolio dialog with job photos
        setAddToPortfolioJob(enquiry);
        setSelectedPhotosForPortfolio([]);
        setPortfolioCategory(enquiry.service?.replace('-', '_') || 'carpentry');
        setIsAddToPortfolioOpen(true);
        break;
        
      default:
    }
  };

  // Handle request review submission
  const handleRequestReview = async (sendEmail = false) => {
    if (!requestReviewJob) return;
    
    try {
      await axios.post(`${API}/admin/feedback/request-review`, {
        job_id: requestReviewJob.id,
        send_email: sendEmail
      }, authHeaders);
      
      toast.success(sendEmail ? "Review request sent!" : "Review request marked!");
      setIsRequestReviewOpen(false);
      setRequestReviewJob(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request review");
    }
  };

  // Handle adding photos to portfolio
  const handleAddPhotosToPortfolio = async () => {
    if (!addToPortfolioJob || selectedPhotosForPortfolio.length === 0) {
      toast.error("Select at least one photo");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/portfolio-photos/from-job`, {
        job_id: addToPortfolioJob.id,
        photo_urls: selectedPhotosForPortfolio,
        category: portfolioCategory
      }, authHeaders);
      
      toast.success(`Added ${selectedPhotosForPortfolio.length} photos to portfolio`);
      setIsAddToPortfolioOpen(false);
      setAddToPortfolioJob(null);
      setSelectedPhotosForPortfolio([]);
      fetchPortfolioPhotos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add photos to portfolio");
    }
  };

  // Toggle photo selection for portfolio
  const togglePhotoForPortfolio = (photoUrl) => {
    setSelectedPhotosForPortfolio(prev => 
      prev.includes(photoUrl) 
        ? prev.filter(url => url !== photoUrl)
        : [...prev, photoUrl]
    );
  };

  // AI Assistance functions
  const generateAIContent = async (type, enquiry) => {
    if (!enquiry) return;
    setAiGenerating(true);
    setAiResultType(type);
    setAiResult(null);
    
    try {
      let endpoint = '';
      let payload = {};
      
      switch (type) {
        case 'quote':
          endpoint = '/api/admin/ai/quote-estimation';
          payload = {
            job_type: enquiry.service || 'general-repairs',
            description: enquiry.message || `Job for ${enquiry.name}`,
            include_materials: true
          };
          break;
        case 'description':
          endpoint = '/api/admin/ai/job-description';
          payload = {
            job_type: enquiry.service || 'general-repairs',
            brief_notes: enquiry.message || `Job for ${enquiry.name}`
          };
          break;
        case 'reply':
          endpoint = '/api/admin/ai/auto-reply';
          payload = {
            customer_name: enquiry.name,
            service_type: enquiry.service || 'general-repairs',
            enquiry_text: enquiry.message || 'Customer enquiry'
          };
          break;
        case 'review_request':
          endpoint = '/api/admin/ai/review-request';
          payload = {
            customer_name: enquiry.name,
            service_type: enquiry.service || 'general-repairs',
            job_description: enquiry.message || 'Completed work'
          };
          break;
        default:
          throw new Error('Unknown AI type');
      }
      
      const response = await axios.post(`${API}${endpoint}`, payload, authHeaders);
      
      if (response.data) {
        const result = response.data.estimation || response.data.description || 
                      response.data.reply || response.data.message || response.data.raw_response || 
                      JSON.stringify(response.data);
        setAiResult(result);
        toast.success('AI content generated!');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error('Failed to generate AI content');
      setAiResult(null);
    } finally {
      setAiGenerating(false);
    }
  };
  
  const copyAIResult = () => {
    if (aiResult) {
      navigator.clipboard.writeText(typeof aiResult === 'string' ? aiResult : JSON.stringify(aiResult, null, 2));
      toast.success('Copied to clipboard!');
    }
  };
  
  const clearAIResult = () => {
    setAiResult(null);
    setAiResultType(null);
  };

  const handleConvertToCustomer = async (enquiryId) => {
    try {
      await axios.post(`${API}/admin/enquiries/${enquiryId}/convert`, {}, authHeaders);
      toast.success("Enquiry converted to customer!");
      fetchData();
      setIsDetailOpen(false);
      setActiveTab("customers");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert");
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers/export`, {
        ...authHeaders,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customers_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV exported!");
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  const openDetail = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setNotes(enquiry.notes || "");
    setIsDetailOpen(true);
  };

  const [publishingToGallery, setPublishingToGallery] = useState(false);
  const handlePublishToGallery = async (enquiry) => {
    setPublishingToGallery(true);
    try {
      const res = await axios.post(`${API}/admin/jobs/${enquiry.id}/publish-to-gallery`, {}, authHeaders);
      if (res.data.status === "published") {
        toast.success(`Published to gallery: "${res.data.title}" — ${res.data.photos?.length || 0} photos, AI-written description & SEO. Tweak it anytime in Site Editor → Gallery.`);
      } else {
        toast.info(`Gallery draft created ("${res.data.title}") — no photos found on this job. Add photos in Site Editor → Gallery, then publish.`);
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to publish to gallery");
    } finally {
      setPublishingToGallery(false);
    }
  };

  // Deep-link resolver: once enquiries/invoices are loaded, open the modal the
  // push notification (or shared link) asked for. Runs whenever pendingDeepLink,
  // enquiries, or invoices change so it handles both cold open and hot open cases.
  useEffect(() => {
    if (!pendingDeepLink) return;
    const { job_id, invoice_id } = pendingDeepLink;

    if (job_id && enquiries && enquiries.length > 0) {
      const match = enquiries.find(e => e.id === job_id);
      if (match) {
        openDetail(match);
        setPendingDeepLink(null);
        setActiveTab('jobs');
        return;
      }
    }

    if (invoice_id && invoices && invoices.length > 0) {
      const match = invoices.find(i => i.id === invoice_id);
      if (match) {
        setEditingInvoice({
          ...match,
          line_items: match.line_items || [],
        });
        setIsEditInvoiceOpen(true);
        setPendingDeepLink(null);
        setActiveTab('quotes-invoices');
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDeepLink, enquiries, invoices]);

  // Customer handlers
  const handleAddCustomer = async () => {
    if (!newCustomer.name || !newCustomer.address) {
      toast.error("Name and address are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/customers`, newCustomer, authHeaders);
      toast.success("Customer added");
      setIsAddCustomerOpen(false);
      setNewCustomer({ name: "", email: "", phone: "", address: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to add customer");
    }
  };

  const handleUpdateCustomer = async () => {
    if (!selectedCustomer) return;
    try {
      await axios.patch(`${API}/admin/customers/${selectedCustomer.id}`, {
        name: selectedCustomer.name,
        email: selectedCustomer.email,
        phone: selectedCustomer.phone,
        address: selectedCustomer.address,
        notes: selectedCustomer.notes
      }, authHeaders);
      toast.success("Customer updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update customer");
    }
  };

  const handleSendPortalInvite = async () => {
    if (!selectedCustomer) return;
    try {
      const response = await axios.post(`${API}/portal/admin/send-invite/${selectedCustomer.id}`, {}, authHeaders);
      if (response.data.email_sent) {
        toast.success(response.data.message);
      } else {
        toast.info(response.data.message);
        // Copy setup link to clipboard
        if (response.data.setup_link) {
          navigator.clipboard.writeText(response.data.setup_link);
          toast.info("Setup link copied to clipboard");
        }
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send portal invite");
    }
  };

  const handleDisablePortalAccess = async (customerId) => {
    if (!window.confirm("Disable portal access for this customer?")) return;
    try {
      await axios.post(`${API}/portal/admin/disable-access/${customerId}`, {}, authHeaders);
      toast.success("Portal access disabled");
      fetchData();
    } catch (error) {
      toast.error("Failed to disable portal access");
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm("Delete this customer and all their job history?")) return;
    try {
      await axios.delete(`${API}/admin/customers/${customerId}`, authHeaders);
      toast.success("Customer deleted");
      fetchData();
      setIsCustomerDetailOpen(false);
    } catch (error) {
      toast.error("Failed to delete customer");
    }
  };

  const handleTaxExport = async (format = 'pdf') => {
    try {
      toast.info(`Preparing tax year ${format.toUpperCase()}...`);
      const token = localStorage.getItem('admin_session');
      const res = await axios.get(`${API}/admin/tax/summary.${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: format === 'pdf' ? 'application/pdf' : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const now = new Date();
      const yearStart = (now.getMonth() > 3 || (now.getMonth() === 3 && now.getDate() >= 6)) ? now.getFullYear() : now.getFullYear() - 1;
      const label = `${yearStart}-${String(yearStart + 1).slice(-2)}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = `SHS_TaxYear_${label}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Tax year ${label} ${format.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to export tax summary`);
    }
  };

  const handleAddJob = async () => {
    if (!selectedCustomer || !newJob.description || !newJob.date) {
      toast.error("Description and date are required");
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/customers/${selectedCustomer.id}/jobs`, newJob, authHeaders);
      toast.success(newJob.is_recurring ? "Recurring job created!" : "Job added");
      setSelectedCustomer(response.data);
      setIsAddJobOpen(false);
      setNewJob({ description: "", date: "", time_slot: "", duration: 2, status: "planned", is_recurring: false, recurring_interval: "", photos: [], featured_in_portfolio: false, assigned_admins: [], is_all_day: false });
      fetchData();
    } catch (error) {
      toast.error("Failed to add job");
    }
  };

  const openJobPhotos = (job, index) => {
    // Normalize photos to new format
    const normalizedPhotos = (job.photos || []).map((photo, i) => {
      if (typeof photo === 'string') {
        return { id: `legacy-${i}`, url: photo, caption: '', photo_type: 'general', order: i };
      }
      return { ...photo, order: photo.order ?? i };
    });
    setSelectedJob({ ...job, photos: normalizedPhotos });
    setSelectedJobIndex(index);
    setIsJobPhotosOpen(true);
  };

  // File upload handler
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedCustomer || selectedJobIndex === null) return;
    
    setIsUploading(true);
    const job = selectedCustomer.jobs[selectedJobIndex];
    let currentPhotos = [...(selectedJob?.photos || [])];
    
    for (const file of files) {
      try {
        // Use the helper function that tries Drive first
        // Customer job photos go to "Customer Database/[Customer Name]" folder
        const uploadResult = await uploadFile(
          file, 
          "customer", 
          selectedCustomer.name, 
          selectedCustomer.id
        );
        
        const newPhoto = {
          id: `photo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          url: uploadResult.url,
          caption: '',
          photo_type: 'general',
          order: currentPhotos.length
        };
        currentPhotos.push(newPhoto);
        
        const storageType = uploadResult.isDrive ? "Google Drive" : "server";
        toast.success(`Uploaded to ${storageType}: ${file.name}`);
      } catch (error) {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }
    
    // Save to database
    try {
      const response = await axios.patch(
        `${API}/admin/customers/${selectedCustomer.id}/jobs/${selectedJobIndex}`,
        { ...job, photos: currentPhotos },
        authHeaders
      );
      setSelectedCustomer(response.data);
      setSelectedJob({ ...job, photos: currentPhotos });
      fetchData();
    } catch (error) {
      toast.error("Failed to save photos");
    }
    
    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddPhoto = async (photoType = 'general') => {
    if (!newPhotoUrl || !selectedCustomer || selectedJobIndex === null) return;
    
    const job = selectedCustomer.jobs[selectedJobIndex];
    const currentPhotos = selectedJob?.photos || [];
    const newPhoto = {
      id: `photo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      url: newPhotoUrl,
      caption: '',
      photo_type: photoType,
      order: currentPhotos.length
    };
    const updatedPhotos = [...currentPhotos, newPhoto];
    
    try {
      const response = await axios.patch(
        `${API}/admin/customers/${selectedCustomer.id}/jobs/${selectedJobIndex}`,
        { ...job, photos: updatedPhotos },
        authHeaders
      );
      setSelectedCustomer(response.data);
      setSelectedJob({ ...job, photos: updatedPhotos });
      setNewPhotoUrl("");
      toast.success("Photo added");
      fetchData();
    } catch (error) {
      toast.error("Failed to add photo");
    }
  };

  const handleRemovePhoto = async (photoIndex) => {
    if (!selectedCustomer || selectedJobIndex === null) return;
    
    const job = selectedCustomer.jobs[selectedJobIndex];
    const updatedPhotos = (selectedJob?.photos || []).filter((_, i) => i !== photoIndex);
    
    try {
      const response = await axios.patch(
        `${API}/admin/customers/${selectedCustomer.id}/jobs/${selectedJobIndex}`,
        { ...job, photos: updatedPhotos },
        authHeaders
      );
      setSelectedCustomer(response.data);
      setSelectedJob({ ...job, photos: updatedPhotos });
      toast.success("Photo removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove photo");
    }
  };

  const handleUpdatePhoto = async (photoIndex, updates) => {
    if (!selectedCustomer || selectedJobIndex === null) return;
    
    const job = selectedCustomer.jobs[selectedJobIndex];
    const updatedPhotos = (selectedJob?.photos || []).map((p, i) => 
      i === photoIndex ? { ...p, ...updates } : p
    );
    
    try {
      const response = await axios.patch(
        `${API}/admin/customers/${selectedCustomer.id}/jobs/${selectedJobIndex}`,
        { ...job, photos: updatedPhotos },
        authHeaders
      );
      setSelectedCustomer(response.data);
      setSelectedJob({ ...job, photos: updatedPhotos });
      setEditingCaption(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update photo");
    }
  };

  // Drag and drop reordering
  const handleDragStart = (e, index) => {
    setDraggedPhoto(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedPhoto === null || draggedPhoto === index) return;
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedPhoto === null || draggedPhoto === dropIndex || !selectedCustomer || selectedJobIndex === null) {
      setDraggedPhoto(null);
      return;
    }
    
    const job = selectedCustomer.jobs[selectedJobIndex];
    const photos = [...(selectedJob?.photos || [])];
    const [draggedItem] = photos.splice(draggedPhoto, 1);
    photos.splice(dropIndex, 0, draggedItem);
    
    // Update order values
    const reorderedPhotos = photos.map((p, i) => ({ ...p, order: i }));
    
    try {
      const response = await axios.patch(
        `${API}/admin/customers/${selectedCustomer.id}/jobs/${selectedJobIndex}`,
        { ...job, photos: reorderedPhotos },
        authHeaders
      );
      setSelectedCustomer(response.data);
      setSelectedJob({ ...job, photos: reorderedPhotos });
      toast.success("Photos reordered");
      fetchData();
    } catch (error) {
      toast.error("Failed to reorder photos");
    }
    
    setDraggedPhoto(null);
  };

  const handleToggleFeatured = async () => {
    if (!selectedCustomer || selectedJobIndex === null) return;
    
    const job = selectedCustomer.jobs[selectedJobIndex];
    const newFeatured = !job.featured_in_portfolio;
    
    try {
      const response = await axios.patch(
        `${API}/admin/customers/${selectedCustomer.id}/jobs/${selectedJobIndex}`,
        { ...job, featured_in_portfolio: newFeatured, photos: selectedJob?.photos || [] },
        authHeaders
      );
      setSelectedCustomer(response.data);
      setSelectedJob({ ...selectedJob, featured_in_portfolio: newFeatured });
      toast.success(newFeatured ? "Photos added to portfolio!" : "Photos removed from portfolio");
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleUpdateJobStatus = async (jobIndex, newStatus) => {
    if (!selectedCustomer) return;
    const job = selectedCustomer.jobs[jobIndex];
    try {
      const response = await axios.patch(
        `${API}/admin/customers/${selectedCustomer.id}/jobs/${jobIndex}`,
        { ...job, status: newStatus },
        authHeaders
      );
      toast.success("Job status updated");
      setSelectedCustomer(response.data);
      fetchData();
    } catch (error) {
      toast.error("Failed to update job");
    }
  };

  const handleDeleteJob = async (jobIndex) => {
    if (!selectedCustomer) return;
    if (!window.confirm("Delete this job?")) return;
    try {
      const response = await axios.delete(`${API}/admin/customers/${selectedCustomer.id}/jobs/${jobIndex}`, authHeaders);
      toast.success("Job deleted");
      setSelectedCustomer(response.data);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  // Blocked days handlers
  const handleBlockDay = async () => {
    // Determine which days to schedule
    let daysToSchedule = [];
    
    if (newBlockedDay.scheduledDays && newBlockedDay.scheduledDays.length > 0) {
      // Multi-day or range selection
      daysToSchedule = [...newBlockedDay.scheduledDays];
    } else if (newBlockedDay.date) {
      // Single day
      daysToSchedule = [newBlockedDay.date];
    }
    
    if (daysToSchedule.length === 0 || !newBlockedDay.reason) {
      toast.error("Date and reason required");
      return;
    }
    
    try {
      // Calculate pricing for multi-day jobs
      const teamSize = newBlockedDay.teamSize || 1;
      const pricing = calculateJobPrice(daysToSchedule.length, teamSize, newBlockedDay.multiDayDiscount);
      
      // Create blocked day entries for each scheduled day
      let blockedCount = 0;
      const linkedJobId = newBlockedDay.linkedJobId || `job_${Date.now()}`;
      
      for (const dayDate of daysToSchedule) {
        // Create ONE blocked entry per day with full duration
        // (instead of separate entries for each hour)
        const payload = {
          date: dayDate,
          reason: newBlockedDay.reason,
          block_type: newBlockedDay.block_type,
          time_slot: newBlockedDay.time_slot || null,
          duration: newBlockedDay.time_slot ? (newBlockedDay.duration || 1) : 1,  // Store full duration
          assigned_admins: newBlockedDay.assigned_admins || [],
          linked_job_id: linkedJobId,
          team_size: teamSize,
          day_rate: pricing.dayRate,
          is_multi_day: daysToSchedule.length > 1,
          total_days: daysToSchedule.length,
        };
        
        try {
          await axios.post(`${API}/admin/blocked-days`, payload, authHeaders);
          blockedCount++;
        } catch (e) {
          // Skip if already blocked
          if (process.env.NODE_ENV === "development") console.warn("Could not block day:", dayDate, e);
        }
      }
      
      // Update linked enquiry with scheduled_days if we have one
      if (newBlockedDay.linkedJobId) {
        try {
          await axios.patch(`${API}/admin/enquiries/${newBlockedDay.linkedJobId}`, {
            scheduled_days: daysToSchedule,
            team_size: teamSize,
            day_rate: pricing.dayRate,
            multi_day_discount: pricing.discountPercent || null,
            estimated_value: pricing.total,
            status: 'scheduled',
            preferred_date: daysToSchedule[0], // First day as primary date
          }, authHeaders);
        } catch (e) {
          if (process.env.NODE_ENV === "development") console.warn("Could not update linked enquiry:", e);
        }
      }
      
      // Show success message with pricing info
      if (daysToSchedule.length > 1) {
        const discountText = pricing.discountPercent > 0 ? ` (${pricing.discountPercent}% multi-day discount)` : '';
        toast.success(`${daysToSchedule.length} days scheduled - £${pricing.total.toLocaleString()}${discountText}`);
      } else {
        toast.success(newBlockedDay.time_slot ? "Time slot blocked" : "Day blocked");
      }
      
      setIsBlockDayOpen(false);
      setNewBlockedDay({ 
        date: "", reason: "", block_type: "enquiry", time_slot: "", duration: 1, assigned_admins: [],
        scheduledDays: [], teamSize: 1, dayRate: PRICING.DAY_RATES[1], multiDayDiscount: null,
        selectionMode: "single", rangeStart: null, rangeEnd: null, linkedJobId: null,
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to block");
    }
  };

  const handleUnblockDay = async (blockedId) => {
    try {
      await axios.delete(`${API}/admin/blocked-days/${blockedId}`, authHeaders);
      toast.success("Unblocked");
      fetchData();
    } catch (error) {
      toast.error("Failed to unblock");
    }
  };

  const openBlockDayDialog = (date, timeSlot = "", prefillReason = "", prefillType = "enquiry") => {
    setNewBlockedDay({ 
      date: date ? getLocalDateStr(date) : getLocalDateStr(new Date()), 
      reason: prefillReason, 
      block_type: prefillType,
      time_slot: timeSlot,
      duration: 1
    });
    setIsBlockDayOpen(true);
  };

  const bookSlotForEnquiry = (enquiry) => {
    const prefillReason = `${enquiry.name} - ${serviceLabels[enquiry.service] || enquiry.service}`;
    setNewBlockedDay({
      date: enquiry.preferred_date || getLocalDateStr(new Date()),
      reason: prefillReason,
      block_type: "enquiry",
      time_slot: "",
      duration: 2
    });
    setIsBlockDayOpen(true);
    setIsDetailOpen(false);
  };

  // Drag and drop handlers for calendar scheduling
  const handleJobDragStart = (e, job) => {
    setDraggedJob(job);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', job.id);
    // Add visual feedback
    e.currentTarget.style.opacity = '0.5';
  };
  
  const handleJobDragEnd = (e) => {
    setDraggedJob(null);
    setDropTargetDate(null);
    e.currentTarget.style.opacity = '1';
  };
  
  const handleCalendarDragOver = (e, date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const dateStr = getLocalDateStr(date);
    if (dropTargetDate !== dateStr) {
      setDropTargetDate(dateStr);
    }
  };
  
  const handleCalendarDragLeave = (e) => {
    // Only clear if leaving to outside the drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTargetDate(null);
    }
  };
  
  const handleCalendarDrop = (e, date) => {
    e.preventDefault();
    setDropTargetDate(null);
    
    if (!draggedJob) return;
    
    const dateStr = getLocalDateStr(date);
    const prefillReason = `${draggedJob.name} - ${serviceLabels[draggedJob.service] || draggedJob.service}`;
    
    // Open the booking dialog with the dropped date
    setNewBlockedDay({
      date: dateStr,
      reason: prefillReason,
      block_type: "enquiry",
      time_slot: "",
      duration: 2
    });
    setIsBlockDayOpen(true);
    setDraggedJob(null);
    
    toast.success(`Scheduling ${draggedJob.name} for ${formatDate(dateStr)}`);
  };

  const openCustomerDetail = (customer) => {
    setSelectedCustomer(customer);
    setIsCustomerDetailOpen(true);
  };

  const printDaySchedule = () => {
    const selectedDateStr = getLocalDateStr(calendarDate);
    const jobsForDate = customers.flatMap(customer => 
      (customer.jobs || [])
        .map((job, index) => ({ ...job, customer, jobIndex: index }))
        .filter(job => job.date === selectedDateStr)
    ).sort((a, b) => (a.time_slot || "").localeCompare(b.time_slot || ""));
    
    const blockedForDate = blockedDays.filter(b => b.date === selectedDateStr);

    const generateSlotContent = (slot) => {
      const job = jobsForDate.find(j => j.time_slot === slot);
      const blocked = blockedForDate.find(b => b.time_slot === slot);
      const spanningJob = jobsForDate.find(j => {
        if (!j.time_slot || j.time_slot === slot) return false;
        const startIdx = TIME_SLOTS.indexOf(j.time_slot);
        const endIdx = startIdx + (j.duration || 1);
        return TIME_SLOTS.indexOf(slot) > startIdx && TIME_SLOTS.indexOf(slot) < endIdx;
      });
      
      if (blocked) {
        return '<div class="blocked"><strong>Blocked:</strong> ' + blocked.reason + '</div>';
      } else if (job) {
        return '<div class="job"><span class="badge ' + job.status + '">' + job.status + '</span><strong>' + job.description + '</strong> (' + (job.duration || 1) + 'hrs)<div class="customer">' + job.customer.name + ' - ' + job.customer.address + '</div><div class="customer">Tel: ' + (job.customer.phone || 'N/A') + '</div></div>';
      } else if (spanningJob) {
        return '<div class="job" style="opacity: 0.7;">↳ ' + spanningJob.description + ' (continued)</div>';
      }
      return '<span class="available">Available</span>';
    };

    const slotsHtml = TIME_SLOTS.map(slot => 
      '<div class="slot"><div class="time">' + slot + '</div><div class="content">' + generateSlotContent(slot) + '</div></div>'
    ).join('');

    const printContent = '<!DOCTYPE html><html><head><title>Schedule - ' + formatDate(calendarDate?.toISOString()) + '</title><style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#2F4F4F;margin-bottom:5px}h2{color:#666;font-weight:normal;margin-top:0}.slot{display:flex;border-bottom:1px solid #eee;padding:10px 0}.time{width:80px;font-weight:bold;color:#2F4F4F}.content{flex:1}.job{background:#f5f5f5;padding:10px;border-radius:8px;margin-bottom:5px}.blocked{background:#fee2e2;padding:10px;border-radius:8px;color:#991b1b}.available{color:#999;font-style:italic}.customer{color:#666;font-size:14px}.badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:12px;margin-right:8px}.planned{background:#dbeafe;color:#1e40af}.in-progress{background:#fef3c7;color:#92400e}.completed{background:#d1fae5;color:#065f46}</style></head><body><h1>Sustainable Handyman Services</h1><h2>Schedule for ' + formatDate(calendarDate?.toISOString()) + '</h2>' + slotsHtml + '</body></html>';
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const exportWeekSchedule = () => {
    const startDate = new Date(calendarDate);
    startDate.setDate(startDate.getDate() - startDate.getDay() + 1);
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      weekDays.push(d);
    }

    const headerRow = '<tr><th>Time</th>' + weekDays.map(d => '<th>' + d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) + '</th>').join('') + '</tr>';
    
    const bodyRows = TIME_SLOTS.map(slot => {
      const cells = weekDays.map(day => {
        const dateStr = getLocalDateStr(day);
        const job = customers.flatMap(c => (c.jobs || []).map(j => ({...j, customer: c}))).find(j => j.date === dateStr && j.time_slot === slot);
        const blocked = blockedDays.find(b => b.date === dateStr && b.time_slot === slot);
        const wholeDayBlocked = blockedDays.find(b => b.date === dateStr && !b.time_slot);
        
        if (wholeDayBlocked) return '<td><div class="blocked">' + wholeDayBlocked.reason + '</div></td>';
        if (blocked) return '<td><div class="blocked">' + blocked.reason + '</div></td>';
        if (job) return '<td><div class="job">' + job.customer.name + '<br/>' + job.description.substring(0,20) + '...</div></td>';
        return '<td></td>';
      }).join('');
      return '<tr><td class="time-col">' + slot + '</td>' + cells + '</tr>';
    }).join('');

    const printContent = '<!DOCTYPE html><html><head><title>Week Schedule</title><style>body{font-family:Arial,sans-serif;padding:20px;font-size:11px}h1{color:#2F4F4F;margin-bottom:5px;font-size:18px}table{width:100%;border-collapse:collapse;margin-top:15px}th{background:#2F4F4F;color:white;padding:8px;text-align:left}td{border:1px solid #ddd;padding:4px;vertical-align:top;height:40px}.time-col{width:60px;font-weight:bold;background:#f5f5f5}.job{background:#e8f5e9;padding:2px 4px;border-radius:3px;margin:1px 0;font-size:10px}.blocked{background:#ffebee;padding:2px 4px;border-radius:3px;margin:1px 0;font-size:10px;color:#c62828}</style></head><body><h1>Sustainable Handyman Services - Week Schedule</h1><p>Week of ' + formatDate(startDate.toISOString()) + '</p><table>' + headerRow + bodyRows + '</table></body></html>';
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const filteredEnquiries = enquiries.filter(e => {
    // When viewing "all", hide paid/invoiced jobs to reduce clutter
    // User can still filter specifically for "paid" or "invoiced" if needed
    const isCompletedStage = ['paid', 'invoiced'].includes(e.status);
    const matchesStatus = filterStatus === "all" 
      ? !isCompletedStage  // Hide completed jobs in "all" view
      : e.status === filterStatus;  // Show specific status when filtering
    const matchesSearch = searchTerm === "" || 
      e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const filteredCustomers = customers.filter(c => {
    return customerSearchTerm === "" ||
      c.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
      c.address.toLowerCase().includes(customerSearchTerm.toLowerCase());
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  // Helper to get local date string (YYYY-MM-DD) from a Date object
  // This avoids timezone issues with toISOString() which converts to UTC
  const getLocalDateStr = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get jobs for time slot view (includes both regular jobs and Gilberts shifts)
  const getJobsForDateWithSlots = () => {
    const selectedDateStr = getLocalDateStr(calendarDate);
    
    // Get regular customer jobs
    const regularJobs = customers.flatMap(customer => 
      (customer.jobs || [])
        .map((job, index) => ({ ...job, customer, jobIndex: index }))
        .filter(job => job.date === selectedDateStr)
    );
    
    // Get Gilberts shifts as job-like objects
    const gilbertsJobsForDate = gilbertsShifts
      .filter(s => s.date === selectedDateStr)
      .map(s => ({
        id: s.id,
        description: `Gilberts Contract - ${s.entries?.map(e => `${e.admin_name}: ${e.hours}hrs ${e.role === 'cnc' ? 'CNC' : 'Maintenance'}`).join(', ') || 'Shift'}`,
        time_slot: '10:00',
        date: s.date,
        duration: Math.max(s.entries?.reduce((sum, e) => Math.max(sum, e.hours || 0), 0), 8),
        customer: { name: 'JR Gilberts Engineering', address: 'Industrial Estate, Abergavenny' },
        status: s.invoiced ? 'completed' : 'in-progress',
        isGilberts: true,
        gilbertsShiftId: s.id,
        total_amount: s.total_amount,
        invoiced: s.invoiced
      }));
    
    return [...regularJobs, ...gilbertsJobsForDate];
  };

  // Helper to check if a job is part of a multi-day job series
  const getMultiDayJobInfo = (job, allJobs) => {
    if (!job.description || !job.customer?.id) return null;
    
    // Find all jobs from the same customer with the same description
    const relatedJobs = allJobs.filter(j => 
      j.customer?.id === job.customer?.id && 
      j.description === job.description &&
      j.date
    ).sort((a, b) => a.date.localeCompare(b.date));
    
    if (relatedJobs.length <= 1) return null;
    
    const jobIndex = relatedJobs.findIndex(j => j.date === job.date);
    const isFirst = jobIndex === 0;
    const isLast = jobIndex === relatedJobs.length - 1;
    const isMiddle = !isFirst && !isLast;
    
    return {
      isMultiDay: true,
      isFirst,
      isLast,
      isMiddle,
      totalDays: relatedJobs.length,
      dayNumber: jobIndex + 1
    };
  };

  return (
    <div className="min-h-screen bg-muted">
      <NoindexHead title={documentTitleForTab(activeTab)} />
      {/* Mobile Header - Always visible on mobile */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-primary text-white p-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Logo" className="h-8 w-8 rounded-full bg-white object-cover" />
          <span className="font-semibold text-sm">Admin Panel</span>
        </div>
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
          className="p-2 hover:bg-white/10 rounded-lg"
          data-testid="mobile-menu-btn"
        >
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50" 
          onClick={() => setIsMobileMenuOpen(false)} 
        />
      )}

      {/* Sidebar - Hidden by default on mobile, always visible on desktop */}
      <aside className={`
        w-64 bg-primary text-primary-foreground h-screen fixed left-0 top-0 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0 z-50' : '-translate-x-full z-30'}
        lg:translate-x-0 lg:z-30
      `}>
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="h-10 w-10 rounded-full bg-white object-cover" />
            <div>
              <h2 className="font-semibold font-heading text-sm">Sustainable Handyman</h2>
              <p className="text-xs text-white/60">Admin Panel</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            <li>
              <Link
                to="/admin/jobs"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={activeTab === "jobs" ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "jobs" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid="nav-jobs"
              >
                <Briefcase className="h-5 w-5" />
                Jobs
              </Link>
            </li>
            <li>
              <Link
                to="/admin/site-mode"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={activeTab === "site-mode" ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "site-mode" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid="nav-site-mode"
              >
                <MapPin className="h-5 w-5" />
                Site Mode
              </Link>
            </li>
            <li>
              <Link
                to="/admin/calendar"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={activeTab === "calendar" ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "calendar" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid="nav-calendar"
              >
                <CalendarIcon className="h-5 w-5" />
                Calendar
              </Link>
            </li>
            <li>
              <Link
                to="/admin/customers"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={activeTab === "customers" ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "customers" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid="nav-customers"
              >
                <Users className="h-5 w-5" />
                Customers
              </Link>
            </li>
            <li>
              <Link
                to="/admin/quotes-invoices"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={activeTab === "quotes-invoices" ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "quotes-invoices" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid="nav-quotes-invoices"
              >
                <FileText className="h-5 w-5" />
                Quotes & Invoices
              </Link>
            </li>
            <li>
              <Link
                to="/admin/gilberts"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={activeTab === "gilberts" ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "gilberts" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid="nav-gilberts"
              >
                <Factory className="h-5 w-5" />
                Gilberts
              </Link>
            </li>
            <li>
              <Link
                to="/admin/lightbox"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={activeTab === "portfolio" ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "portfolio" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid="nav-portfolio"
              >
                <Image className="h-5 w-5" />
                Lightbox
              </Link>
            </li>
            <li>
              <Link
                to="/admin/feedback"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={activeTab === "feedback" ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "feedback" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid="nav-feedback"
              >
                <MessageSquareText className="h-5 w-5" />
                Feedback
              </Link>
            </li>
            <li>
              <Link
                to="/admin/analytics"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={activeTab === "analytics" ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "analytics" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid="nav-analytics"
              >
                <BarChart3 className="h-5 w-5" />
                Analytics
              </Link>
            </li>
            <li>
              <Link
                to="/admin/ai-assistant"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={activeTab === "ai-assistant" ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "ai-assistant" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid="nav-ai-assistant"
              >
                <Sparkles className="h-5 w-5" />
                AI Assistant
              </Link>
            </li>
            <li>
              <Link
                to="/admin/materials"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={activeTab === "materials" ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "materials" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid="nav-materials"
              >
                <Package className="h-5 w-5" />
                Materials
              </Link>
            </li>
            <li>
              <Link
                to="/admin/site-editor"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={activeTab === "site-editor" ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "site-editor" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid="nav-site-editor"
              >
                <FileText className="h-5 w-5" />
                Site Editor
              </Link>
            </li>
            <li>
              <Link
                to="/admin/settings"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={activeTab === "settings" ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "settings" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"}`}
                data-testid="nav-settings"
              >
                <Settings className="h-5 w-5" />
                Settings
              </Link>
            </li>
          </ul>
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <button 
            onClick={() => { handleExportCSV(); setIsMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors text-sm"
            data-testid="export-csv-btn"
          >
            <Download className="h-4 w-4" />
            Export Customers
          </button>
          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-white/80 hover:text-white transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
            Logout
          </button>
          <button
            onClick={handleForceReload}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors text-xs"
            title="Clear cached app shell and reload (fixes stale UI)"
            data-testid="force-reload-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Force refresh app
          </button>
          <div className="px-2 pt-1 text-[10px] text-white/40 text-center" data-testid="app-version-stamp">
            Build {APP_BUILD_VERSION}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 p-3 sm:p-4 lg:p-8 pt-16 lg:pt-8 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Iter 65+: Version-drift banner removed per user directive —
              the sidebar "Force refresh app" button + service-worker auto-
              update handle staleness silently. */}
          {/* Header - hidden in Site Mode for clean field experience */}
          {activeTab !== "site-mode" && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 lg:mb-8">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-heading text-foreground" data-testid="admin-page-heading">
                {sectionForTab(activeTab)?.label || "Page not found"}
              </h1>
              <p className="text-xs sm:text-sm lg:text-base text-muted-foreground">
                {sectionForTab(activeTab)?.subtitle || "This admin page does not exist"}
              </p>
            </div>
            <Button onClick={fetchData} variant="outline" size="sm" className="gap-2 w-full sm:w-auto" data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
          )}

          {activeTab === "__not-found__" && (
            <Card className="max-w-lg" data-testid="admin-not-found">
              <CardContent className="p-6 space-y-3">
                <p className="font-semibold text-lg">Page not found</p>
                <p className="text-sm text-muted-foreground">This admin page does not exist. Check the address, or head back to Jobs.</p>
                <Button asChild data-testid="admin-not-found-jobs-link">
                  <Link to="/admin/jobs">Go to Jobs</Link>
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Command Center Dashboard - only shown on Jobs tab (main dashboard view) */}
          {activeTab === "jobs" && (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 lg:mb-8">
            {/* TODAY Section */}
            <Card className="col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-blue-500" />
                  Today
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between items-center p-2 rounded hover:bg-muted/50 cursor-pointer" onClick={() => setActiveTab("calendar")}>
                  <span className="text-sm">Scheduled Jobs <MetricInfo metricKey="today_scheduled" /></span>
                  <Badge variant="outline">{enquiries.filter(e => {
                    const today = getLocalDateStr(new Date());
                    return e.preferred_date === today && ['scheduled', 'in_progress'].includes(normalizeJobStatus(e.status));
                  }).length}</Badge>
                </div>
                <div className="flex justify-between items-center p-2 rounded hover:bg-muted/50 cursor-pointer" onClick={() => { setActiveTab("jobs"); setFilterStatus("in_progress"); }}>
                  <span className="text-sm">In Progress <MetricInfo metricKey="jobs_in_progress" /></span>
                  <Badge variant="outline" className="bg-blue-50">{metrics?.jobs_in_progress?.value ?? enquiries.filter(e => normalizeJobStatus(e.status) === 'in_progress').length}</Badge>
                </div>
                <div className="flex justify-between items-center p-2 rounded hover:bg-amber-50 cursor-pointer" onClick={() => { setActiveTab("jobs"); }}>
                  <span className="text-sm text-amber-700">Need Action <MetricInfo metricKey="need_action" /></span>
                  <Badge className="bg-amber-100 text-amber-800">{enquiries.filter(e => e.priority === 'urgent' || (normalizeJobStatus(e.status) === 'accepted' && !e.preferred_date)).length}</Badge>
                </div>
              </CardContent>
            </Card>
            
            {/* THIS WEEK Section */}
            <Card className="col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4 text-purple-500" />
                  This Week
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between items-center p-2 rounded hover:bg-muted/50 cursor-pointer" onClick={() => setActiveTab("calendar")}>
                  <span className="text-sm">Open Jobs <MetricInfo metricKey="jobs_open" /></span>
                  <Badge variant="outline" data-testid="stat-upcoming-jobs">{metrics?.jobs_open?.value ?? (() => {
                    const uniqueIds = new Set(
                      enquiries
                        .filter(e => UPCOMING_JOB_STATUSES.includes(normalizeJobStatus(e.status)))
                        .map(e => e.id)
                    );
                    return uniqueIds.size;
                  })()}</Badge>
                </div>
                <div className="flex justify-between items-center p-2 rounded hover:bg-muted/50 cursor-pointer" onClick={() => { setActiveTab("jobs"); setFilterStatus("accepted"); }}>
                  <span className="text-sm">Awaiting Schedule <MetricInfo metricKey="jobs_accepted" /></span>
                  <Badge variant="outline" className="bg-cyan-50">{metrics?.jobs_accepted?.value ?? enquiries.filter(e => normalizeJobStatus(e.status) === 'accepted').length}</Badge>
                </div>
                <div className="flex justify-between items-center p-2 rounded hover:bg-muted/50 cursor-pointer" onClick={() => { setActiveTab("jobs"); setFilterStatus("quoted"); }}>
                  <span className="text-sm">Jobs Quoted <MetricInfo metricKey="jobs_quoted" /></span>
                  <Badge variant="outline" className="bg-amber-50">{metrics?.jobs_quoted?.value ?? enquiries.filter(e => normalizeJobStatus(e.status) === 'quoted').length}</Badge>
                </div>
              </CardContent>
            </Card>
            
            {/* COMMERCIAL Section */}
            <Card className="col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <PoundSterling className="h-4 w-4 text-green-500" />
                  Commercial
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between items-center p-2 rounded hover:bg-muted/50 cursor-pointer" onClick={() => setActiveTab("quotes-invoices")}>
                  <span className="text-sm">Lifetime Income <MetricInfo metricKey="lifetime_income" /></span>
                  <span className="font-bold text-green-700">{!invoicesLoaded ? '—' : `£${
                    invoices.reduce((sum, inv) => sum + (inv.amount_paid || (inv.status === 'paid' ? (inv.total || 0) : 0)), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
                  }`}</span>
                </div>
                <div className="flex justify-between items-center p-2 rounded hover:bg-green-50 cursor-pointer" onClick={() => setActiveTab("quotes-invoices")}>
                  <span className="text-sm">Tax Year <MetricInfo metricKey="tax_year_income" /></span>
                  <span className="font-medium text-green-600">{!invoicesLoaded ? '—' : `£${(() => {
                    const now = new Date();
                    // UK tax year starts 6 April
                    const year = (now.getMonth() > 3) || (now.getMonth() === 3 && now.getDate() >= 6) ? now.getFullYear() : now.getFullYear() - 1;
                    const taxYearStart = new Date(year, 3, 6);
                    const getLatestPaymentDate = (inv) => {
                      const pays = Array.isArray(inv.payments) ? inv.payments : [];
                      if (pays.length > 0) {
                        const max = pays.reduce((m, p) => {
                          const d = new Date(p.date || p.created_at);
                          return (!m || d > m) ? d : m;
                        }, null);
                        if (max) return max;
                      }
                      return new Date(inv.paid_at || inv.deposit_paid_at || inv.date || inv.created_at);
                    };
                    return invoices.filter(inv => {
                      if (!((inv.amount_paid || 0) > 0 || inv.status === 'paid')) return false;
                      return getLatestPaymentDate(inv) >= taxYearStart;
                    }).reduce((sum, inv) => sum + (inv.amount_paid || inv.total || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
                  })()}`}</span>
                </div>
                <div className="flex justify-between items-center p-2 rounded hover:bg-green-50 cursor-pointer" onClick={() => setActiveTab("quotes-invoices")}>
                  <span className="text-sm">Payments This Month <MetricInfo metricKey="payments_this_month" /></span>
                  <span className="font-medium text-green-600">{!invoicesLoaded ? '—' : `£${(() => {
                    const now = new Date();
                    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                    const getLatestPaymentDate = (inv) => {
                      const pays = Array.isArray(inv.payments) ? inv.payments : [];
                      if (pays.length > 0) {
                        const max = pays.reduce((m, p) => {
                          const d = new Date(p.date || p.created_at);
                          return (!m || d > m) ? d : m;
                        }, null);
                        if (max) return max;
                      }
                      return new Date(inv.paid_at || inv.deposit_paid_at || inv.date || inv.created_at);
                    };
                    return invoices.filter(inv => {
                      if (!((inv.amount_paid || 0) > 0 || inv.status === 'paid')) return false;
                      return getLatestPaymentDate(inv) >= monthStart;
                    }).reduce((sum, inv) => sum + (inv.amount_paid || inv.total || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
                  })()}`}</span>
                </div>
                <div className="flex justify-between items-center p-2 rounded hover:bg-muted/50 cursor-pointer" onClick={() => { setActiveTab("jobs"); setFilterStatus("quoted"); }}>
                  <span className="text-sm">Quote Pipeline <MetricInfo metricKey="quote_pipeline_value" /></span>
                  <span className="font-medium" data-testid="stat-quote-pipeline">£{(metrics?.pending_quote_value?.value ?? enquiries.filter(e => ['new', 'site_visit', 'quoted'].includes(normalizeJobStatus(e.status))).reduce((sum, e) => sum + (e.estimated_value || 0), 0)).toLocaleString()}</span>
                </div>
                <div className="pt-1 border-t border-muted mt-1 flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 h-8 text-xs gap-1"
                    onClick={() => handleTaxExport('pdf')}
                    data-testid="tax-summary-pdf-btn"
                  >
                    <Download className="h-3.5 w-3.5" /> Tax Year PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 h-8 text-xs gap-1"
                    onClick={() => handleTaxExport('csv')}
                    data-testid="tax-summary-csv-btn"
                  >
                    <Download className="h-3.5 w-3.5" /> CSV
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* ACTIONS NEEDED Section */}
            <Card className="col-span-1 border-amber-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  Actions Needed
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between items-center p-2 rounded hover:bg-amber-50 cursor-pointer" onClick={() => { setActiveTab("jobs"); setFilterStatus("accepted"); }}>
                  <span className="text-sm">Not Scheduled</span>
                  <Badge className="bg-amber-100 text-amber-800">{enquiries.filter(e => normalizeJobStatus(e.status) === 'accepted' && !e.preferred_date).length}</Badge>
                </div>
                <div className="flex justify-between items-center p-2 rounded hover:bg-amber-50 cursor-pointer" onClick={() => { setActiveTab("jobs"); setFilterStatus("completed"); }}>
                  <span className="text-sm">Not Invoiced <MetricInfo metricKey="not_invoiced" /></span>
                  <Badge className="bg-amber-100 text-amber-800">{enquiries.filter(e => normalizeJobStatus(e.status) === 'completed' && !e.invoice_id).length}</Badge>
                </div>
                <div className="flex justify-between items-center p-2 rounded hover:bg-amber-50 cursor-pointer" onClick={() => { setActiveTab("jobs"); setFilterStatus("paid"); }}>
                  <span className="text-sm">No Review Request <MetricInfo metricKey="no_review_request" /></span>
                  <Badge className="bg-amber-100 text-amber-800">{enquiries.filter(e => {
                    // Iter 69/70: only flag jobs created on or after 1 December 2025
                    // and not yet asked, declined or actively suppressed.
                    // Historical imported invoices before that cutoff and rows the
                    // admin has explicitly suppressed are excluded so the counter
                    // reflects ACTIVE post-launch customers only.
                    if (normalizeJobStatus(e.status) !== 'paid') return false;
                    if (e.review_status !== 'not_requested') return false;
                    const ts = (e.created_at || e.date_completed || '').slice(0, 10);
                    return ts && ts >= '2025-12-01';
                  }).length}</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Quick Stats Row */}
          <div className="overflow-x-auto pb-2 mb-4 lg:mb-8 -mx-3 px-3 sm:mx-0 sm:px-0">
            <div className="flex lg:grid lg:grid-cols-6 gap-2 lg:gap-4 min-w-max lg:min-w-0">
              <Card 
                className="min-w-[100px] cursor-pointer hover:bg-accent/5 transition-colors"
                onClick={() => { setActiveTab("jobs"); setFilterStatus("all"); }}
                data-testid="stat-enquiries"
              >
                <CardContent className="p-3 lg:pt-6"><div className="text-center"><p className="text-xs lg:text-sm text-muted-foreground">Total Jobs <MetricInfo metricKey="total_jobs" /></p><p className="text-lg lg:text-2xl font-bold" data-testid="stat-total-jobs">{metrics?.total_jobs?.value ?? (() => {
                  const excluded = ['rejected', 'on_hold', 'cancelled', 'spam', 'duplicate'];
                  const unique = new Set(
                    enquiries
                      .filter(e => !excluded.includes(normalizeJobStatus(e.status)))
                      .map(e => e.id)
                  );
                  return unique.size;
                })()}</p></div></CardContent>
              </Card>
              <Card 
                className="min-w-[100px] cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => { setActiveTab("jobs"); setFilterStatus("new"); }}
                data-testid="stat-new"
              >
                <CardContent className="p-3 lg:pt-6"><div className="text-center"><p className="text-xs lg:text-sm text-muted-foreground">New <MetricInfo metricKey="jobs_new" /></p><p className="text-lg lg:text-2xl font-bold text-blue-600">{metrics?.jobs_new?.value ?? enquiries.filter(e => normalizeJobStatus(e.status) === 'new').length}</p></div></CardContent>
              </Card>
              <Card 
                className="min-w-[100px] cursor-pointer hover:bg-cyan-50 transition-colors"
                onClick={() => { setActiveTab("jobs"); setFilterStatus("scheduled"); }}
                data-testid="stat-scheduled"
              >
                <CardContent className="p-3 lg:pt-6"><div className="text-center"><p className="text-xs lg:text-sm text-muted-foreground">Scheduled <MetricInfo metricKey="jobs_scheduled" /></p><p className="text-lg lg:text-2xl font-bold text-cyan-600">{metrics?.jobs_scheduled?.value ?? enquiries.filter(e => normalizeJobStatus(e.status) === 'scheduled').length}</p></div></CardContent>
              </Card>
              {/* Iter 71: removed redundant "Completed" stat card — pipeline ends
                  at Paid, so a Completed counter was always 0 and confused users. */}
              <Card 
                className="min-w-[100px] cursor-pointer hover:bg-primary/5 transition-colors"
                onClick={() => { setActiveTab("customers"); }}
                data-testid="stat-customers"
              >
                <CardContent className="p-3 lg:pt-6"><div className="text-center"><p className="text-xs lg:text-sm text-muted-foreground">Customers <MetricInfo metricKey="customers_total" /></p><p className="text-lg lg:text-2xl font-bold text-primary">{metrics?.customers_total?.value ?? stats.total_customers}</p></div></CardContent>
              </Card>
              <Card 
                className="min-w-[100px] cursor-pointer hover:bg-teal-50 transition-colors"
                onClick={() => { setActiveTab("jobs"); setFilterStatus("paid"); }}
                data-testid="stat-paid"
              >
                <CardContent className="p-3 lg:pt-6"><div className="text-center"><p className="text-xs lg:text-sm text-muted-foreground">Paid Jobs <MetricInfo metricKey="jobs_paid" /></p><p className="text-lg lg:text-2xl font-bold text-teal-600">{metrics?.jobs_paid?.value ?? enquiries.filter(e => normalizeJobStatus(e.status) === 'paid').length}</p></div></CardContent>
              </Card>
            </div>
          </div>
          </>
          )}

          {/* AI Insights Panel - Dashboard Level - Collapsed by Default */}
          {activeTab === "jobs" && (
          <div className="mb-4 lg:mb-8">
            <AIInsightsPanel 
              authHeaders={authHeaders} 
              context={activeTab === "jobs" ? "jobs" : 
                       activeTab === "customers" ? "customers" :
                       activeTab === "calendar" ? "calendar" :
                       activeTab === "gilberts" ? "gilberts" :
                       activeTab === "quotes-invoices" ? "invoices" :
                       activeTab === "analytics" ? "analytics" : "dashboard"}
              collapsed={true}
            />
          </div>
          )}

          {/* Growth Opportunities Panel - Shows on Jobs (default dashboard view) */}
          {activeTab === "jobs" && (
            <div className="mb-4 lg:mb-8" data-testid="growth-opportunities-panel">
              <GrowthOpportunitiesPanel authHeaders={authHeaders} />
            </div>
          )}

          {/* Jobs Tab - Using JobsTab Component */}
          {activeTab === "jobs" && (
            <JobsTab
              enquiries={enquiries}
              customers={customers}
              allAdmins={allAdmins}
              isLoading={isLoading}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              onAddJob={() => setIsAddEnquiryOpen(true)}
              onViewJob={(enquiry) => openDetail(enquiry)}
              onEditJob={(enquiry) => openEditEnquiry(enquiry)}
              onScheduleJob={(enquiry) => bookSlotForEnquiry(enquiry)}
              onGenerateQuote={(enquiry) => handleQuickAction(enquiry, 'generate_quote')}
              onCreateInvoice={(enquiry) => handleQuickAction(enquiry, 'create_invoice')}
              onMarkComplete={(enquiryId) => handleStatusChange(enquiryId, 'completed')}
              onDeleteJob={(enquiryId) => handleDelete(enquiryId)}
              onStatusChange={handleStatusChange}
              onViewCustomer={(customer) => {
                setSelectedCustomer(customer);
                setActiveTab("customers");
              }}
              onRefresh={() => { fetchData(); fetchInvoices(); }}
            />
          )}

          {/* Quotes & Invoices Tab - Combines both */}
          {/* Quotes & Invoices Tab */}
          {activeTab === "quotes-invoices" && (
            <QuotesInvoicesTab 
              token={token}
              customers={customers}
              enquiries={enquiries}
              quotes={quotes}
              invoices={invoices}
              onInvoiceCreated={fetchInvoices}
              onQuoteCreated={fetchQuotes}
            />
          )}

          {/* Customers Tab */}
          {activeTab === "customers" && (
            <CustomersTab 
              customers={customers}
              onAddCustomer={() => setIsAddCustomerOpen(true)}
              onViewCustomer={openCustomerDetail}
              formatDate={formatDate}
              authHeaders={authHeaders}
              onRefresh={fetchData}
            />
          )}

          {/* Calendar Tab with Time Slots */}
          {activeTab === "calendar" && (
            <div className="space-y-6">
              {/* Main Calendar Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading">Calendar</CardTitle>
                      <Button size="sm" variant="outline" onClick={() => openBlockDayDialog(calendarDate)}><Ban className="h-4 w-4 mr-1" />Block</Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Manual Month Navigation */}
                    <div className="flex items-center justify-between mb-4">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <h3 className="font-medium">
                        {calendarMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                      </h3>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    <Calendar
                      mode="single"
                      selected={calendarDate}
                      onSelect={(date) => date && setCalendarDate(date)}
                      month={calendarMonth}
                      onMonthChange={setCalendarMonth}
                      className="rounded-md border w-full [&_table]:w-full [&_th]:w-full [&_td]:w-full [&_td_button]:w-full [&_td_button]:h-10"
                      classNames={{
                        caption: "hidden", // Hide default caption since we have custom nav
                      }}
                      modifiers={{
                        hasJob: customers.flatMap(c => (c.jobs || []).filter(j => j.status !== 'completed').map(j => new Date(j.date))),
                        blocked: blockedDays.filter(b => !b.time_slot).map(b => new Date(b.date)),
                        partiallyBooked: blockedDays.filter(b => b.time_slot).map(b => new Date(b.date))
                      }}
                      modifiersStyles={{
                        hasJob: { backgroundColor: 'hsl(var(--accent))', color: 'white', borderRadius: '50%' },
                        blocked: { backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '50%', textDecoration: 'line-through' },
                        partiallyBooked: { backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '50%' }
                      }}
                    />
                    {/* Color Legend */}
                    <div className="mt-4 pt-4 border-t space-y-3">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Legend</p>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-4 h-4 rounded-full bg-accent"></span>
                          <span>Jobs Scheduled</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-4 h-4 rounded-full bg-red-100 border border-red-300"></span>
                          <span>Day Blocked</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-4 h-4 rounded-full bg-amber-100 border border-amber-300"></span>
                          <span>Partially Booked</span>
                        </div>
                        {Object.entries(blockTypeConfig).slice(0, 3).map(([key, config]) => (
                          <div key={key} className="flex items-center gap-2">
                            <span className={`w-4 h-4 rounded ${config.color.split(' ')[0]}`}></span>
                            <span>{config.label}</span>
                          </div>
                        ))}
                      </div>
                      {/* Team member colors */}
                      {allAdmins.length > 0 && (
                        <>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider pt-2">Team</p>
                          <div className="flex flex-wrap gap-3">
                            {allAdmins.map(admin => (
                              <div key={admin.id} className="flex items-center gap-2">
                                <span 
                                  className="w-4 h-4 rounded-full" 
                                  style={{ backgroundColor: admin.color || TEAM_COLORS[0] }}
                                ></span>
                                <span className="text-sm">{admin.name || admin.email.split('@')[0]}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Drag indicator for calendar area */}
                {draggedJob && (
                  <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-amber-500 text-white px-4 py-2 rounded-full shadow-lg z-50 animate-bounce">
                    <span className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      Drag {draggedJob.name} to a date in the Week View
                    </span>
                  </div>
                )}

              {/* Day/Week Schedule Card */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading">
                      {calendarViewMode === 'day' 
                        ? `${formatDate(calendarDate?.toISOString())} Schedule`
                        : `Week of ${formatDate(calendarDate?.toISOString())}`
                      }
                    </CardTitle>
                    <div className="flex gap-2">
                      {/* View Mode Toggle */}
                      <div className="flex border rounded-lg overflow-hidden">
                        <Button 
                          size="sm" 
                          variant={calendarViewMode === 'day' ? 'default' : 'ghost'}
                          className="rounded-none"
                          onClick={() => setCalendarViewMode('day')}
                        >
                          Day
                        </Button>
                        <Button 
                          size="sm" 
                          variant={calendarViewMode === 'week' ? 'default' : 'ghost'}
                          className="rounded-none"
                          onClick={() => setCalendarViewMode('week')}
                        >
                          Week
                        </Button>
                      </div>
                      <Button size="sm" variant="outline" onClick={printDaySchedule}><Printer className="h-4 w-4 mr-1" />Print</Button>
                      <Button size="sm" variant="outline" onClick={exportWeekSchedule}><Download className="h-4 w-4 mr-1" />Export</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {calendarViewMode === 'week' ? (
                    /* Week View */
                    <div className="overflow-x-auto">
                      <div className="grid grid-cols-7 gap-2 min-w-[700px]">
                        {/* Week Header */}
                        {(() => {
                          const startOfWeek = new Date(calendarDate);
                          startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1); // Monday
                          const weekDays = [];
                          for (let i = 0; i < 7; i++) {
                            const day = new Date(startOfWeek);
                            day.setDate(day.getDate() + i);
                            weekDays.push(day);
                          }
                          
                          // Get all jobs for the week to detect multi-day jobs
                          const allWeekJobs = customers.flatMap(c => 
                            (c.jobs || []).map(j => ({ ...j, customer: c }))
                          );
                          
                          return weekDays.map((day, idx) => {
                            const dayStr = getLocalDateStr(day);
                            const isToday = dayStr === getLocalDateStr(new Date());
                            const isSelected = dayStr === getLocalDateStr(calendarDate);
                            const dayBlock = blockedDays.find(b => b.date === dayStr && !b.time_slot);
                            const dayJobs = allWeekJobs.filter(j => j.date === dayStr && j.status !== 'completed');
                            // Add Gilberts shifts as jobs
                            const gilbertsJobsForDay = gilbertsShifts
                              .filter(s => s.date === dayStr)
                              .map(s => ({
                                id: s.id,
                                title: 'Gilberts Contract',
                                job_type: 'gilberts',
                                time_slot: '10:00',
                                date: s.date,
                                duration: s.entries?.reduce((sum, e) => sum + (e.hours || 0), 0) / 2 || 8,
                                customer: { name: 'JR Gilberts Engineering' },
                                status: s.invoiced ? 'completed' : 'in_progress',
                                isGilberts: true
                              }));
                            const allDayJobs = [...dayJobs, ...gilbertsJobsForDay];
                            const daySlots = blockedDays.filter(b => b.date === dayStr && b.time_slot);
                            
                            return (
                              <div 
                                key={idx} 
                                className={`border rounded-lg p-2 min-h-[150px] cursor-pointer transition-all ${
                                  isSelected ? 'ring-2 ring-primary' : ''
                                } ${isToday ? 'bg-accent/20' : ''} ${dayBlock ? 'bg-red-50' : ''} ${
                                  dropTargetDate === dayStr ? 'ring-2 ring-amber-400 bg-amber-50' : ''
                                }`}
                                onClick={() => setCalendarDate(day)}
                                onDragOver={(e) => handleCalendarDragOver(e, day)}
                                onDragLeave={handleCalendarDragLeave}
                                onDrop={(e) => handleCalendarDrop(e, day)}
                                data-testid={`calendar-day-${dayStr}`}
                              >
                                <div className="text-center mb-2">
                                  <p className="text-xs text-muted-foreground">
                                    {day.toLocaleDateString('en-GB', { weekday: 'short' })}
                                  </p>
                                  <p className={`text-lg font-bold ${isToday ? 'text-primary' : ''}`}>
                                    {day.getDate()}
                                  </p>
                                  {dropTargetDate === dayStr && draggedJob && (
                                    <p className="text-xs text-amber-600 font-medium animate-pulse">Drop here</p>
                                  )}
                                </div>
                                {dayBlock ? (
                                  <div className="text-center">
                                    <Badge className="bg-red-100 text-red-800 text-xs">Blocked</Badge>
                                    <p className="text-xs text-red-600 mt-1 truncate">{dayBlock.reason}</p>
                                  </div>
                                ) : (
                                  <div className="space-y-1">
                                    {dayJobs.slice(0, 3).map((job, jIdx) => {
                                      // Check if this job is part of a multi-day series
                                      const multiDayInfo = getMultiDayJobInfo(job, allWeekJobs);
                                      
                                      return (
                                        <div 
                                          key={jIdx} 
                                          className={`text-xs rounded px-1 py-0.5 truncate relative ${job.isGilberts ? 'bg-indigo-200 text-indigo-800' : 'bg-accent/30'}`} 
                                          title={`${job.description || job.title || job.job_type}${multiDayInfo ? ` (Day ${multiDayInfo.dayNumber}/${multiDayInfo.totalDays})` : ''}`}
                                        >
                                          <div className="flex items-center gap-1">
                                            {multiDayInfo && (
                                              <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] bg-orange-500 text-white rounded-full font-bold flex-shrink-0" title={`Day ${multiDayInfo.dayNumber} of ${multiDayInfo.totalDays}`}>
                                                {multiDayInfo.dayNumber}
                                              </span>
                                            )}
                                            <span className="truncate">
                                              {job.time_slot && <span className="font-medium">{job.time_slot} </span>}
                                              {job.isGilberts ? 'Gilberts' : (job.title || job.job_type || job.description?.substring(0, 20))}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                    {gilbertsJobsForDay.length > 0 && dayJobs.length < 3 && gilbertsJobsForDay.slice(0, 3 - dayJobs.length).map((gJob, gIdx) => (
                                      <div key={`g${gIdx}`} className="text-xs bg-indigo-200 text-indigo-800 rounded px-1 py-0.5 truncate" title="Gilberts Contract">
                                        <span className="font-medium">10:00 </span>Gilberts
                                      </div>
                                    ))}
                                    {daySlots.slice(0, 2).map((slot, sIdx) => (
                                      <div key={`s${sIdx}`} className={`text-xs rounded px-1 py-0.5 truncate ${blockTypeConfig[slot.block_type]?.color || 'bg-gray-100'}`}>
                                        {slot.time_slot} {slot.reason}
                                      </div>
                                    ))}
                                    {(allDayJobs.length + daySlots.length) > 3 && (
                                      <p className="text-xs text-muted-foreground text-center">
                                        +{allDayJobs.length + daySlots.length - 3} more
                                      </p>
                                    )}
                                    {allDayJobs.length === 0 && daySlots.length === 0 && (
                                      <p className="text-xs text-muted-foreground text-center mt-4">No events</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  ) : (
                    /* Day View */
                    (() => {
                    const selectedDateStr = getLocalDateStr(calendarDate);
                    // Only treat as "whole day blocked" if time_slot is null/undefined
                    const wholeDayBlock = blockedDays.find(b => b.date === selectedDateStr && !b.time_slot);
                    const jobsForDate = getJobsForDateWithSlots();
                    
                    // Get all jobs to detect multi-day series
                    const allJobs = customers.flatMap(c => 
                      (c.jobs || []).map(j => ({ ...j, customer: c }))
                    );

                    if (wholeDayBlock) {
                      return (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Ban className="h-5 w-5 text-red-600" />
                              <div>
                                <p className="font-medium text-red-800">Day Blocked</p>
                                <p className="text-sm text-red-600">{wholeDayBlock.reason}</p>
                              </div>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => handleUnblockDay(wholeDayBlock.id)}>Unblock</Button>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-2">
                        {TIME_SLOTS.map((slot, slotIndex) => {
                          // Check for jobs that START at this slot
                          const jobsStartingAtSlot = jobsForDate.filter(j => j.time_slot === slot);
                          
                          // Check for jobs that SPAN into this slot (started earlier but duration covers this slot)
                          const jobsSpanningSlot = jobsForDate.filter(j => {
                            if (!j.time_slot || j.time_slot === slot) return false;
                            const jobStartIndex = TIME_SLOTS.indexOf(j.time_slot);
                            const jobDuration = j.duration || 1;
                            const jobEndIndex = jobStartIndex + jobDuration;
                            return slotIndex > jobStartIndex && slotIndex < jobEndIndex;
                          });
                          
                          // Check for blocked slots
                          const blockedSlot = blockedDays.find(b => b.date === selectedDateStr && b.time_slot === slot);
                          
                          // Check for blocked slots spanning this time
                          const blockedSpanning = blockedDays.find(b => {
                            if (!b.time_slot || b.date !== selectedDateStr) return false;
                            const blockedStartIndex = TIME_SLOTS.indexOf(b.time_slot);
                            const blockedDuration = b.duration || 1;
                            const blockedEndIndex = blockedStartIndex + blockedDuration;
                            return slotIndex > blockedStartIndex && slotIndex < blockedEndIndex;
                          });
                          
                          const allDayJobs = jobsForDate.filter(j => j.is_all_day);
                          
                          return (
                            <div 
                              key={slot} 
                              className={`flex items-start gap-3 py-2 border-b last:border-0 transition-colors ${
                                dropTargetDate === `${selectedDateStr}_${slot}` ? 'bg-amber-50' : ''
                              }`}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                                setDropTargetDate(`${selectedDateStr}_${slot}`);
                              }}
                              onDragLeave={(e) => {
                                if (!e.currentTarget.contains(e.relatedTarget)) {
                                  setDropTargetDate(null);
                                }
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                setDropTargetDate(null);
                                if (!draggedJob) return;
                                
                                const prefillReason = `${draggedJob.name} - ${serviceLabels[draggedJob.service] || draggedJob.service}`;
                                setNewBlockedDay({
                                  date: selectedDateStr,
                                  reason: prefillReason,
                                  block_type: "enquiry",
                                  time_slot: slot,
                                  duration: 2
                                });
                                setIsBlockDayOpen(true);
                                setDraggedJob(null);
                                toast.success(`Scheduling ${draggedJob.name} for ${formatDate(selectedDateStr)} at ${slot}`);
                              }}
                              data-testid={`time-slot-${slot}`}
                            >
                              <span className="w-14 text-sm font-medium text-muted-foreground">{slot}</span>
                              <div className="flex-1 min-h-[2rem]">
                                {dropTargetDate === `${selectedDateStr}_${slot}` && draggedJob && (
                                  <div className="text-xs text-amber-600 font-medium animate-pulse mb-1">
                                    Drop {draggedJob.name} here at {slot}
                                  </div>
                                )}
                                {slotIndex === 0 && allDayJobs.length > 0 && (
                                  <div className="mb-2 space-y-1">
                                    {allDayJobs.map((job, i) => (
                                      <div 
                                        key={`allday-${i}`} 
                                        className="p-2 rounded-lg text-sm bg-blue-100 border border-blue-200"
                                      >
                                        <p className="font-medium text-blue-800 flex items-center gap-2">
                                          <CalendarIcon className="h-4 w-4" />
                                          All Day: {job.description}
                                        </p>
                                        <p className="text-xs text-blue-600">{job.customer?.name} - {job.customer?.address}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {blockedSlot && !blockedSpanning ? (
                                  <div 
                                    className={`p-2 rounded-lg text-sm cursor-pointer hover:opacity-90 ${blockTypeConfig[blockedSlot.block_type]?.color || 'bg-red-100 text-red-800'}`}
                                    onClick={() => { setSelectedBlockedDay(blockedSlot); setIsBlockedDayDetailOpen(true); }}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="font-medium">{blockedSlot.reason || 'Blocked'}</p>
                                        <div className="flex items-center gap-2">
                                          <p className="text-xs opacity-75">{blockedSlot.duration || 1} hour(s)</p>
                                          {blockedSlot.assigned_admins?.length > 0 && (
                                            <div className="flex gap-1">
                                              {blockedSlot.assigned_admins.map(admin => (
                                                <Badge key={admin.id} variant="outline" className="text-xs py-0" style={{ borderColor: getAdminColor(admin.id) }}>
                                                  {admin.name?.split(' ')[0]}
                                                </Badge>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleUnblockDay(blockedSlot.id); }} className="h-7 px-2">
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ) : blockedSpanning ? (
                                  <div className="p-2 rounded-lg text-sm bg-gray-100 text-gray-500 italic">
                                    (continues...)
                                  </div>
                                ) : jobsStartingAtSlot.length > 0 ? (
                                  jobsStartingAtSlot.map((job, i) => {
                                    // Check if this is part of a multi-day job
                                    const multiDayInfo = !job.isGilberts ? getMultiDayJobInfo(job, allJobs) : null;
                                    
                                    return (
                                    <div 
                                      key={i} 
                                      className={`p-2 rounded-lg text-sm mb-1 border cursor-pointer hover:shadow-md transition-shadow ${
                                        job.isGilberts 
                                          ? 'bg-indigo-100 border-indigo-300' 
                                          : 'bg-accent/20 border-accent/30'
                                      }`}
                                      style={!job.isGilberts && job.assigned_admins?.length === 1 ? { borderLeftColor: getAdminColor(job.assigned_admins[0].id), borderLeftWidth: '4px' } : {}}
                                      onClick={() => {
                                        // Open job detail dialog when clicking on a calendar job
                                        if (!job.isGilberts && job.customer) {
                                          const linkedEnquiry = enquiries.find(e => e.customer_id === job.customer.id || e.id === job.enquiry_id);
                                          if (linkedEnquiry) {
                                            openDetail(linkedEnquiry);
                                          }
                                        }
                                      }}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            {multiDayInfo && (
                                              <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full font-bold" title={`Day ${multiDayInfo.dayNumber} of ${multiDayInfo.totalDays}-day job`}>
                                                Day {multiDayInfo.dayNumber}/{multiDayInfo.totalDays}
                                              </span>
                                            )}
                                            <p className="font-medium">{job.description}</p>
                                            {job.isGilberts && (
                                              <Badge className="bg-indigo-600 text-white text-xs py-0">Contract</Badge>
                                            )}
                                          </div>
                                          <p className="text-xs text-muted-foreground">
                                            {job.customer?.name} • {job.duration}h
                                            {job.isGilberts && job.total_amount && ` • £${job.total_amount.toFixed(2)}`}
                                            {job.isGilberts && (
                                              <span className={`ml-2 ${job.invoiced ? 'text-green-600' : 'text-amber-600'}`}>
                                                {job.invoiced ? '✓ Invoiced' : '⏳ Pending'}
                                              </span>
                                            )}
                                          </p>
                                          {/* Show address if available */}
                                          {job.customer?.address && (
                                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                              <MapPin className="h-3 w-3" />
                                              {job.customer.address.split(',')[0]}
                                            </p>
                                          )}
                                        </div>
                                        {job.assigned_admins?.length > 0 && (
                                          <div className="flex gap-1 flex-shrink-0">
                                            {job.assigned_admins.map(admin => (
                                              <Badge key={admin.id} variant="outline" className="text-xs" style={{ borderColor: getAdminColor(admin.id) }}>
                                                {admin.name}
                                              </Badge>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )})
                                ) : jobsSpanningSlot.length > 0 ? (
                                  <div className="p-2 rounded-lg text-sm bg-accent/10 text-muted-foreground italic">
                                    (continues...)
                                  </div>
                                ) : (
                                  <div className="text-sm text-muted-foreground/50 italic">Available</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()
                  )}
                </CardContent>
              </Card>
            </div>

              {/* Unscheduled Jobs Panel - Jobs awaiting scheduling */}
              <Card className="border-amber-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-500" />
                      Jobs Awaiting Scheduling
                      <Badge className="bg-amber-100 text-amber-800">{enquiries.filter(e => normalizeJobStatus(e.status) === 'accepted').length}</Badge>
                    </CardTitle>
                  </div>
                  <p className="text-sm text-muted-foreground">Drag jobs to calendar dates or click to schedule</p>
                </CardHeader>
                <CardContent>
                  {enquiries.filter(e => normalizeJobStatus(e.status) === 'accepted').length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">All accepted jobs have been scheduled</p>
                  ) : (
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {enquiries.filter(e => normalizeJobStatus(e.status) === 'accepted').slice(0, 6).map(job => (
                        <div 
                          key={job.id} 
                          className={`p-3 border rounded-lg hover:bg-muted/50 cursor-grab transition-all ${
                            draggedJob?.id === job.id ? 'opacity-50 scale-95' : ''
                          }`}
                          draggable
                          onDragStart={(e) => handleJobDragStart(e, job)}
                          onDragEnd={handleJobDragEnd}
                          onClick={() => bookSlotForEnquiry(job)}
                          data-testid={`draggable-job-${job.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-2">
                              <div className="mt-1 cursor-grab text-muted-foreground">
                                <GripVertical className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="font-medium">{job.name}</p>
                                <p className="text-sm text-muted-foreground">{getServiceLabel(job.service)}</p>
                                {job.preferred_date && (
                                  <p className="text-xs text-amber-600">Tentative: {formatDate(job.preferred_date)}</p>
                                )}
                                {job.estimated_value && (
                                  <p className="text-sm font-medium text-green-600">£{job.estimated_value.toLocaleString()}</p>
                                )}
                              </div>
                            </div>
                            <Button size="sm" variant="outline" className="gap-1 flex-shrink-0">
                              <CalendarIcon className="h-3 w-3" />
                              {job.preferred_date ? 'Confirm' : 'Schedule'}
                            </Button>
                          </div>
                          {job.message && (
                            <p className="text-xs text-muted-foreground mt-2 line-clamp-2 ml-6">{job.message}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {enquiries.filter(e => normalizeJobStatus(e.status) === 'accepted').length > 6 && (
                    <div className="text-center mt-4">
                      <Button variant="link" onClick={() => { setActiveTab("jobs"); setFilterStatus("accepted"); }}>
                        View all {enquiries.filter(e => normalizeJobStatus(e.status) === 'accepted').length} jobs awaiting scheduling
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recurring Contracts Card */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-heading flex items-center gap-2">
                        <Repeat className="h-5 w-5" />
                        Recurring Contracts
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">Automatically schedule repeating jobs</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={handleCheckAllConflicts}>
                        Check Conflicts
                      </Button>
                      <Button size="sm" onClick={() => setIsAddContractOpen(true)}>
                        <Plus className="h-4 w-4 mr-1" />New Contract
                      </Button>
                    </div>
                  </div>
                  
                  {/* Quick Stats Bar */}
                  {recurringContracts.length > 0 && (
                    <div className="flex gap-4 mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span className="text-muted-foreground">Active:</span>
                        <span className="font-medium">{recurringContracts.filter(c => c.active).length}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                        <span className="text-muted-foreground">Paused:</span>
                        <span className="font-medium">{recurringContracts.filter(c => !c.active).length}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-muted-foreground">This week:</span>
                        <span className="font-medium">
                          {recurringContracts.filter(c => c.active).reduce((sum, c) => sum + (c.days_of_week?.length || 0), 0)} slots
                        </span>
                      </div>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="pt-2">
                  {recurringContracts.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Repeat className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>No recurring contracts yet</p>
                      <p className="text-sm">Create one to auto-schedule repeating jobs</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {recurringContracts.map(contract => (
                        <div 
                          key={contract.id} 
                          className={`p-4 rounded-lg border transition-all hover:shadow-sm ${
                            contract.active 
                              ? 'bg-white border-l-4 border-l-green-500' 
                              : 'bg-gray-50/50 opacity-70 border-l-4 border-l-gray-300'
                          }`}
                          data-testid={`contract-card-${contract.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              {/* Header row */}
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-semibold truncate">{contract.name}</h4>
                                <Badge 
                                  variant={contract.active ? "default" : "secondary"}
                                  className={contract.active ? "bg-green-100 text-green-800" : ""}
                                >
                                  {contract.active ? "Active" : "Paused"}
                                </Badge>
                                {contract.service_type && (
                                  <Badge variant="outline" className="text-xs">
                                    {serviceLabels[contract.service_type] || contract.service_type}
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Customer info */}
                              {(contract.customer_name || contract.customer_address) && (
                                <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                                  <User className="h-3.5 w-3.5" />
                                  <span>{contract.customer_name}</span>
                                  {contract.customer_address && (
                                    <>
                                      <span className="text-gray-300">•</span>
                                      <MapPin className="h-3.5 w-3.5" />
                                      <span className="truncate">{contract.customer_address}</span>
                                    </>
                                  )}
                                </div>
                              )}
                              
                              {/* Schedule info row */}
                              <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2">
                                <div className="flex flex-wrap gap-1">
                                  {contract.days_of_week.map(day => (
                                    <Badge 
                                      key={day} 
                                      variant="outline" 
                                      className="text-xs px-1.5 py-0"
                                    >
                                      {DAYS_OF_WEEK.find(d => d.value === day)?.label.slice(0, 3)}
                                    </Badge>
                                  ))}
                                </div>
                                <span className="text-sm text-muted-foreground">
                                  {contract.start_time} - {contract.end_time}
                                </span>
                                <span className="text-sm text-muted-foreground">
                                  {contract.rolling_weeks} weeks ahead
                                </span>
                              </div>
                              
                              {/* Team members */}
                              {contract.assigned_admins?.length > 0 && (
                                <div className="flex items-center gap-1 mt-2">
                                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                  {contract.assigned_admins.map((admin) => (
                                    <Badge 
                                      key={admin.id || admin.email} 
                                      variant="outline" 
                                      className="text-xs"
                                      style={{ 
                                        borderColor: getAdminColor(admin.id), 
                                        color: getAdminColor(admin.id) 
                                      }}
                                    >
                                      {admin.name}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Action buttons - stack on mobile */}
                            <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openEditContract(contract)}
                                className="h-8"
                              >
                                <Edit3 className="h-3.5 w-3.5 sm:mr-1" />
                                <span className="hidden sm:inline">Edit</span>
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleGenerateContractSlots(contract.id)}
                                disabled={!contract.active}
                                className="h-8"
                              >
                                <RefreshCw className="h-3.5 w-3.5 sm:mr-1" />
                                <span className="hidden sm:inline">Generate</span>
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleToggleContractActive(contract)}>
                                    {contract.active ? "Pause Contract" : "Activate Contract"}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => handleDeleteContract(contract.id)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Contract
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Blocked Day Detail Dialog */}
            <Dialog open={isBlockedDayDetailOpen} onOpenChange={setIsBlockedDayDetailOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5" />
                    Booking Details
                  </DialogTitle>
                </DialogHeader>
                {selectedBlockedDay && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Date</p>
                        <p className="font-medium">{selectedBlockedDay.date}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Time</p>
                        <p className="font-medium">{selectedBlockedDay.time_slot || 'All Day'} ({selectedBlockedDay.duration || 1}h)</p>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm text-muted-foreground">Booking Type</p>
                      <Badge className={blockTypeConfig[selectedBlockedDay.block_type]?.color || 'bg-gray-100'}>
                        {blockTypeConfig[selectedBlockedDay.block_type]?.label || selectedBlockedDay.block_type}
                      </Badge>
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground">Reason/Description</p>
                      <p className="font-medium">{selectedBlockedDay.reason || 'No description'}</p>
                    </div>

                    {selectedBlockedDay.job_description && (
                      <div>
                        <p className="text-sm text-muted-foreground">Job Description</p>
                        <p className="text-sm bg-muted p-3 rounded-lg">{selectedBlockedDay.job_description}</p>
                      </div>
                    )}

                    {selectedBlockedDay.customer_name && (
                      <div className="border-t pt-4">
                        <p className="text-sm text-muted-foreground mb-2">Customer</p>
                        <div className="space-y-1">
                          <p className="font-medium">{selectedBlockedDay.customer_name}</p>
                          {selectedBlockedDay.customer_phone && (
                            <p className="text-sm"><a href={`tel:${selectedBlockedDay.customer_phone}`} className="text-primary hover:underline">{selectedBlockedDay.customer_phone}</a></p>
                          )}
                          {selectedBlockedDay.customer_address && (
                            <p className="text-sm text-muted-foreground">{selectedBlockedDay.customer_address}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {selectedBlockedDay.assigned_admins?.length > 0 && (
                      <div className="border-t pt-4">
                        <p className="text-sm text-muted-foreground mb-2">Assigned Team</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedBlockedDay.assigned_admins.map(admin => (
                            <Badge key={admin.id} variant="outline" style={{ borderColor: getAdminColor(admin.id) }}>
                              {admin.name || admin.email}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 pt-4 border-t">
                      <Button variant="outline" size="sm" onClick={() => setIsBlockedDayDetailOpen(false)} className="flex-1">
                        Close
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => { handleUnblockDay(selectedBlockedDay.id); setIsBlockedDayDetailOpen(false); }} className="flex-1">
                        Remove Booking
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

          {/* Portfolio/Lightbox Tab */}
          {activeTab === "portfolio" && (
            <PortfolioTab 
              token={token}
              customers={customers}
              onViewCustomer={(customer) => {
                setSelectedCustomer(customer);
                setIsCustomerDetailOpen(true);
              }}
              onGoToCustomers={() => setActiveTab("customers")}
              onPhotoSelect={(photo) => setSelectedPortfolioPhoto(photo)}
            />
          )}

          {/* Analytics Tab - Enhanced */}

          {/* Analytics Tab */}
          {activeTab === "analytics" && (
            <AnalyticsTab authHeaders={authHeaders} />
          )}

          {/* Feedback Tab */}
          {activeTab === "feedback" && (
            <FeedbackTab 
              token={token}
              onCreateReview={() => setIsCreateReviewOpen(true)}
              onEditReview={(fb) => {
                setEditingFeedback(fb);
                setIsEditFeedbackOpen(true);
              }}
            />
          )}

          {/* AI Assistant Tab */}
          {activeTab === "ai-assistant" && (
            <AIAssistantTab 
              token={token}
              customers={customers}
              onCreateQuote={(quoteData) => {
                setNewQuote(prev => ({
                  ...prev,
                  ...quoteData
                }));
                setActiveTab('quotes-invoices');
                setIsCreateQuoteOpen(true);
              }}
            />
          )}
          {/* Gilberts Tab */}
          {activeTab === "gilberts" && (
            <GilbertsTab 
              token={token} 
              onShiftsChange={(shifts) => setGilbertsShifts(shifts)}
              onInvoiceCreated={fetchInvoices}
            />
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <SettingsTab 
              token={token}
              onProfileUpdate={fetchAdminProfile}
            />
          )}

          {/* Site Mode Tab */}
          {activeTab === "site-mode" && (
            <SiteModeTab authHeaders={authHeaders} />
          )}

          {/* Materials Tab */}
          {activeTab === "materials" && (
            <MaterialsTab
              authHeaders={authHeaders}
              enquiries={enquiries}
              onOpenJob={(job) => { setSelectedEnquiry(job); setIsDetailOpen(true); }}
            />
          )}

          {/* Site Editor Tab */}
          {activeTab === "site-editor" && (
            <SiteEditorTab authHeaders={authHeaders} />
          )}
        </div>
      </main>

      {/* Portfolio Photo Detail Dialog */}
      <Dialog open={!!selectedPortfolioPhoto} onOpenChange={(open) => !open && setSelectedPortfolioPhoto(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="edit-portfolio-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Edit Portfolio Photo</DialogTitle>
            <DialogDescription className="text-sm">
              Update photo details or remove from portfolio
            </DialogDescription>
          </DialogHeader>
          {selectedPortfolioPhoto && (
            <div className="space-y-4">
              {/* Photo Preview — smaller on mobile */}
              <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                <img 
                  src={getImageUrl(selectedPortfolioPhoto.url)} 
                  alt={selectedPortfolioPhoto.caption || "Portfolio photo"}
                  className="w-full h-full object-contain"
                />
                {selectedPortfolioPhoto.photo_type && selectedPortfolioPhoto.photo_type !== 'general' && (
                  <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-bold ${selectedPortfolioPhoto.photo_type === 'before' ? 'bg-orange-500 text-white' : 'bg-green-500 text-white'}`}>
                    {selectedPortfolioPhoto.photo_type.toUpperCase()}
                  </div>
                )}
              </div>

              {/* Photo Info — compact */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-muted rounded-lg text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Customer</p>
                  <p className="font-medium">{selectedPortfolioPhoto.customerName || 'Direct Upload'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Job</p>
                  <p className="font-medium">{selectedPortfolioPhoto.jobDescription || '—'}</p>
                </div>
              </div>

              {/* Caption — with save button */}
              <div>
                <Label className="text-sm">Caption</Label>
                <div className="flex gap-2 mt-1">
                  <Input 
                    placeholder="Add a caption..."
                    defaultValue={selectedPortfolioPhoto.caption || ''}
                    id="portfolio-caption-input"
                    data-testid="portfolio-caption-input"
                  />
                  <Button 
                    size="sm" 
                    variant="outline"
                    data-testid="save-caption-btn"
                    onClick={() => {
                      const val = document.getElementById('portfolio-caption-input')?.value;
                      if (val !== undefined) {
                        handleUpdatePortfolioPhoto(selectedPortfolioPhoto, { caption: val });
                      }
                    }}
                  >
                    Save
                  </Button>
                </div>
              </div>

              {/* Photo Type & Category — side by side on mobile */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Photo Type</Label>
                  <Select 
                    value={selectedPortfolioPhoto.photo_type || 'general'} 
                    onValueChange={(type) => handleUpdatePortfolioPhoto(selectedPortfolioPhoto, { photo_type: type })}
                  >
                    <SelectTrigger className="mt-1" data-testid="photo-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="before">Before</SelectItem>
                      <SelectItem value="after">After</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Category</Label>
                  <Select 
                    value={selectedPortfolioPhoto.category || 'general'} 
                    onValueChange={(category) => handleUpdatePortfolioPhoto(selectedPortfolioPhoto, { category: category })}
                  >
                    <SelectTrigger className="mt-1" data-testid="photo-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="carpentry">Carpentry</SelectItem>
                      <SelectItem value="general-repairs">General Repairs</SelectItem>
                      <SelectItem value="furniture-builds">Furniture Builds</SelectItem>
                      <SelectItem value="garden-storage">Garden Storage</SelectItem>
                      <SelectItem value="garden-buildings">Garden Buildings</SelectItem>
                      <SelectItem value="fencing-gates">Fencing & Gates</SelectItem>
                      <SelectItem value="cnc-factory">CNC/Factory Work</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Actions — stacked on mobile */}
              <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t">
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => {
                    handleRemoveFromPortfolio(selectedPortfolioPhoto);
                    setSelectedPortfolioPhoto(null);
                  }}
                  className="sm:flex-1"
                  data-testid="remove-portfolio-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove from Portfolio
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedPortfolioPhoto(null)}
                  className="sm:flex-1"
                  data-testid="close-portfolio-dialog"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Job Detail Dialog - Unified for all stages */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <DialogTitle className="font-heading">Job Details</DialogTitle>
              {/* Edit/Delete buttons prominently placed in header */}
              <div className="flex gap-2 ml-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => selectedEnquiry && openEditEnquiry(selectedEnquiry)}
                  data-testid="edit-enquiry-btn"
                >
                  <Edit3 className="h-4 w-4" />
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => selectedEnquiry && handleDelete(selectedEnquiry.id)}
                  data-testid="delete-enquiry-btn"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          {selectedEnquiry && (
            <ErrorBoundary label="job details">
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="min-w-0"><p className="text-sm text-muted-foreground">Name</p><p className="font-medium break-words">{selectedEnquiry.name}</p></div>
                <div className="min-w-0"><p className="text-sm text-muted-foreground">Email</p><p className="font-medium break-all">{selectedEnquiry.email}</p></div>
                <div className="min-w-0"><p className="text-sm text-muted-foreground">Phone</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium break-words">{selectedEnquiry.phone}</p>
                    <WhatsAppButton
                      phone={selectedEnquiry.phone}
                      name={selectedEnquiry.name}
                      service={getServiceLabel(selectedEnquiry.service)}
                    />
                  </div>
                </div>
                <div className="min-w-0"><p className="text-sm text-muted-foreground">Service</p><p className="font-medium break-words">{getServiceLabel(selectedEnquiry.service)}</p></div>
              </div>
              
              {/* Job Details */}
              <div><p className="text-sm text-muted-foreground">Details</p><div className="bg-muted p-3 rounded-lg mt-1">{selectedEnquiry.message}</div></div>
              
              {/* Project Command Center — quotes, invoices, materials, profitability */}
              <ProjectCommandCenter
                authHeaders={authHeaders}
                jobId={selectedEnquiry.id}
                jobStatus={selectedEnquiry.status}
                onNavigate={(type, recordId) => {
                  if (type === 'quote' || type === 'invoice') {
                    setActiveTab('quotes-invoices');
                    setIsDetailOpen(false);
                  }
                }}
                onInvoiceGenerated={() => { fetchInvoices(); fetchData(); }}
              />
              
              {/* Materials (per-job) */}
              <JobMaterialsPanel authHeaders={authHeaders} jobId={selectedEnquiry.id} />
              
              {/* Source Badge */}
              {selectedEnquiry.source && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Source:</span>
                  <Badge variant="outline" className="capitalize">{SOURCE_LABELS[selectedEnquiry.source]?.label || selectedEnquiry.source}</Badge>
                </div>
              )}
              
              {/* Linked Customer */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-sm font-medium flex items-center gap-1.5">
                    <User className="h-4 w-4" />
                    Linked Customer
                  </Label>
                  {selectedEnquiry.customer_id && (() => {
                    const linkedCust = customers.find(c => c.id === selectedEnquiry.customer_id);
                    return linkedCust ? (
                      <Button variant="link" size="sm" className="gap-1 p-0 h-auto text-xs" onClick={() => { setIsDetailOpen(false); openCustomerDetail(linkedCust); }} data-testid="view-linked-customer-btn">
                        <ExternalLink className="h-3 w-3" />
                        View Profile
                      </Button>
                    ) : null;
                  })()}
                </div>
                <Select 
                  value={selectedEnquiry.customer_id || "none"}
                  onValueChange={(v) => {
                    const customerId = v === "none" ? null : v;
                    handleQuickFieldUpdate(selectedEnquiry.id, 'customer_id', customerId);
                  }}
                >
                  <SelectTrigger data-testid="job-detail-customer-select">
                    <SelectValue placeholder="Select customer..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No customer linked</SelectItem>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} - {c.phone || c.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Master Record Info - Priority, Tags, Assignment */}
              <div className="border-t pt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Priority */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Priority</p>
                    <Select 
                      value={selectedEnquiry.priority || 'normal'} 
                      onValueChange={(v) => handleQuickFieldUpdate(selectedEnquiry.id, 'priority', v)}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Assigned To - Multi-select with All Team option */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Assigned To</p>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="h-8 w-full justify-between text-left font-normal" data-testid="assigned-to-dropdown">
                          <span className="truncate">
                            {selectedEnquiry.all_team_assigned 
                              ? 'All Team' 
                              : selectedEnquiry.assigned_admins?.length > 0
                                ? selectedEnquiry.assigned_admins.map(email => allAdmins.find(a => a.email === email)?.name || email).join(', ')
                                : selectedEnquiry.assigned_admin 
                                  ? allAdmins.find(a => a.email === selectedEnquiry.assigned_admin)?.name || selectedEnquiry.assigned_admin
                                  : 'Unassigned'
                            }
                          </span>
                          <ChevronDown className="h-4 w-4 opacity-50" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-56" align="start">
                        {/* All Team Option */}
                        <DropdownMenuItem 
                          onClick={() => {
                            handleQuickFieldUpdate(selectedEnquiry.id, 'all_team_assigned', !selectedEnquiry.all_team_assigned);
                            if (!selectedEnquiry.all_team_assigned) {
                              handleQuickFieldUpdate(selectedEnquiry.id, 'assigned_admins', allAdmins.map(a => a.email));
                            }
                          }}
                          className="flex items-center gap-2"
                          data-testid="assign-all-team"
                        >
                          <div className={`w-4 h-4 border rounded flex items-center justify-center ${selectedEnquiry.all_team_assigned ? 'bg-primary border-primary' : 'border-gray-300'}`}>
                            {selectedEnquiry.all_team_assigned && <CheckCircle className="h-3 w-3 text-white" />}
                          </div>
                          <Users className="h-4 w-4" />
                          <span className="font-medium">All Team Members</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {/* Individual Team Members */}
                        {allAdmins.map(admin => {
                          const isAssigned = selectedEnquiry.all_team_assigned || 
                            selectedEnquiry.assigned_admins?.includes(admin.email) ||
                            selectedEnquiry.assigned_admin === admin.email;
                          return (
                            <DropdownMenuItem 
                              key={admin.email}
                              onClick={(e) => {
                                e.preventDefault();
                                const currentAdmins = selectedEnquiry.assigned_admins || 
                                  (selectedEnquiry.assigned_admin ? [selectedEnquiry.assigned_admin] : []);
                                let newAdmins;
                                if (isAssigned && !selectedEnquiry.all_team_assigned) {
                                  newAdmins = currentAdmins.filter(email => email !== admin.email);
                                } else {
                                  newAdmins = [...new Set([...currentAdmins, admin.email])];
                                }
                                // Clear all_team_assigned if manually selecting
                                handleQuickFieldUpdate(selectedEnquiry.id, 'all_team_assigned', false);
                                handleQuickFieldUpdate(selectedEnquiry.id, 'assigned_admins', newAdmins);
                              }}
                              className="flex items-center gap-2"
                              data-testid={`assign-${admin.email}`}
                            >
                              <div className={`w-4 h-4 border rounded flex items-center justify-center ${isAssigned ? 'bg-primary border-primary' : 'border-gray-300'}`}>
                                {isAssigned && <CheckCircle className="h-3 w-3 text-white" />}
                              </div>
                              <div 
                                className="w-3 h-3 rounded-full flex-shrink-0" 
                                style={{ backgroundColor: getAdminColor(admin.id) }}
                              />
                              <span>{admin.name}</span>
                            </DropdownMenuItem>
                          );
                        })}
                        <DropdownMenuSeparator />
                        {/* Clear Assignment */}
                        <DropdownMenuItem 
                          onClick={() => {
                            handleQuickFieldUpdate(selectedEnquiry.id, 'all_team_assigned', false);
                            handleQuickFieldUpdate(selectedEnquiry.id, 'assigned_admins', []);
                            handleQuickFieldUpdate(selectedEnquiry.id, 'assigned_admin', null);
                          }}
                          className="text-muted-foreground"
                          data-testid="clear-assignment"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Clear Assignment
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  {/* Estimated Value */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Est. Value</p>
                    <div className="flex items-center gap-1">
                      <span className="text-sm">£</span>
                      <Input 
                        type="number" 
                        className="h-8" 
                        placeholder="0.00"
                        value={selectedEnquiry.estimated_value || ''} 
                        onChange={(e) => handleQuickFieldUpdate(selectedEnquiry.id, 'estimated_value', e.target.value ? parseFloat(e.target.value) : null)}
                      />
                    </div>
                  </div>
                  
                  {/* Created Date */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Created</p>
                    <p className="text-sm font-medium">{formatDate(selectedEnquiry.created_at)}</p>
                  </div>
                </div>
                
                {/* Tags */}
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground mb-2">Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(JOB_TAGS).map(([tagId, tagInfo]) => {
                      const isActive = selectedEnquiry.tags?.includes(tagId);
                      return (
                        <Badge 
                          key={tagId}
                          variant={isActive ? "default" : "outline"}
                          className={`cursor-pointer transition-colors ${isActive ? tagInfo.bgColor : 'hover:bg-muted'}`}
                          onClick={() => {
                            const newTags = isActive 
                              ? (selectedEnquiry.tags || []).filter(t => t !== tagId)
                              : [...(selectedEnquiry.tags || []), tagId];
                            handleQuickFieldUpdate(selectedEnquiry.id, 'tags', newTags);
                          }}
                        >
                          {tagInfo.label}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
                
                {/* Next Action - Actionable prompt */}
                {getNextAction(selectedEnquiry.status) && !selectedEnquiry.next_action_dismissed && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <p className="text-sm font-medium text-amber-800">
                        Next: {getNextAction(selectedEnquiry.status).action}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="bg-amber-100 hover:bg-amber-200 border-amber-300"
                        onClick={() => {
                          const status = normalizeJobStatus(selectedEnquiry.status);
                          // Execute the suggested action
                          if (status === 'new' || status === 'site_visit') {
                            handleQuickAction(selectedEnquiry, 'generate_quote');
                          } else if (status === 'quoted') {
                            // Follow up - could open email/WhatsApp
                            toast.info("Follow up with customer about the quote");
                          } else if (status === 'accepted') {
                            bookSlotForEnquiry(selectedEnquiry);
                          } else if (status === 'scheduled') {
                            handleStatusChange(selectedEnquiry.id, 'in_progress');
                          } else if (status === 'in_progress') {
                            handleStatusChange(selectedEnquiry.id, 'completed');
                          } else if (status === 'completed') {
                            handleQuickAction(selectedEnquiry, 'create_invoice');
                          } else if (status === 'invoiced') {
                            toast.info("Send payment reminder to customer");
                          } else if (status === 'paid') {
                            handleQuickAction(selectedEnquiry, 'request_review');
                          }
                          setIsDetailOpen(false);
                        }}
                      >
                        Do This
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-amber-600 hover:text-amber-800 hover:bg-amber-100"
                        data-testid="dismiss-next-action"
                        onClick={async () => {
                          try {
                            await axios.patch(`${API}/admin/enquiries/${selectedEnquiry.id}`, 
                              { next_action_dismissed: true }, authHeaders);
                            setSelectedEnquiry(prev => ({ ...prev, next_action_dismissed: true }));
                            toast.success("Recommendation dismissed");
                          } catch (err) { toast.error("Failed to dismiss"); }
                        }}
                        title="Dismiss this recommendation"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Customer Uploaded Photos */}
              {selectedEnquiry.photos?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Photos ({selectedEnquiry.photos.length})</p>
                  <div className="grid grid-cols-4 gap-2">
                    {selectedEnquiry.photos.map((photo, idx) => (
                      <a 
                        key={photo} 
                        href={getImageUrl(photo)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="aspect-square rounded-lg overflow-hidden border hover:border-primary transition-colors"
                      >
                        <img 
                          src={getImageUrl(photo)} 
                          alt={`Photo ${idx + 1}`} 
                          className="w-full h-full object-cover"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Stage Selection - Using Centralized Pipeline Stages */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Move to Stage</p>
                <div className="flex flex-wrap gap-2">
                  {PIPELINE_STAGES.map(stage => (
                    <Button
                      key={stage.id}
                      variant="outline"
                      size="sm"
                      className={`${selectedEnquiry.status === stage.id || normalizeJobStatus(selectedEnquiry.status) === stage.id ? JOB_STATUSES[stage.id]?.bgColor + ' border-2 border-current' : ''}`}
                      onClick={() => handleStatusChange(selectedEnquiry.id, stage.id)}
                    >
                      {stage.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Quick Actions - Context-aware based on job status */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Quick Actions</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {/* Always available actions */}
                  <Button onClick={() => bookSlotForEnquiry(selectedEnquiry)} variant="outline" size="sm" className="gap-1 justify-start">
                    <CalendarIcon className="h-4 w-4" />Schedule
                  </Button>
                  <Button onClick={() => openEditEnquiry(selectedEnquiry)} variant="outline" size="sm" className="gap-1 justify-start">
                    <Edit3 className="h-4 w-4" />Edit Job
                  </Button>
                  
                  {/* Quote actions - show if not yet quoted */}
                  {!['quoted', 'accepted', 'scheduled', 'in_progress', 'completed', 'invoiced', 'paid'].includes(normalizeJobStatus(selectedEnquiry.status)) && (
                    <Button 
                      onClick={() => handleQuickAction(selectedEnquiry, 'generate_quote')} 
                      variant="outline" 
                      size="sm" 
                      className="gap-1 justify-start"
                    >
                      <FileText className="h-4 w-4" />Generate Quote
                    </Button>
                  )}
                  
                  {/* Invoice actions - show if completed or later */}
                  {['completed', 'invoiced'].includes(normalizeJobStatus(selectedEnquiry.status)) && (
                    <Button 
                      onClick={() => handleQuickAction(selectedEnquiry, 'create_invoice')} 
                      variant="outline" 
                      size="sm" 
                      className="gap-1 justify-start"
                    >
                      <PoundSterling className="h-4 w-4" />Create Invoice
                    </Button>
                  )}

                  {/* Publish to Gallery - show if completed or later */}
                  {['completed', 'invoiced', 'paid'].includes(normalizeJobStatus(selectedEnquiry.status)) && (
                    <Button
                      onClick={() => handlePublishToGallery(selectedEnquiry)}
                      disabled={publishingToGallery}
                      variant="outline"
                      size="sm"
                      className="gap-1 justify-start bg-purple-50 hover:bg-purple-100"
                      data-testid="publish-to-gallery-btn"
                    >
                      {publishingToGallery ? <Loader2 className="h-4 w-4 text-purple-600 animate-spin" /> : <Image className="h-4 w-4 text-purple-600" />}
                      {publishingToGallery ? "Publishing…" : "Publish to Gallery"}
                    </Button>
                  )}
                  
                  {/* Mark complete - show if in progress */}
                  {['scheduled', 'in_progress'].includes(normalizeJobStatus(selectedEnquiry.status)) && (
                    <Button 
                      onClick={() => handleStatusChange(selectedEnquiry.id, 'completed')} 
                      variant="outline" 
                      size="sm" 
                      className="gap-1 justify-start bg-green-50 hover:bg-green-100"
                    >
                      <CheckCircle className="h-4 w-4 text-green-600" />Mark Complete
                    </Button>
                  )}
                  
                  {/* Review request - show if paid */}
                  {normalizeJobStatus(selectedEnquiry.status) === 'paid' && selectedEnquiry.review_status !== 'requested' && (
                    <Button 
                      onClick={() => handleQuickAction(selectedEnquiry, 'request_review')} 
                      variant="outline" 
                      size="sm" 
                      className="gap-1 justify-start"
                    >
                      <Star className="h-4 w-4" />Request Review
                    </Button>
                  )}
                  
                  {/* Add to portfolio - show if completed */}
                  {['completed', 'invoiced', 'paid'].includes(normalizeJobStatus(selectedEnquiry.status)) && (
                    <Button 
                      onClick={() => handleQuickAction(selectedEnquiry, 'add_to_portfolio')} 
                      variant="outline" 
                      size="sm" 
                      className="gap-1 justify-start"
                    >
                      <Image className="h-4 w-4" />Add to Portfolio
                    </Button>
                  )}
                  
                  {/* Convert to customer - show if not already linked */}
                  {!selectedEnquiry.customer_id && (
                    <Button onClick={() => handleConvertToCustomer(selectedEnquiry.id)} variant="outline" size="sm" className="gap-1 justify-start">
                      <UserPlus className="h-4 w-4" />Add to Customers
                    </Button>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="border-t pt-4">
                <Label>Notes</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" placeholder="Add notes about this job..." />
                <Button onClick={handleSaveNotes} size="sm" className="mt-2" disabled={isSavingNotes}>
                  {isSavingNotes ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Save Notes
                </Button>
              </div>
              
              {/* AI Assist Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-purple-500" />
                    AI Assist
                  </p>
                  {aiResult && (
                    <Button variant="ghost" size="sm" onClick={clearAIResult}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                
                {!aiResult ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      onClick={() => generateAIContent('quote', selectedEnquiry)} 
                      variant="outline" 
                      size="sm" 
                      className="gap-1 justify-start"
                      disabled={aiGenerating}
                      data-testid="ai-draft-quote-btn"
                    >
                      {aiGenerating && aiResultType === 'quote' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4 text-purple-500" />}
                      Draft Quote
                    </Button>
                    <Button 
                      onClick={() => generateAIContent('description', selectedEnquiry)} 
                      variant="outline" 
                      size="sm" 
                      className="gap-1 justify-start"
                      disabled={aiGenerating}
                      data-testid="ai-job-description-btn"
                    >
                      {aiGenerating && aiResultType === 'description' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-purple-500" />}
                      Job Description
                    </Button>
                    <Button 
                      onClick={() => generateAIContent('reply', selectedEnquiry)} 
                      variant="outline" 
                      size="sm" 
                      className="gap-1 justify-start"
                      disabled={aiGenerating}
                      data-testid="ai-suggest-reply-btn"
                    >
                      {aiGenerating && aiResultType === 'reply' ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4 text-purple-500" />}
                      Suggest Reply
                    </Button>
                    {['completed', 'invoiced', 'paid'].includes(normalizeJobStatus(selectedEnquiry.status)) && (
                      <Button 
                        onClick={() => generateAIContent('review_request', selectedEnquiry)} 
                        variant="outline" 
                        size="sm" 
                        className="gap-1 justify-start"
                        disabled={aiGenerating}
                        data-testid="ai-review-request-btn"
                      >
                        {aiGenerating && aiResultType === 'review_request' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4 text-purple-500" />}
                        Review Request
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary" className="capitalize">{aiResultType?.replace('_', ' ')}</Badge>
                      <span className="text-xs text-muted-foreground">AI Generated</span>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                      <pre className="text-sm whitespace-pre-wrap font-sans">
                        {typeof aiResult === 'string' ? aiResult : JSON.stringify(aiResult, null, 2)}
                      </pre>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={copyAIResult} size="sm" variant="outline" className="gap-1">
                        <Copy className="h-3 w-3" /> Copy
                      </Button>
                      <Button onClick={() => generateAIContent(aiResultType, selectedEnquiry)} size="sm" variant="outline" className="gap-1" disabled={aiGenerating}>
                        {aiGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Regenerate
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </ErrorBoundary>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={isAddCustomerOpen} onOpenChange={setIsAddCustomerOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Add Customer</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={newCustomer.name} onChange={(e) => setNewCustomer(p => ({ ...p, name: e.target.value }))} /></div>
            <div><Label>Address *</Label><Textarea value={newCustomer.address} onChange={(e) => setNewCustomer(p => ({ ...p, address: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Email</Label><Input value={newCustomer.email} onChange={(e) => setNewCustomer(p => ({ ...p, email: e.target.value }))} /></div>
              <div><Label>Phone</Label><Input value={newCustomer.phone} onChange={(e) => setNewCustomer(p => ({ ...p, phone: e.target.value }))} /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={newCustomer.notes} onChange={(e) => setNewCustomer(p => ({ ...p, notes: e.target.value }))} /></div>
            <Button onClick={handleAddCustomer} className="w-full bg-primary">Add Customer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Job Dialog - Manual Creation */}
      <Dialog open={isAddEnquiryOpen} onOpenChange={setIsAddEnquiryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">New Job</DialogTitle>
            <DialogDescription>Add a new job from any source</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Customer Selection */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <Label className="text-sm font-medium">Select Existing Customer (optional)</Label>
              {newEnquiry.customer_id ? (
                <div className="flex items-center justify-between mt-1 p-2 bg-primary/10 rounded-md border border-primary/20">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">{newEnquiry.name}</span>
                    <Badge variant="secondary" className="text-xs">Linked</Badge>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 px-2"
                    onClick={() => setNewEnquiry(p => ({ ...p, customer_id: '' }))}
                    data-testid="unlink-customer-btn"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <Select onValueChange={(v) => {
                  const customer = customers.find(c => c.id === v);
                  if (customer) {
                    const customerEmail = customer.email && customer.email !== 'N/A' && customer.email.includes('@') 
                      ? customer.email 
                      : '';
                    setNewEnquiry(p => ({
                      ...p,
                      name: customer.name,
                      email: customerEmail,
                      phone: customer.phone || '',
                      customer_id: customer.id
                    }));
                  }
                }}>
                  <SelectTrigger className="mt-1" data-testid="new-job-customer-select"><SelectValue placeholder="Select customer or enter new details below..." /></SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} - {c.phone || c.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input value={newEnquiry.name} onChange={(e) => setNewEnquiry(p => ({ ...p, name: e.target.value }))} placeholder="Customer name" /></div>
              <div><Label>Phone *</Label><Input value={newEnquiry.phone} onChange={(e) => setNewEnquiry(p => ({ ...p, phone: e.target.value }))} placeholder="Phone number" /></div>
            </div>
            <div><Label>Email *</Label><Input type="email" value={newEnquiry.email} onChange={(e) => setNewEnquiry(p => ({ ...p, email: e.target.value }))} placeholder="Email address" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Service Type</Label>
                <Select value={newEnquiry.service} onValueChange={(v) => setNewEnquiry(p => ({ ...p, service: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carpentry">Carpentry</SelectItem>
                    <SelectItem value="general-repairs">General Repairs</SelectItem>
                    <SelectItem value="furniture-builds">Furniture Builds</SelectItem>
                    <SelectItem value="cnc-factory">CNC/Factory Work</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Source</Label>
                <Select value={newEnquiry.source} onValueChange={(v) => setNewEnquiry(p => ({ ...p, source: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="phone">Phone Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Message / Details *</Label><Textarea value={newEnquiry.message} onChange={(e) => setNewEnquiry(p => ({ ...p, message: e.target.value }))} placeholder="What do they need help with?" rows={3} /></div>
            <div><Label>Preferred Date</Label><Input type="date" value={newEnquiry.preferred_date} onChange={(e) => setNewEnquiry(p => ({ ...p, preferred_date: e.target.value }))} /></div>
            <Button onClick={handleAddEnquiry} className="w-full bg-primary">Add Job</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Detail Dialog */}
      <Dialog open={isCustomerDetailOpen} onOpenChange={setIsCustomerDetailOpen}>
        <DialogContent className="max-w-3xl mx-4 max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Customer Details</DialogTitle></DialogHeader>
          {selectedCustomer && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={selectedCustomer.name} onChange={(e) => setSelectedCustomer(p => ({ ...p, name: e.target.value }))} className="h-11" /></div>
                <div><Label>Phone</Label><Input value={selectedCustomer.phone || ""} onChange={(e) => setSelectedCustomer(p => ({ ...p, phone: e.target.value }))} className="h-11" /></div>
                <div><Label>Email</Label><Input value={selectedCustomer.email || ""} onChange={(e) => setSelectedCustomer(p => ({ ...p, email: e.target.value }))} className="h-11" /></div>
                <div><Label>Address</Label><Input value={selectedCustomer.address} onChange={(e) => setSelectedCustomer(p => ({ ...p, address: e.target.value }))} className="h-11" /></div>
              </div>
              <div><Label>Notes</Label><Textarea value={selectedCustomer.notes || ""} onChange={(e) => setSelectedCustomer(p => ({ ...p, notes: e.target.value }))} /></div>
              
              {/* Customer Timeline — chronological journey */}
              <CustomerTimeline
                authHeaders={authHeaders}
                customerId={selectedCustomer.id}
                onNavigate={(type, recordId) => {
                  if (type === 'quote' || type === 'invoice' || type === 'payment') {
                    setActiveTab('quotes-invoices');
                    setIsCustomerDetailOpen(false);
                  } else if (type === 'enquiry') {
                    const enq = enquiries.find(e => e.id === recordId);
                    if (enq) {
                      setSelectedEnquiry(enq);
                      setIsCustomerDetailOpen(false);
                      setIsDetailOpen(true);
                    }
                  }
                }}
              />
              
              {/* Portal Access Section */}
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Customer Portal Access</p>
                    <p className="text-xs text-muted-foreground">
                      {selectedCustomer.portal_enabled 
                        ? "✓ Portal access enabled - customer can view quotes/invoices at /portal" 
                        : selectedCustomer.portal_invite_sent
                        ? "⏳ Invite sent - waiting for customer to set password"
                        : "Portal access not enabled"}
                    </p>
                  </div>
                  {selectedCustomer.email ? (
                    selectedCustomer.portal_enabled ? (
                      <Button variant="outline" size="sm" onClick={() => handleDisablePortalAccess(selectedCustomer.id)}>
                        Disable Access
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={handleSendPortalInvite}>
                        {selectedCustomer.portal_invite_sent ? "Resend Invite" : "Send Portal Invite"}
                      </Button>
                    )
                  ) : (
                    <span className="text-xs text-muted-foreground">Add email first</span>
                  )}
                </div>
              </div>
              
              {/* Payment Summary */}
              {(() => {
                const customerInvoices = invoices.filter(inv => 
                  inv.customer_name?.toLowerCase() === selectedCustomer.name?.toLowerCase()
                );
                const totalInvoiced = customerInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
                const totalPaid = customerInvoices
                  .filter(inv => inv.status === 'paid')
                  .reduce((sum, inv) => sum + (inv.total || 0), 0);
                const pendingAmount = totalInvoiced - totalPaid;
                
                return customerInvoices.length > 0 ? (
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Wallet className="h-5 w-5 text-green-600" />
                      <h4 className="font-semibold text-green-800">Payment Summary</h4>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-green-700">£{totalPaid.toFixed(2)}</p>
                        <p className="text-xs text-green-600">Total Paid</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-amber-600">£{pendingAmount.toFixed(2)}</p>
                        <p className="text-xs text-amber-600">Pending</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-700">£{totalInvoiced.toFixed(2)}</p>
                        <p className="text-xs text-gray-600">Total Invoiced</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <p className="text-xs text-green-700 font-medium">{customerInvoices.length} invoice(s) on record</p>
                    </div>
                  </div>
                ) : null;
              })()}
              
              <div className="flex flex-wrap gap-2">
                <Button onClick={handleUpdateCustomer} className="bg-primary flex-1 sm:flex-none">Save Changes</Button>
                <Button onClick={() => { setIsCustomerDetailOpen(false); setActiveTab('customers'); toast.info('Use the Possible Duplicates panel at the top of the Customers tab — it previews every change and is reversible.'); }} variant="outline" className="flex-1 sm:flex-none" data-testid="open-merge-customer-btn">Find Duplicates</Button>
                <Button onClick={() => handleDeleteCustomer(selectedCustomer.id)} variant="destructive" className="flex-1 sm:flex-none" data-testid="delete-customer-btn">Delete</Button>
                <Button variant="outline" onClick={() => setSelectedCustomer(null)} className="flex-1 sm:flex-none">Close</Button>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Job History</h3>
                  <div className="flex items-center gap-2">
                    <Button 
                      onClick={() => {
                        const customerEmail = (selectedCustomer.email && selectedCustomer.email !== 'N/A' && selectedCustomer.email.includes('@')) 
                          ? selectedCustomer.email : '';
                        setNewEnquiry({
                          name: selectedCustomer.name,
                          email: customerEmail,
                          phone: selectedCustomer.phone || '',
                          service: 'general-repairs',
                          message: '',
                          preferred_date: '',
                          source: 'manual',
                          customer_id: selectedCustomer.id
                        });
                        setIsCustomerDetailOpen(false);
                        setIsAddEnquiryOpen(true);
                      }} 
                      size="sm" 
                      variant="outline"
                      className="gap-1"
                      data-testid="create-pipeline-job-btn"
                    >
                      <Briefcase className="h-4 w-4" />
                      New Pipeline Job
                    </Button>
                    <Button onClick={() => setIsAddJobOpen(true)} size="sm" className="bg-accent"><Plus className="h-4 w-4 mr-1" />Add Job</Button>
                  </div>
                </div>
                {(() => {
                  // Check if this is the Gilberts customer
                  const isGilbertsCustomer = selectedCustomer.name?.toLowerCase().includes('gilberts');
                  
                  // Get Gilberts shifts as job-like entries for Gilberts customer
                  const gilbertsAsJobs = isGilbertsCustomer ? gilbertsShifts.map(s => ({
                    id: s.id,
                    description: `Contract Work - ${s.entries?.map(e => `${e.admin_name}: ${e.hours}hrs ${e.role === 'cnc' ? 'CNC' : 'Maintenance'}`).join(', ') || 'Shift'}`,
                    date: s.date,
                    time_slot: '10:00',
                    status: s.invoiced ? 'completed' : 'in-progress',
                    isGilbertsShift: true,
                    total_amount: s.total_amount,
                    invoiced: s.invoiced,
                    shiftId: s.id
                  })) : [];
                  
                  // Get linked pipeline jobs (enquiries linked to this customer)
                  const linkedPipelineJobs = enquiries
                    .filter(e => e.customer_id === selectedCustomer.id)
                    .map(e => ({
                      id: e.id,
                      description: `${getServiceLabel(e.service)} — ${e.message?.substring(0, 80) || 'No details'}${e.message?.length > 80 ? '...' : ''}`,
                      date: e.preferred_date || e.created_at?.split('T')[0],
                      time_slot: null,
                      status: normalizeJobStatus(e.status),
                      isPipelineJob: true,
                      pipelineStatus: e.status,
                      estimated_value: e.estimated_value,
                      enquiryRef: e
                    }));
                  
                  // Combine regular jobs with Gilberts shifts and linked pipeline jobs, sort by date descending
                  const allJobs = [...(selectedCustomer.jobs || []), ...gilbertsAsJobs, ...linkedPipelineJobs]
                    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
                  
                  if (allJobs.length === 0) {
                    return <p className="text-muted-foreground text-center py-4">No jobs yet</p>;
                  }
                  
                  return (
                    <div className="space-y-2">
                      {allJobs.map((job, index) => (
                        <div key={job.isGilbertsShift ? `gilberts-${job.id}` : job.isPipelineJob ? `pipeline-${job.id}` : index} className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg gap-2 ${job.isGilbertsShift ? 'bg-indigo-50 border border-indigo-200' : job.isPipelineJob ? 'bg-blue-50 border border-blue-200' : 'bg-muted'}`}>
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              {job.isGilbertsShift ? (
                                <>
                                  <Badge className={job.invoiced ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                                    {job.invoiced ? 'Invoiced' : 'Pending'}
                                  </Badge>
                                  <Badge className="bg-indigo-600 text-white text-xs">Contract</Badge>
                                </>
                              ) : job.isPipelineJob ? (
                                <>
                                  <Badge className={getStatusInfo(job.pipelineStatus, 'job')?.bgColor || 'bg-gray-100'}>
                                    {getStatusInfo(job.pipelineStatus, 'job')?.label || job.pipelineStatus}
                                  </Badge>
                                  <Badge className="bg-blue-600 text-white text-xs">Pipeline</Badge>
                                  {job.estimated_value && (
                                    <span className="text-xs text-muted-foreground">£{job.estimated_value.toLocaleString()}</span>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Badge className={jobStatusConfig[job.status]?.color}>{jobStatusConfig[job.status]?.label}</Badge>
                                  {job.time_slot && <span className="text-xs text-muted-foreground">{job.time_slot}</span>}
                                  {job.photos?.length > 0 && (
                                    <Badge variant="outline" className="text-xs">
                                      <Image className="h-3 w-3 mr-1" />{job.photos.length} photo{job.photos.length > 1 ? 's' : ''}
                                    </Badge>
                                  )}
                                  {job.featured_in_portfolio && (
                                    <Badge className="bg-accent/20 text-accent border-accent/30 text-xs">Featured</Badge>
                                  )}
                                </>
                              )}
                            </div>
                            <p className="font-medium text-sm">{job.description}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(job.date)}
                              {job.isGilbertsShift && job.total_amount && ` • £${job.total_amount.toFixed(2)}`}
                            </p>
                          </div>
                          {job.isPipelineJob ? (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="gap-1"
                              onClick={() => { setIsCustomerDetailOpen(false); openDetail(job.enquiryRef); }}
                              data-testid={`view-pipeline-job-${job.id}`}
                            >
                              <Eye className="h-4 w-4" /> View Job
                            </Button>
                          ) : !job.isGilbertsShift && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Button variant="ghost" size="sm" onClick={() => openJobPhotos(job, selectedCustomer.jobs?.indexOf(job))} title="Manage Photos" data-testid={`job-photos-btn-${index}`}>
                                <Image className="h-4 w-4 text-primary" />
                              </Button>
                              <Select value={job.status} onValueChange={(v) => handleUpdateJobStatus(selectedCustomer.jobs?.indexOf(job), v)}>
                                <SelectTrigger className="w-28 h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="planned">Planned</SelectItem>
                                  <SelectItem value="in-progress">In Progress</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteJob(selectedCustomer.jobs?.indexOf(job))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Job Dialog */}
      <Dialog open={isAddJobOpen} onOpenChange={setIsAddJobOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Add Job</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Description *</Label><Textarea value={newJob.description} onChange={(e) => setNewJob(p => ({ ...p, description: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Date *</Label><Input type="date" value={newJob.date} onChange={(e) => setNewJob(p => ({ ...p, date: e.target.value }))} /></div>
              <div className="flex items-center pt-6">
                <Checkbox 
                  id="allDay" 
                  checked={newJob.is_all_day} 
                  onCheckedChange={(c) => setNewJob(p => ({ ...p, is_all_day: c, time_slot: c ? '' : p.time_slot, duration: c ? 8 : p.duration }))} 
                />
                <Label htmlFor="allDay" className="ml-2 cursor-pointer">All Day Job</Label>
              </div>
            </div>
            {!newJob.is_all_day && (
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Time Slot</Label>
                  <Select value={newJob.time_slot} onValueChange={(v) => setNewJob(p => ({ ...p, time_slot: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select time" /></SelectTrigger>
                    <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Duration (hours)</Label>
                  <Select value={String(newJob.duration)} onValueChange={(v) => setNewJob(p => ({ ...p, duration: parseInt(v) }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 hour</SelectItem>
                      <SelectItem value="2">2 hours</SelectItem>
                      <SelectItem value="3">3 hours</SelectItem>
                      <SelectItem value="4">4 hours</SelectItem>
                      <SelectItem value="5">5 hours</SelectItem>
                      <SelectItem value="6">6 hours</SelectItem>
                      <SelectItem value="7">7 hours</SelectItem>
                      <SelectItem value="8">8 hours</SelectItem>
                      <SelectItem value="9">9 hours</SelectItem>
                      <SelectItem value="10">10 hours</SelectItem>
                      <SelectItem value="11">11 hours</SelectItem>
                      <SelectItem value="12">12 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {newJob.is_all_day && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700 flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                This job will be booked for the entire day
              </div>
            )}
            <div><Label>Status</Label>
              <Select value={newJob.status} onValueChange={(v) => setNewJob(p => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="recurring" checked={newJob.is_recurring} onCheckedChange={(c) => setNewJob(p => ({ ...p, is_recurring: c }))} />
              <Label htmlFor="recurring" className="flex items-center gap-2"><Repeat className="h-4 w-4" />Recurring Job</Label>
            </div>
            {newJob.is_recurring && (
              <div><Label>Repeat Every</Label>
                <Select value={newJob.recurring_interval} onValueChange={(v) => setNewJob(p => ({ ...p, recurring_interval: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select interval" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Assign Team Members</Label>
              <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border rounded-lg p-2">
                {allAdmins.map(admin => (
                  <div 
                    key={admin.id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                      newJob.assigned_admins?.find(a => a.id === admin.id) 
                        ? 'bg-primary/20 border border-primary/30' 
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => {
                      const current = newJob.assigned_admins || [];
                      const exists = current.find(a => a.id === admin.id);
                      if (exists) {
                        setNewJob(p => ({ ...p, assigned_admins: current.filter(a => a.id !== admin.id) }));
                      } else {
                        setNewJob(p => ({ ...p, assigned_admins: [...current, { id: admin.id, name: admin.name || admin.email.split('@')[0] }] }));
                      }
                    }}
                  >
                    <Checkbox checked={!!newJob.assigned_admins?.find(a => a.id === admin.id)} />
                    <span>{admin.name || admin.email.split('@')[0]}</span>
                  </div>
                ))}
              </div>
              {newJob.assigned_admins?.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {newJob.assigned_admins.length} member{newJob.assigned_admins.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>
            <Button onClick={handleAddJob} className="w-full bg-primary">Add Job</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Job Photos Dialog - Enhanced */}
      <Dialog open={isJobPhotosOpen} onOpenChange={(open) => { setIsJobPhotosOpen(open); setEditingCaption(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Manage Job Photos
            </DialogTitle>
            <DialogDescription>
              Upload, organize, and feature photos for this job
            </DialogDescription>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-6">
              {/* Job Info */}
              <div className="bg-muted p-3 rounded-lg">
                <p className="font-medium">{selectedJob.description}</p>
                <p className="text-sm text-muted-foreground">{selectedCustomer?.name} • {formatDate(selectedJob.date)}</p>
              </div>

              {/* Feature in Portfolio Toggle */}
              <div className="flex items-center justify-between p-4 bg-accent/5 border border-accent/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="bg-accent/20 p-2 rounded-lg">
                    <Star className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-medium">Feature in Portfolio</p>
                    <p className="text-sm text-muted-foreground">Show these photos on your landing page</p>
                  </div>
                </div>
                <Checkbox 
                  checked={selectedJob.featured_in_portfolio} 
                  onCheckedChange={handleToggleFeatured}
                  disabled={!selectedJob.photos?.length}
                  data-testid="toggle-featured-checkbox"
                />
              </div>

              {/* Upload Section */}
              <div className="space-y-4">
                <Label className="text-base font-medium">Add Photos</Label>
                
                {/* Direct File Upload */}
                <div 
                  className="border-2 border-dashed border-primary/30 rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.heic,.heif"
                    multiple
                    className="hidden"
                    onChange={handleFileUpload}
                    data-testid="file-upload-input"
                  />
                  {isUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-10 w-10 text-primary animate-spin" />
                      <p className="text-sm text-muted-foreground">Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-10 w-10 text-primary/60" />
                      <p className="font-medium">Click to upload photos</p>
                      <p className="text-sm text-muted-foreground">or drag and drop files here</p>
                      <p className="text-xs text-muted-foreground">Supports: JPEG, PNG, WebP, GIF</p>
                    </div>
                  )}
                </div>

                {/* URL Input (Alternative) */}
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-sm text-muted-foreground">Or paste image URL</Label>
                    <Input 
                      placeholder="https://..."
                      value={newPhotoUrl}
                      onChange={(e) => setNewPhotoUrl(e.target.value)}
                      data-testid="photo-url-input"
                    />
                  </div>
                  <Select defaultValue="general" onValueChange={(type) => newPhotoUrl && handleAddPhoto(type)}>
                    <SelectTrigger className="w-32" disabled={!newPhotoUrl}>
                      <SelectValue placeholder="Add as..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="before">Before</SelectItem>
                      <SelectItem value="after">After</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => handleAddPhoto('general')} disabled={!newPhotoUrl} data-testid="add-photo-btn">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Photo Grid with Drag & Drop */}
              {selectedJob.photos?.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Photos ({selectedJob.photos.length})</Label>
                    <p className="text-xs text-muted-foreground">Drag photos to reorder</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {selectedJob.photos.map((photo, photoIndex) => (
                      <div 
                        key={photo.id || photoIndex}
                        draggable
                        onDragStart={(e) => handleDragStart(e, photoIndex)}
                        onDragOver={(e) => handleDragOver(e, photoIndex)}
                        onDrop={(e) => handleDrop(e, photoIndex)}
                        className={`relative group rounded-lg overflow-hidden border-2 ${draggedPhoto === photoIndex ? 'border-primary opacity-50' : 'border-transparent hover:border-primary/30'} transition-all cursor-grab active:cursor-grabbing`}
                      >
                        {/* Drag Handle */}
                        <div className="absolute top-2 left-2 z-10 bg-black/50 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                          <GripVertical className="h-4 w-4 text-white" />
                        </div>

                        {/* Photo Type Badge */}
                        {photo.photo_type && photo.photo_type !== 'general' && (
                          <div className={`absolute top-2 right-2 z-10 px-2 py-0.5 rounded text-xs font-medium ${photo.photo_type === 'before' ? 'bg-orange-500 text-white' : 'bg-green-500 text-white'}`}>
                            {photo.photo_type === 'before' ? 'BEFORE' : 'AFTER'}
                          </div>
                        )}

                        {/* Image */}
                        <div className="aspect-square">
                          <img 
                            src={getImageUrl(photo.url || photo)} 
                            alt={photo.caption || `Photo ${photoIndex + 1}`} 
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Overlay Actions */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                          {/* Top Actions */}
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="secondary" 
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setEditingCaption(editingCaption === photoIndex ? null : photoIndex)}
                              title="Edit caption"
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleRemovePhoto(photoIndex)}
                              data-testid={`remove-photo-${photoIndex}`}
                              title="Remove photo"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          {/* Bottom: Type Selector */}
                          <Select 
                            value={photo.photo_type || 'general'} 
                            onValueChange={(type) => handleUpdatePhoto(photoIndex, { photo_type: type })}
                          >
                            <SelectTrigger className="h-8 bg-white/90 text-black text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general">General</SelectItem>
                              <SelectItem value="before">Before</SelectItem>
                              <SelectItem value="after">After</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Caption (shown below image) */}
                        {photo.caption && editingCaption !== photoIndex && (
                          <div className="p-2 bg-muted text-xs truncate">{photo.caption}</div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Caption Editor */}
                  {editingCaption !== null && (
                    <div className="p-3 bg-muted rounded-lg space-y-2">
                      <Label className="text-sm">Edit Caption for Photo {editingCaption + 1}</Label>
                      <div className="flex gap-2">
                        <Input 
                          placeholder="Add a caption..."
                          defaultValue={selectedJob.photos[editingCaption]?.caption || ''}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdatePhoto(editingCaption, { caption: e.target.value });
                            }
                          }}
                          data-testid="caption-input"
                        />
                        <Button 
                          size="sm"
                          onClick={(e) => {
                            const input = e.target.closest('.flex')?.querySelector('input');
                            if (input) handleUpdatePhoto(editingCaption, { caption: input.value });
                          }}
                        >
                          Save
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setEditingCaption(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                  <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No photos yet</p>
                  <p className="text-sm">Upload photos or paste URLs above to showcase this job</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Portfolio Photo Dialog */}
      <Dialog open={isAddPortfolioPhotoOpen} onOpenChange={setIsAddPortfolioPhotoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Photo to Portfolio
            </DialogTitle>
            <DialogDescription>
              Add a photo directly to your portfolio without linking it to a customer
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Upload Option */}
            <div 
              className="border-2 border-dashed border-primary/30 rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => portfolioFileInputRef.current?.click()}
            >
              {isUploadingPortfolio ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                  <span className="text-sm text-muted-foreground">Uploading...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-primary/60" />
                  <span className="font-medium">Click to upload photos</span>
                  <span className="text-xs text-muted-foreground">JPEG, PNG, WebP, GIF</span>
                </div>
              )}
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">Or paste URL</span></div>
            </div>

            <div>
              <Label>Photo URL</Label>
              <Input 
                value={newPortfolioPhoto.url}
                onChange={(e) => setNewPortfolioPhoto(p => ({ ...p, url: e.target.value }))}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Caption (optional)</Label>
              <Input 
                value={newPortfolioPhoto.caption}
                onChange={(e) => setNewPortfolioPhoto(p => ({ ...p, caption: e.target.value }))}
                placeholder="Describe this photo..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Photo Type</Label>
              <Select value={newPortfolioPhoto.photo_type} onValueChange={(v) => setNewPortfolioPhoto(p => ({ ...p, photo_type: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="before">Before</SelectItem>
                  <SelectItem value="after">After</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={newPortfolioPhoto.category} onValueChange={(v) => setNewPortfolioPhoto(p => ({ ...p, category: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="carpentry">Carpentry</SelectItem>
                  <SelectItem value="general-repairs">General Repairs</SelectItem>
                  <SelectItem value="furniture-builds">Furniture Builds</SelectItem>
                  <SelectItem value="garden-storage">Garden Storage</SelectItem>
                  <SelectItem value="garden-buildings">Garden Buildings</SelectItem>
                  <SelectItem value="fencing-gates">Fencing & Gates</SelectItem>
                  <SelectItem value="cnc-factory">CNC/Factory Work</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAddStandalonePhoto} className="w-full" disabled={!newPortfolioPhoto.url}>
              Add to Portfolio
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Team Member Dialog */}
      <Dialog open={isAddAdminOpen} onOpenChange={setIsAddAdminOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Add Team Member
            </DialogTitle>
            <DialogDescription>
              Create a new admin account who can access the dashboard
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input 
                value={newAdminData.name}
                onChange={(e) => setNewAdminData(p => ({ ...p, name: e.target.value }))}
                placeholder="Team member name"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input 
                type="email"
                value={newAdminData.email}
                onChange={(e) => setNewAdminData(p => ({ ...p, email: e.target.value }))}
                placeholder="email@example.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input 
                type="password"
                value={newAdminData.password}
                onChange={(e) => setNewAdminData(p => ({ ...p, password: e.target.value }))}
                placeholder="Minimum 8 characters"
                className="mt-1"
              />
            </div>
            <Button onClick={handleAddAdmin} className="w-full" disabled={!newAdminData.email || !newAdminData.password}>
              Add Team Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Team Member Dialog */}
      <Dialog open={!!editingAdmin} onOpenChange={(open) => !open && setEditingAdmin(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              Edit Team Member
            </DialogTitle>
            <DialogDescription>
              Update team member information
            </DialogDescription>
          </DialogHeader>
          {editingAdmin && (
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input 
                  value={editingAdmin.name || ''}
                  onChange={(e) => setEditingAdmin(p => ({ ...p, name: e.target.value }))}
                  placeholder="Team member name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={editingAdmin.email || ''}
                  onChange={(e) => setEditingAdmin(p => ({ ...p, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  value={editingAdmin.phone || ''}
                  onChange={(e) => setEditingAdmin(p => ({ ...p, phone: e.target.value }))}
                  placeholder="07XXX XXXXXX"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input 
                  value={editingAdmin.address || ''}
                  onChange={(e) => setEditingAdmin(p => ({ ...p, address: e.target.value }))}
                  placeholder="Address"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Calendar Color</Label>
                <div className="flex gap-2 mt-2">
                  {['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'].map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setEditingAdmin(p => ({ ...p, color }))}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${editingAdmin.color === color ? 'border-gray-900 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleUpdateAdmin} className="flex-1">
                  Save Changes
                </Button>
                <Button variant="outline" onClick={() => setEditingAdmin(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Calendar Guide Dialog */}
      <Dialog open={isShareCalendarOpen} onOpenChange={setIsShareCalendarOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              Share Your Business Calendar
            </DialogTitle>
            <DialogDescription>
              Follow these steps to share your calendar with team members
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted p-4 rounded-lg space-y-3">
              <div className="flex gap-3">
                <div className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">1</div>
                <p className="text-sm">Open <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google Calendar</a> in your browser</p>
              </div>
              <div className="flex gap-3">
                <div className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">2</div>
                <p className="text-sm">Find your calendar in the left sidebar, click the three dots (⋮) next to it</p>
              </div>
              <div className="flex gap-3">
                <div className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">3</div>
                <p className="text-sm">Select <strong>&quot;Settings and sharing&quot;</strong></p>
              </div>
              <div className="flex gap-3">
                <div className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">4</div>
                <p className="text-sm">Scroll to <strong>&quot;Share with specific people or groups&quot;</strong></p>
              </div>
              <div className="flex gap-3">
                <div className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">5</div>
                <p className="text-sm">Click <strong>&quot;Add people and groups&quot;</strong> and enter team member emails:</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Team Member Emails:</Label>
              {allAdmins.map((admin) => (
                <div key={admin.id} className="flex items-center justify-between bg-muted/50 p-2 rounded text-sm">
                  <span>{admin.email}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigator.clipboard.writeText(admin.email).then(() => toast.success('Email copied!'))}
                  >
                    Copy
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <a
                href="https://calendar.google.com/calendar/r/settings"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
              >
                <Button className="w-full gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Open Calendar Settings
                </Button>
              </a>
              <Button variant="outline" onClick={() => setIsShareCalendarOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Block Day Dialog - Enhanced with Multi-Day Scheduling */}
      <Dialog open={isBlockDayOpen} onOpenChange={setIsBlockDayOpen}>
        <DialogContent className="max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{newBlockedDay.scheduledDays?.length > 1 ? 'Schedule Multi-Day Job' : (newBlockedDay.time_slot ? 'Block Time' : 'Schedule Job')}</DialogTitle>
            <DialogDescription>Schedule a booking or block time on the calendar</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Day Selection Mode */}
            <div>
              <Label>Scheduling Mode</Label>
              <div className="flex gap-2 mt-2">
                <Button 
                  type="button"
                  variant={newBlockedDay.selectionMode === "single" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setNewBlockedDay(p => ({ ...p, selectionMode: "single", scheduledDays: p.date ? [p.date] : [], rangeStart: null, rangeEnd: null }))}
                >
                  Single Day
                </Button>
                <Button 
                  type="button"
                  variant={newBlockedDay.selectionMode === "range" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setNewBlockedDay(p => ({ ...p, selectionMode: "range", scheduledDays: [] }))}
                >
                  Date Range
                </Button>
                <Button 
                  type="button"
                  variant={newBlockedDay.selectionMode === "multi" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setNewBlockedDay(p => ({ ...p, selectionMode: "multi", scheduledDays: p.date ? [p.date] : [] }))}
                >
                  Pick Days
                </Button>
              </div>
            </div>

            {/* Single Day Selection */}
            {newBlockedDay.selectionMode === "single" && (
              <div>
                <Label>Date *</Label>
                <Input 
                  type="date" 
                  value={newBlockedDay.date} 
                  onChange={(e) => setNewBlockedDay(p => ({ ...p, date: e.target.value, scheduledDays: [e.target.value] }))} 
                  className="h-11" 
                />
              </div>
            )}

            {/* Date Range Selection */}
            {newBlockedDay.selectionMode === "range" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input 
                    type="date" 
                    value={newBlockedDay.rangeStart || ""} 
                    onChange={(e) => {
                      const start = e.target.value;
                      const end = newBlockedDay.rangeEnd;
                      let days = [];
                      if (start && end && start <= end) {
                        // Generate date range without timezone issues
                        // Parse as local date parts, not UTC
                        let [y, m, d] = start.split('-').map(Number);
                        const [ey, em, ed] = end.split('-').map(Number);
                        const endDateNum = new Date(ey, em - 1, ed).getTime();
                        
                        while (new Date(y, m - 1, d).getTime() <= endDateNum) {
                          // Format as YYYY-MM-DD
                          days.push(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
                          // Increment day
                          const nextDate = new Date(y, m - 1, d + 1);
                          y = nextDate.getFullYear();
                          m = nextDate.getMonth() + 1;
                          d = nextDate.getDate();
                        }
                      }
                      setNewBlockedDay(p => ({ ...p, rangeStart: start, date: start, scheduledDays: days }));
                    }} 
                    className="h-11" 
                  />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input 
                    type="date" 
                    value={newBlockedDay.rangeEnd || ""} 
                    min={newBlockedDay.rangeStart || ""}
                    onChange={(e) => {
                      const start = newBlockedDay.rangeStart;
                      const end = e.target.value;
                      let days = [];
                      if (start && end && start <= end) {
                        // Generate date range without timezone issues
                        let [y, m, d] = start.split('-').map(Number);
                        const [ey, em, ed] = end.split('-').map(Number);
                        const endDateNum = new Date(ey, em - 1, ed).getTime();
                        
                        while (new Date(y, m - 1, d).getTime() <= endDateNum) {
                          days.push(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
                          const nextDate = new Date(y, m - 1, d + 1);
                          y = nextDate.getFullYear();
                          m = nextDate.getMonth() + 1;
                          d = nextDate.getDate();
                        }
                      }
                      setNewBlockedDay(p => ({ ...p, rangeEnd: end, scheduledDays: days }));
                    }} 
                    className="h-11" 
                  />
                </div>
              </div>
            )}

            {/* Multi-Day Picker */}
            {newBlockedDay.selectionMode === "multi" && (
              <div>
                <Label>Select Days (click to add/remove)</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input 
                    type="date" 
                    onChange={(e) => {
                      const dateVal = e.target.value;
                      if (dateVal) {
                        setNewBlockedDay(p => {
                          const days = p.scheduledDays || [];
                          if (days.includes(dateVal)) {
                            return { ...p, scheduledDays: days.filter(d => d !== dateVal) };
                          } else {
                            return { ...p, scheduledDays: [...days, dateVal].sort(), date: dateVal };
                          }
                        });
                        e.target.value = "";
                      }
                    }} 
                    className="h-11" 
                  />
                </div>
                {newBlockedDay.scheduledDays?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {newBlockedDay.scheduledDays.map(day => (
                      <Badge 
                        key={day} 
                        variant="secondary" 
                        className="cursor-pointer hover:bg-red-100"
                        onClick={() => setNewBlockedDay(p => ({ ...p, scheduledDays: p.scheduledDays.filter(d => d !== day) }))}
                      >
                        {formatDate(day)} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Show selected days count */}
            {(newBlockedDay.scheduledDays?.length || 0) > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm font-medium text-blue-800">
                  {newBlockedDay.scheduledDays.length} day{newBlockedDay.scheduledDays.length > 1 ? 's' : ''} selected
                </p>
                {newBlockedDay.scheduledDays.length <= 5 && (
                  <p className="text-xs text-blue-600">
                    {newBlockedDay.scheduledDays.map(d => formatDate(d)).join(', ')}
                  </p>
                )}
              </div>
            )}

            {/* Time Selection */}
            <div>
              <Label>Start Time (leave empty for whole day)</Label>
              <Select value={newBlockedDay.time_slot || "whole-day"} onValueChange={(v) => setNewBlockedDay(p => ({ ...p, time_slot: v === "whole-day" ? "" : v }))}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Whole day" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="whole-day">Whole Day</SelectItem>
                  {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {newBlockedDay.time_slot && (
              <div><Label>Duration (hours)</Label>
                <Select value={String(newBlockedDay.duration)} onValueChange={(v) => setNewBlockedDay(p => ({ ...p, duration: parseInt(v) }))}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(h => (
                      <SelectItem key={h} value={String(h)}>{h} hour{h > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div><Label>Reason / Description *</Label><Input value={newBlockedDay.reason} onChange={(e) => setNewBlockedDay(p => ({ ...p, reason: e.target.value }))} placeholder="Customer name, job description..." className="h-11" /></div>
            
            <div><Label>Type</Label>
              <Select value={newBlockedDay.block_type} onValueChange={(v) => setNewBlockedDay(p => ({ ...p, block_type: v }))}>
                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="enquiry">Enquiry</SelectItem>
                  <SelectItem value="job">Job</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="holiday">Holiday</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Pricing Section - Only for job/enquiry types */}
            {(newBlockedDay.block_type === 'job' || newBlockedDay.block_type === 'enquiry') && (
              <div className="border rounded-lg p-4 bg-emerald-50">
                <Label className="text-emerald-800 font-semibold">Pricing Strategy</Label>
                
                {/* Team Size Selection */}
                <div className="mt-3">
                  <Label className="text-sm">Team Size</Label>
                  <div className="flex gap-2 mt-1">
                    <Button 
                      type="button"
                      variant={newBlockedDay.teamSize === 1 ? "default" : "outline"}
                      size="sm"
                      className={newBlockedDay.teamSize === 1 ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                      onClick={() => setNewBlockedDay(p => ({ ...p, teamSize: 1, dayRate: PRICING.DAY_RATES[1] }))}
                    >
                      1 Person (£{PRICING.DAY_RATES[1]}/day)
                    </Button>
                    <Button 
                      type="button"
                      variant={newBlockedDay.teamSize === 2 ? "default" : "outline"}
                      size="sm"
                      className={newBlockedDay.teamSize === 2 ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                      onClick={() => setNewBlockedDay(p => ({ ...p, teamSize: 2, dayRate: PRICING.DAY_RATES[2] }))}
                    >
                      2 People (£{PRICING.DAY_RATES[2]}/day)
                    </Button>
                  </div>
                </div>

                {/* Multi-day Discount */}
                {(newBlockedDay.scheduledDays?.length || 0) >= 3 && (
                  <div className="mt-3">
                    <Label className="text-sm">Multi-Day Discount</Label>
                    <Select 
                      value={String(newBlockedDay.multiDayDiscount || "auto")} 
                      onValueChange={(v) => setNewBlockedDay(p => ({ ...p, multiDayDiscount: v === "auto" ? null : parseFloat(v) }))}
                    >
                      <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto ({newBlockedDay.scheduledDays?.length >= 10 ? '15%' : newBlockedDay.scheduledDays?.length >= 5 ? '10%' : '5%'})</SelectItem>
                        <SelectItem value="0">No Discount</SelectItem>
                        <SelectItem value="5">5% Discount</SelectItem>
                        <SelectItem value="10">10% Discount</SelectItem>
                        <SelectItem value="15">15% Discount</SelectItem>
                        <SelectItem value="20">20% Discount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Price Calculation */}
                {(newBlockedDay.scheduledDays?.length || 0) > 0 && (() => {
                  const pricing = calculateJobPrice(
                    newBlockedDay.scheduledDays?.length || 1,
                    newBlockedDay.teamSize || 1,
                    newBlockedDay.multiDayDiscount
                  );
                  return (
                    <div className="mt-4 p-3 bg-white rounded border border-emerald-200">
                      <div className="flex justify-between text-sm">
                        <span>{pricing.numDays} day{pricing.numDays > 1 ? 's' : ''} × £{pricing.dayRate}/day</span>
                        <span>£{pricing.subtotal.toLocaleString()}</span>
                      </div>
                      {pricing.discountPercent > 0 && (
                        <div className="flex justify-between text-sm text-emerald-600">
                          <span>Multi-day discount ({pricing.discountPercent}%)</span>
                          <span>-£{pricing.discountAmount.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg border-t mt-2 pt-2">
                        <span>Total</span>
                        <span className="text-emerald-700">£{pricing.total.toLocaleString()}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Team Assignment */}
            <div>
              <Label>Assign Team Members</Label>
              <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border rounded-lg p-2">
                {allAdmins.map(admin => (
                  <div 
                    key={admin.id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                      newBlockedDay.assigned_admins?.find(a => a.id === admin.id) 
                        ? 'bg-primary/20 border border-primary/30' 
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => {
                      const current = newBlockedDay.assigned_admins || [];
                      const exists = current.find(a => a.id === admin.id);
                      if (exists) {
                        setNewBlockedDay(p => ({ ...p, assigned_admins: current.filter(a => a.id !== admin.id) }));
                      } else {
                        setNewBlockedDay(p => ({ ...p, assigned_admins: [...current, { id: admin.id, name: admin.name || admin.email.split('@')[0] }] }));
                      }
                    }}
                  >
                    <Checkbox checked={!!newBlockedDay.assigned_admins?.find(a => a.id === admin.id)} />
                    <span className="text-sm">{admin.name || admin.email.split('@')[0]}</span>
                  </div>
                ))}
              </div>
              {newBlockedDay.assigned_admins?.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {newBlockedDay.assigned_admins.length} member{newBlockedDay.assigned_admins.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button onClick={handleBlockDay} className="flex-1 bg-primary hover:bg-primary/90">
                {newBlockedDay.scheduledDays?.length > 1 
                  ? `Book ${newBlockedDay.scheduledDays.length} Days`
                  : (newBlockedDay.time_slot 
                      ? (newBlockedDay.duration > 1 ? `${newBlockedDay.duration} Hours` : '1 Hour') 
                      : 'Whole Day'
                    )
                }
              </Button>
              <Button variant="outline" onClick={() => setIsBlockDayOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Recurring Contract Dialog */}
      <Dialog open={isAddContractOpen} onOpenChange={closeContractDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Repeat className="h-5 w-5" />
              {editingContract ? 'Edit Recurring Contract' : 'New Recurring Contract'}
            </DialogTitle>
            <DialogDescription>
              {editingContract ? 'Update the contract settings below' : 'Create a recurring job schedule (e.g., Gilberts - Mon/Tue/Wed 10am-8pm)'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Customer (optional)</Label>
              <Select 
                value={newContract.customer_id || "none"} 
                onValueChange={(v) => {
                  if (v === "none") {
                    setNewContract(p => ({ ...p, customer_id: "", customer_name: "", customer_address: "" }));
                  } else {
                    const selected = customers.find(c => c.id === v);
                    if (selected) {
                      setNewContract(p => ({ 
                        ...p, 
                        customer_id: v, 
                        customer_name: selected.name || "",
                        customer_address: selected.address || "",
                        name: p.name || `${selected.name} Contract`
                      }));
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a customer..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- No customer selected --</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Customer Name</Label>
                <Input 
                  value={newContract.customer_name} 
                  onChange={(e) => setNewContract(p => ({ ...p, customer_name: e.target.value }))} 
                  placeholder="Customer name"
                />
              </div>
              <div>
                <Label>Customer Address</Label>
                <Input 
                  value={newContract.customer_address} 
                  onChange={(e) => setNewContract(p => ({ ...p, customer_address: e.target.value }))} 
                  placeholder="Address"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Contract Name *</Label>
                <Input 
                  value={newContract.name} 
                  onChange={(e) => setNewContract(p => ({ ...p, name: e.target.value }))} 
                  placeholder="e.g., Gilberts Weekly Contract"
                />
              </div>
              <div>
                <Label>Job Type</Label>
                <Select value={newContract.service_type || "general-repairs"} onValueChange={(v) => setNewContract(p => ({ ...p, service_type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carpentry">Carpentry</SelectItem>
                    <SelectItem value="general-repairs">General Repairs</SelectItem>
                    <SelectItem value="furniture-builds">Furniture Builds</SelectItem>
                    <SelectItem value="garden-storage">Garden Storage</SelectItem>
                    <SelectItem value="garden-buildings">Garden Buildings</SelectItem>
                    <SelectItem value="fencing-gates">Fencing & Gates</SelectItem>
                    <SelectItem value="cnc-factory">CNC/Factory Work</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Days of Week *</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {DAYS_OF_WEEK.map(day => (
                  <div 
                    key={day.value}
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                      newContract.days_of_week.includes(day.value) 
                        ? 'bg-primary/20 border-primary' 
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => {
                      const current = newContract.days_of_week;
                      if (current.includes(day.value)) {
                        setNewContract(p => ({ ...p, days_of_week: current.filter(d => d !== day.value) }));
                      } else {
                        setNewContract(p => ({ ...p, days_of_week: [...current, day.value].sort() }));
                      }
                    }}
                  >
                    <Checkbox checked={newContract.days_of_week.includes(day.value)} />
                    <span className="text-sm">{day.label.slice(0, 3)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Select value={newContract.start_time} onValueChange={(v) => setNewContract(p => ({ ...p, start_time: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>End Time</Label>
                <Select value={newContract.end_time} onValueChange={(v) => setNewContract(p => ({ ...p, end_time: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rolling Weeks Ahead</Label>
                <Select value={String(newContract.rolling_weeks)} onValueChange={(v) => setNewContract(p => ({ ...p, rolling_weeks: parseInt(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 weeks</SelectItem>
                    <SelectItem value="4">4 weeks</SelectItem>
                    <SelectItem value="6">6 weeks</SelectItem>
                    <SelectItem value="8">8 weeks</SelectItem>
                    <SelectItem value="12">12 weeks</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={newContract.block_type} onValueChange={(v) => setNewContract(p => ({ ...p, block_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="job">Job</SelectItem>
                    <SelectItem value="enquiry">Enquiry</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Assign Team Members</Label>
              <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border rounded-lg p-2">
                {allAdmins.map(admin => (
                  <div 
                    key={admin.id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                      newContract.assigned_admins?.find(a => a.id === admin.id) 
                        ? 'bg-primary/20 border border-primary/30' 
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => {
                      const current = newContract.assigned_admins || [];
                      const exists = current.find(a => a.id === admin.id);
                      if (exists) {
                        setNewContract(p => ({ ...p, assigned_admins: current.filter(a => a.id !== admin.id) }));
                      } else {
                        setNewContract(p => ({ ...p, assigned_admins: [...current, { id: admin.id, name: admin.name || admin.email.split('@')[0] }] }));
                      }
                    }}
                  >
                    <Checkbox checked={!!newContract.assigned_admins?.find(a => a.id === admin.id)} />
                    <span 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: admin.color || TEAM_COLORS[0] }}
                    ></span>
                    <span>{admin.name || admin.email.split('@')[0]}</span>
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={handleSaveContract} className="w-full">
              {editingContract ? 'Save Changes' : 'Create Contract'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Conflicts Dialog */}
      <Dialog open={showConflicts} onOpenChange={setShowConflicts}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-orange-600 flex items-center gap-2">
              ⚠️ Scheduling Conflicts
            </DialogTitle>
            <DialogDescription>
              These dates have existing bookings that conflict with recurring contracts
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {contractConflicts.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No conflicts found! ✓</p>
            ) : (
              contractConflicts.map((conflict, idx) => (
                <div key={idx} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{conflict.contract_name || 'Contract'}</p>
                    <Badge variant="outline">{conflict.date}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Existing booking: {conflict.existing_bookings?.[0]?.reason || conflict.conflicting_bookings?.[0]?.reason || 'Unknown'}
                  </p>
                </div>
              ))
            )}
          </div>
          <Button onClick={() => setShowConflicts(false)} className="w-full mt-4">
            Close
          </Button>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Dialog */}
      <Dialog open={isEditBookingOpen} onOpenChange={setIsEditBookingOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              Edit Booking
            </DialogTitle>
            <DialogDescription>
              Update the booking details below
            </DialogDescription>
          </DialogHeader>
          {editingBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input 
                    type="date" 
                    value={editingBooking.date} 
                    onChange={(e) => setEditingBooking(p => ({ ...p, date: e.target.value }))} 
                  />
                </div>
                <div>
                  <Label>Start Time</Label>
                  <Select value={editingBooking.time_slot || "whole_day"} onValueChange={(v) => setEditingBooking(p => ({ ...p, time_slot: v === "whole_day" ? null : v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="whole_day">Whole Day</SelectItem>
                      {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {editingBooking.time_slot && (
                <div>
                  <Label>Duration (hours)</Label>
                  <Select value={String(editingBooking.duration || 1)} onValueChange={(v) => setEditingBooking(p => ({ ...p, duration: parseInt(v) }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(h => (
                        <SelectItem key={h} value={String(h)}>{h} hour{h > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Type</Label>
                <Select value={editingBooking.block_type} onValueChange={(v) => setEditingBooking(p => ({ ...p, block_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(blockTypeConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Description/Reason</Label>
                <Input 
                  value={editingBooking.reason} 
                  onChange={(e) => setEditingBooking(p => ({ ...p, reason: e.target.value }))} 
                  placeholder="Description of the booking"
                />
              </div>

              <div>
                <Label>Assign Team Members</Label>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                  {allAdmins.map(admin => (
                    <div 
                      key={admin.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        editingBooking.assigned_admins?.find(a => a.id === admin.id) 
                          ? 'bg-primary/20 border border-primary/30' 
                          : 'hover:bg-muted'
                      }`}
                      onClick={() => {
                        const current = editingBooking.assigned_admins || [];
                        const exists = current.find(a => a.id === admin.id);
                        if (exists) {
                          setEditingBooking(p => ({ ...p, assigned_admins: current.filter(a => a.id !== admin.id) }));
                        } else {
                          setEditingBooking(p => ({ ...p, assigned_admins: [...current, { id: admin.id, name: admin.name || admin.email.split('@')[0] }] }));
                        }
                      }}
                    >
                      <Checkbox checked={!!editingBooking.assigned_admins?.find(a => a.id === admin.id)} />
                      <span 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: admin.color || TEAM_COLORS[0] }}
                      ></span>
                      <span>{admin.name || admin.email.split('@')[0]}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleUpdateBooking} className="flex-1">
                  Save Changes
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => { handleUnblockDay(editingBooking.id); setIsEditBookingOpen(false); }}
                >
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Invoice Dialog */}
      <Dialog open={isCreateInvoiceOpen} onOpenChange={setIsCreateInvoiceOpen}>
        <DialogContent className="max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Create Invoice
            </DialogTitle>
            <DialogDescription>
              Generate a professional PDF invoice
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Customer Selection */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <Label className="text-sm font-medium">Select Existing Customer</Label>
              <Select onValueChange={(v) => {
                const customer = customers.find(c => c.id === v);
                if (customer) {
                  setNewInvoice(p => ({
                    ...p,
                    customer_name: customer.name,
                    customer_email: customer.email || '',
                    customer_address: customer.address || ''
                  }));
                }
              }}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select customer..." /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name} - {c.address?.substring(0, 30)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Customer Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Input 
                  value={newInvoice.customer_name} 
                  onChange={(e) => setNewInvoice(p => ({ ...p, customer_name: e.target.value }))}
                  placeholder="e.g., JR Gilbert Engineering"
                />
              </div>
              <div>
                <Label>Payment Terms (days)</Label>
                <Input 
                  type="number"
                  value={newInvoice.payment_days} 
                  onChange={(e) => setNewInvoice(p => ({ ...p, payment_days: parseInt(e.target.value) || 7 }))}
                />
              </div>
            </div>

            {/* Date and Reference - Optional */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Invoice Date <span className="text-muted-foreground text-xs">(optional - defaults to today)</span></Label>
                <Input 
                  type="date"
                  value={newInvoice.date} 
                  onChange={(e) => setNewInvoice(p => ({ ...p, date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Reference Number <span className="text-muted-foreground text-xs">(optional - auto-generated)</span></Label>
                <Input 
                  value={newInvoice.reference} 
                  onChange={(e) => setNewInvoice(p => ({ ...p, reference: e.target.value }))}
                  placeholder="e.g., SHS-2026-005"
                />
              </div>
            </div>
            
            <div>
              <Label>Customer Address *</Label>
              <Textarea 
                value={newInvoice.customer_address} 
                onChange={(e) => setNewInvoice(p => ({ ...p, customer_address: e.target.value }))}
                placeholder="Unit 2&#10;Business Park&#10;Town&#10;Postcode"
                rows={3}
              />
            </div>
            
            <div>
              <Label>Description (optional)</Label>
              <Textarea 
                value={newInvoice.description} 
                onChange={(e) => setNewInvoice(p => ({ ...p, description: e.target.value }))}
                placeholder="Brief description of work completed..."
                rows={2}
              />
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Line Items *</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={addInvoiceLineItem}
                  className="gap-1"
                >
                  <Plus className="h-3 w-3" />
                  Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {newInvoice.line_items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <div className="flex-1">
                      <Input 
                        value={item.description}
                        onChange={(e) => updateInvoiceLineItem(index, 'description', e.target.value)}
                        placeholder="Service description"
                      />
                    </div>
                    <div className="w-20">
                      <Input 
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateInvoiceLineItem(index, 'quantity', e.target.value)}
                        placeholder="Qty"
                        min="0"
                        step="0.5"
                      />
                    </div>
                    <div className="w-24">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                        <Input 
                          type="number"
                          value={item.unit_cost}
                          onChange={(e) => updateInvoiceLineItem(index, 'unit_cost', e.target.value)}
                          placeholder="0.00"
                          className="pl-7"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                    <div className="w-24 text-right pt-2 font-medium">
                      £{(item.quantity * item.unit_cost).toFixed(2)}
                    </div>
                    {newInvoice.line_items.length > 1 && (
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeInvoiceLineItem(index)}
                        className="text-red-600 hover:text-red-700 px-2"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Total */}
              <div className="flex justify-end mt-4 pt-4 border-t">
                <div className="text-right">
                  <span className="text-muted-foreground mr-4">Total:</span>
                  <span className="text-xl font-bold">
                    £{newInvoice.line_items.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button 
                onClick={handleCreateInvoice} 
                className="flex-1 gap-2"
                disabled={isCreatingInvoice}
              >
                {isCreatingInvoice ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    Create Invoice
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={() => setIsCreateInvoiceOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Quote Dialog */}
      <Dialog open={isCreateQuoteOpen} onOpenChange={setIsCreateQuoteOpen}>
        <DialogContent className="max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Create Quote
            </DialogTitle>
            <DialogDescription>
              Generate a professional quote PDF
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Customer Selection */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <Label className="text-sm font-medium">Select Existing Customer</Label>
              <Select onValueChange={(v) => {
                const customer = customers.find(c => c.id === v);
                if (customer) {
                  setNewQuote(p => ({
                    ...p,
                    customer_name: customer.name,
                    customer_email: customer.email || '',
                    customer_address: customer.address || ''
                  }));
                }
              }}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select customer..." /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name} - {c.address?.substring(0, 30)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Customer Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Input 
                  value={newQuote.customer_name} 
                  onChange={(e) => setNewQuote(p => ({ ...p, customer_name: e.target.value }))}
                  placeholder="e.g., John Smith"
                />
              </div>
              <div>
                <Label>Customer Email</Label>
                <Input 
                  type="email"
                  value={newQuote.customer_email} 
                  onChange={(e) => setNewQuote(p => ({ ...p, customer_email: e.target.value }))}
                  placeholder="customer@email.com"
                />
              </div>
            </div>

            {/* Date and Reference */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Quote Date <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input 
                  type="date"
                  value={newQuote.date} 
                  onChange={(e) => setNewQuote(p => ({ ...p, date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Reference <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input 
                  value={newQuote.reference} 
                  onChange={(e) => setNewQuote(p => ({ ...p, reference: e.target.value }))}
                  placeholder="e.g., SHS-Q-2026-001"
                />
              </div>
              <div>
                <Label>Valid For (days)</Label>
                <Input 
                  type="number"
                  value={newQuote.valid_days} 
                  onChange={(e) => setNewQuote(p => ({ ...p, valid_days: parseInt(e.target.value) || 30 }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Customer Address *</Label>
              <Textarea 
                value={newQuote.customer_address} 
                onChange={(e) => setNewQuote(p => ({ ...p, customer_address: e.target.value }))}
                placeholder="Full address..."
                rows={2}
              />
            </div>
            
            <div>
              <Label>Description (optional)</Label>
              <Textarea 
                value={newQuote.description} 
                onChange={(e) => setNewQuote(p => ({ ...p, description: e.target.value }))}
                placeholder="Brief description of proposed work... (line breaks will be preserved in PDF)"
                rows={6}
                className="whitespace-pre-wrap"
              />
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Line Items *</Label>
                <Button type="button" variant="outline" size="sm" onClick={addQuoteLineItem} className="gap-1">
                  <Plus className="h-3 w-3" />
                  Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {newQuote.line_items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <div className="flex-1">
                      <Input 
                        value={item.description}
                        onChange={(e) => updateQuoteLineItem(index, 'description', e.target.value)}
                        placeholder="Service description"
                      />
                    </div>
                    <div className="w-20">
                      <Input 
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateQuoteLineItem(index, 'quantity', e.target.value)}
                        placeholder="Qty"
                        min="0"
                        step="0.5"
                      />
                    </div>
                    <div className="w-24">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                        <Input 
                          type="number"
                          value={item.unit_cost}
                          onChange={(e) => updateQuoteLineItem(index, 'unit_cost', e.target.value)}
                          placeholder="0.00"
                          className="pl-7"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                    <div className="w-24 text-right pt-2 font-medium">
                      £{(item.quantity * item.unit_cost).toFixed(2)}
                    </div>
                    {newQuote.line_items.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeQuoteLineItem(index)} className="text-red-600 px-2">
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="flex justify-end mt-4 pt-4 border-t">
                <div className="text-right">
                  <span className="text-muted-foreground mr-4">Total:</span>
                  <span className="text-xl font-bold">
                    £{newQuote.line_items.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* File Attachments */}
            <div>
              <Label>Attachments (photos, documents)</Label>
              <div className="mt-2 space-y-2">
                <Input 
                  type="file" 
                  multiple 
                  onChange={handleQuoteFileUpload}
                  disabled={isUploadingQuoteFiles}
                  className="cursor-pointer"
                />
                {isUploadingQuoteFiles && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </div>
                )}
                {quoteFiles.length > 0 && (
                  <div className="space-y-1">
                    {quoteFiles.map((file) => (
                      <div key={file.name} className="flex items-center justify-between bg-muted/50 rounded px-3 py-2 text-sm">
                        <span className="truncate flex-1">{file.name}</span>
                        <Button variant="ghost" size="sm" onClick={() => removeQuoteFile(index)} className="h-6 px-2">
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button onClick={handleCreateQuote} className="flex-1 gap-2" disabled={isCreatingQuote}>
                {isCreatingQuote ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    Create Quote
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={() => setIsCreateQuoteOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={isEditInvoiceOpen} onOpenChange={setIsEditInvoiceOpen}>
        <DialogContent className="max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              Edit Invoice {editingInvoice?.reference}
            </DialogTitle>
            <DialogDescription>
              Update the invoice details. A new PDF will be generated automatically.
            </DialogDescription>
          </DialogHeader>
          {editingInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer Name *</Label>
                  <Input 
                    value={editingInvoice.customer_name || ''} 
                    onChange={(e) => setEditingInvoice(p => ({ ...p, customer_name: e.target.value }))}
                    data-testid="edit-invoice-name"
                  />
                </div>
                <div>
                  <Label>Invoice Date</Label>
                  <Input 
                    type="date"
                    value={editingInvoice.date || ''}
                    onChange={(e) => setEditingInvoice(p => ({ ...p, date: e.target.value }))}
                    data-testid="edit-invoice-date"
                  />
                </div>
              </div>
              
              <div>
                <Label>Customer Address *</Label>
                <Textarea 
                  value={editingInvoice.customer_address || ''} 
                  onChange={(e) => setEditingInvoice(p => ({ ...p, customer_address: e.target.value }))}
                  rows={3}
                  data-testid="edit-invoice-address"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea 
                  value={editingInvoice.description || ''} 
                  onChange={(e) => setEditingInvoice(p => ({ ...p, description: e.target.value }))}
                  rows={2}
                  data-testid="edit-invoice-description"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Line Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addEditInvoiceLineItem}>
                    <Plus className="h-3 w-3 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="space-y-2">
                  {editingInvoice.line_items.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateEditInvoiceLineItem(index, 'description', e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateEditInvoiceLineItem(index, 'quantity', e.target.value)}
                        className="w-20"
                      />
                      <Input
                        type="number"
                        placeholder="Cost"
                        value={item.unit_cost}
                        onChange={(e) => updateEditInvoiceLineItem(index, 'unit_cost', e.target.value)}
                        className="w-24"
                      />
                      <div className="w-24 text-right pt-2 font-medium">
                        £{((item.quantity || 0) * (item.unit_cost || 0)).toFixed(2)}
                      </div>
                      {editingInvoice.line_items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeEditInvoiceLineItem(index)}
                          className="text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="text-right mt-2 text-lg font-bold">
                  Total: £{editingInvoice.line_items.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_cost || 0), 0).toFixed(2)}
                </div>
              </div>

              {/* Profitability widget — live-recomputes from edited line items minus received materials */}
              {(() => {
                const target = (editingInvoice.customer_name || '').trim().toLowerCase();
                const inferredJobId = editingInvoice.job_id ||
                  (enquiries || []).find(e => (e.name || '').trim().toLowerCase() === target)?.id;
                if (!inferredJobId) return null;
                return (
                  <InvoiceProfitabilityWidget
                    authHeaders={authHeaders}
                    jobId={inferredJobId}
                    revenueOverride={editingInvoice.line_items.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_cost || 0), 0)}
                  />
                );
              })()}

              <div>
                <Label>Payment Terms (days)</Label>
                <Input 
                  type="number"
                  value={editingInvoice.payment_days || 7} 
                  onChange={(e) => setEditingInvoice(p => ({ ...p, payment_days: parseInt(e.target.value) || 7 }))}
                  className="w-24"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={saveEditedInvoice} 
                  className="flex-1 gap-2"
                  disabled={isSavingInvoice}
                  data-testid="save-invoice-btn"
                >
                  {isSavingInvoice ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Save & Regenerate PDF
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={() => setIsEditInvoiceOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Quote Dialog */}
      <Dialog open={isEditQuoteOpen} onOpenChange={setIsEditQuoteOpen}>
        <DialogContent className="max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              Edit Quote {editingQuote?.reference}
            </DialogTitle>
            <DialogDescription>
              Update the quote details. A new PDF will be generated automatically.
            </DialogDescription>
          </DialogHeader>
          {editingQuote && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer Name *</Label>
                  <Input 
                    value={editingQuote.customer_name || ''} 
                    onChange={(e) => setEditingQuote(p => ({ ...p, customer_name: e.target.value }))}
                    data-testid="edit-quote-name"
                  />
                </div>
                <div>
                  <Label>Customer Email</Label>
                  <Input 
                    type="email"
                    value={editingQuote.customer_email || ''} 
                    onChange={(e) => setEditingQuote(p => ({ ...p, customer_email: e.target.value }))}
                    data-testid="edit-quote-email"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quote Date</Label>
                  <Input 
                    type="date"
                    value={editingQuote.date ? '' : ''}
                    onChange={(e) => setEditingQuote(p => ({ ...p, date: e.target.value }))}
                    placeholder="Leave blank to keep existing"
                    data-testid="edit-quote-date"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Current: {editingQuote.date}</p>
                </div>
                <div>
                  <Label>Valid For (days)</Label>
                  <Input 
                    type="number"
                    value={editingQuote.valid_days || 30} 
                    onChange={(e) => setEditingQuote(p => ({ ...p, valid_days: parseInt(e.target.value) || 30 }))}
                    className="w-24"
                    data-testid="edit-quote-valid-days"
                  />
                </div>
              </div>
              
              <div>
                <Label>Customer Address *</Label>
                <Textarea 
                  value={editingQuote.customer_address || ''} 
                  onChange={(e) => setEditingQuote(p => ({ ...p, customer_address: e.target.value }))}
                  rows={3}
                  data-testid="edit-quote-address"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea 
                  value={editingQuote.description || ''} 
                  onChange={(e) => setEditingQuote(p => ({ ...p, description: e.target.value }))}
                  rows={6}
                  className="whitespace-pre-wrap"
                  data-testid="edit-quote-description"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Line Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addEditQuoteLineItem}>
                    <Plus className="h-3 w-3 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="space-y-2">
                  {editingQuote.line_items.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateEditQuoteLineItem(index, 'description', e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateEditQuoteLineItem(index, 'quantity', e.target.value)}
                        className="w-20"
                      />
                      <Input
                        type="number"
                        placeholder="Cost"
                        value={item.unit_cost}
                        onChange={(e) => updateEditQuoteLineItem(index, 'unit_cost', e.target.value)}
                        className="w-24"
                      />
                      <div className="w-24 text-right pt-2 font-medium">
                        £{((item.quantity || 0) * (item.unit_cost || 0)).toFixed(2)}
                      </div>
                      {editingQuote.line_items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeEditQuoteLineItem(index)}
                          className="text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="text-right mt-2 text-lg font-bold">
                  Total: £{editingQuote.line_items.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_cost || 0), 0).toFixed(2)}
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={saveEditedQuote} 
                  className="flex-1 gap-2"
                  disabled={isSavingQuote}
                  data-testid="save-quote-btn"
                >
                  {isSavingQuote ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Save & Regenerate PDF
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={() => setIsEditQuoteOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Enquiry/Job Dialog */}
      <Dialog open={isEditEnquiryOpen} onOpenChange={setIsEditEnquiryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              Edit Job
            </DialogTitle>
            <DialogDescription>
              Update job details for {editingEnquiry?.name}
            </DialogDescription>
          </DialogHeader>
          {editingEnquiry && (
            <div className="space-y-4">
              {/* Link to Customer */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <Label className="text-sm font-medium flex items-center gap-1.5">
                  <User className="h-4 w-4" />
                  Link to Customer
                </Label>
                <Select 
                  value={editingEnquiry.customer_id || "none"} 
                  onValueChange={(v) => {
                    if (v === "none") {
                      setEditingEnquiry(p => ({ ...p, customer_id: "" }));
                    } else {
                      setEditingEnquiry(p => ({ ...p, customer_id: v }));
                    }
                  }}
                >
                  <SelectTrigger className="mt-1" data-testid="edit-enquiry-customer-select">
                    <SelectValue placeholder="Select customer..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No customer linked</SelectItem>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} - {c.phone || c.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={editingEnquiry.name}
                    onChange={(e) => setEditingEnquiry({...editingEnquiry, name: e.target.value})}
                    placeholder="Customer name"
                    data-testid="edit-enquiry-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editingEnquiry.email}
                    onChange={(e) => setEditingEnquiry({...editingEnquiry, email: e.target.value})}
                    placeholder="email@example.com"
                    data-testid="edit-enquiry-email"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    value={editingEnquiry.phone}
                    onChange={(e) => setEditingEnquiry({...editingEnquiry, phone: e.target.value})}
                    placeholder="07xxx xxxxxx"
                    data-testid="edit-enquiry-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Service</Label>
                  <Select 
                    value={editingEnquiry.service || 'general-repairs'} 
                    onValueChange={(v) => setEditingEnquiry({...editingEnquiry, service: v})}
                  >
                    <SelectTrigger data-testid="edit-enquiry-service">
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general-repairs">General Repairs</SelectItem>
                      <SelectItem value="carpentry">Carpentry</SelectItem>
                      <SelectItem value="furniture-builds">Furniture Builds</SelectItem>
                      <SelectItem value="cnc-factory">CNC/Factory Work</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea
                  value={editingEnquiry.address || ''}
                  onChange={(e) => setEditingEnquiry({...editingEnquiry, address: e.target.value})}
                  placeholder="Customer address"
                  className="min-h-[60px]"
                  data-testid="edit-enquiry-address"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Message/Details</Label>
                <Textarea
                  value={editingEnquiry.message}
                  onChange={(e) => setEditingEnquiry({...editingEnquiry, message: e.target.value})}
                  placeholder="Job details..."
                  className="min-h-[80px]"
                  data-testid="edit-enquiry-message"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Internal Notes</Label>
                <Textarea
                  value={editingEnquiry.notes || ''}
                  onChange={(e) => setEditingEnquiry({...editingEnquiry, notes: e.target.value})}
                  placeholder="Notes (not visible to customer)..."
                  className="min-h-[60px]"
                  data-testid="edit-enquiry-notes"
                />
              </div>

              {/* Iter 60: Completion date — editable to fix historical "Marked Today" errors. */}
              <div className="space-y-2">
                <Label>Completed Date</Label>
                <Input
                  type="date"
                  value={editingEnquiry.date_completed || ''}
                  onChange={(e) => setEditingEnquiry({...editingEnquiry, date_completed: e.target.value})}
                  data-testid="edit-enquiry-date-completed"
                />
                <p className="text-xs text-muted-foreground">
                  Defaults to the linked invoice's date when an invoice is marked paid.
                  Edit here to correct historical dates.
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => editingEnquiry && handleDelete(editingEnquiry.id)}
              className="sm:mr-auto"
              data-testid="delete-enquiry-from-edit-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete Job
            </Button>
            <Button variant="outline" onClick={() => setIsEditEnquiryOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEnquiry} data-testid="save-enquiry-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Review Dialog */}
      <Dialog open={isRequestReviewOpen} onOpenChange={setIsRequestReviewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-500" />
              Request Review
            </DialogTitle>
            <DialogDescription>
              Request a review from the customer for this completed job
            </DialogDescription>
          </DialogHeader>
          {requestReviewJob && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{requestReviewJob.name}</p>
                <p className="text-sm text-muted-foreground">{getServiceLabel(requestReviewJob.service)}</p>
                {requestReviewJob.email && requestReviewJob.email !== 'no-email@placeholder.local' && (
                  <p className="text-sm text-muted-foreground mt-1">{requestReviewJob.email}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <p className="text-sm font-medium">What would you like to do?</p>
                <div className="space-y-2">
                  <Button 
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => handleRequestReview(false)}
                    data-testid="mark-review-requested-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Just mark as requested (I'll send it manually)
                  </Button>
                  {requestReviewJob.email && requestReviewJob.email !== 'no-email@placeholder.local' && (
                    <Button 
                      className="w-full justify-start"
                      onClick={() => handleRequestReview(true)}
                      data-testid="send-review-request-btn"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Send review request email
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  Tip: Consider sending a WhatsApp message with a direct link to your Google or Facebook review page for best response rates.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRequestReviewOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Portfolio Dialog */}
      <Dialog open={isAddToPortfolioOpen} onOpenChange={setIsAddToPortfolioOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Image className="h-5 w-5 text-emerald-500" />
              Add Photos to Portfolio
            </DialogTitle>
            <DialogDescription>
              Select photos from this job to add to your portfolio/lightbox
            </DialogDescription>
          </DialogHeader>
          {addToPortfolioJob && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{addToPortfolioJob.name}</p>
                <p className="text-sm text-muted-foreground">{getServiceLabel(addToPortfolioJob.service)}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Portfolio Category</Label>
                <Select value={portfolioCategory} onValueChange={setPortfolioCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carpentry">Carpentry</SelectItem>
                    <SelectItem value="general_repairs">General Repairs</SelectItem>
                    <SelectItem value="furniture_builds">Furniture Builds</SelectItem>
                    <SelectItem value="cnc_factory">CNC/Factory Work</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {addToPortfolioJob.photos && addToPortfolioJob.photos.length > 0 ? (
                <div className="space-y-2">
                  <Label>Select Photos ({selectedPhotosForPortfolio.length} selected)</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {addToPortfolioJob.photos.map((photo, idx) => {
                      const photoUrl = typeof photo === 'string' ? photo : photo.url;
                      const isSelected = selectedPhotosForPortfolio.includes(photoUrl);
                      return (
                        <div 
                          key={photoUrl} 
                          className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                            isSelected ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-transparent hover:border-gray-300'
                          }`}
                          onClick={() => togglePhotoForPortfolio(photoUrl)}
                          data-testid={`portfolio-photo-${idx}`}
                        >
                          <img 
                            src={getImageUrl(photoUrl)} 
                            alt={`Photo ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                          {isSelected && (
                            <div className="absolute inset-0 bg-emerald-500/20 flex items-center justify-center">
                              <CheckCircle className="h-8 w-8 text-emerald-600" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No photos found for this job</p>
                  <p className="text-sm">Add photos to the customer record first</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddToPortfolioOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddPhotosToPortfolio}
              disabled={selectedPhotosForPortfolio.length === 0}
              data-testid="confirm-add-to-portfolio-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add {selectedPhotosForPortfolio.length} Photo{selectedPhotosForPortfolio.length !== 1 ? 's' : ''} to Portfolio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
